import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { focusRefWhenReady, formatDateToYMD, parseCalendarRedirectDate } from '../../Utils/utils';
import {
    maskNricOrPassport,
    mergeNricMaskedInput,
    sanitizeNricInput,
    validateNricOrPassport,
} from '../../Utils/nricUtils';
import { showMembershipPaymentSuccessToast } from '../../Utils/membershipPaymentToast';
import { getCustomerDetails, getClientSalesList, updateCustomerData, postUserNotes, deleteCustomer, getGuestCheckinDetails, getMembershipPackages, getGiftCategoriesByMembershipPackage, postMembershipPurchaseTopup, postMembershipTransactionPayment, getSalesList, getSalesDetails, addSaleNote, getMembershipTransactions, getMembershipTransactionDetail, emailBookingInvoice, postMembershipTransactionNote, getCustomerList } from '../../APICalling/APICalling';
import MembershipTransactionSidePanel from '../MembershipTransactionSidePanel/MembershipTransactionSidePanel';
import SideBarRight from '../../Pages/Bookings/SideBarRight';
import MembershipSaleSidePanel from '../MembershipSaleSidePanel/MembershipSaleSidePanel';
import MembershipTransactionNotePopup from '../Popups/MembershipTransactionNotePopup/MembershipTransactionNotePopup';
import SaleNotePopup from '../Popups/SaleNotePopup/SaleNotePopup';
import { fetchCountries, findCountryByCodeSync, getLocalPhoneDigits } from '../../Utils/countryUtils';
import styles from './CustomerDetailsModal.module.css';
import rightStyles from '../RightSidebar/rightsidebar.module.css';
import requestIcon from '../../Assets/images/requested.svg';
import menuIcon from '../../Assets/images/menu.svg';
import DropArrow from '../../Assets/images/select-arrow.svg';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomDatePicker from './CustomDatePicker';
import CustomRadio from '../CustomRadio/CustomRadio';
import 'react-datepicker/dist/react-datepicker.css';
import "react-phone-number-input/style.css";
import { getCountryCallingCode, parsePhoneNumber, isValidPhoneNumber } from "react-phone-number-input";
import { getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import CountrySelectorWithSearch from "../PhoneInput/CountrySelectorWithSearch";
import editIcon from '../../Assets/images/EditIcon.svg';
import refreshIcon from '../../Assets/images/refresh.svg';
import IconMember from '../../Assets/images/IconMember.svg';
import PrimiumMemberIcon from '../Icons/PrimiumMemberIcon';
import GuestIcon from '../../Assets/images/guestIcon.svg';
import Loader from '../Loader/Loader';
import DeleteConfirmPopup from '../Popups/DeleteConfirmPopup/DeleteConfirmPopup';
import MembershipPasswordPopup from '../Popups/MembershipPasswordPopup/MembershipPasswordPopup';
import SetMembershipPasswordPopup from '../Popups/MembershipPasswordPopup/SetMembershipPasswordPopup';
import { useGlobalContext } from '../../Context/GlobalContext';
import BorderedButton from '../Buttons/BorderedButton';
import PrimaryButton from '../Buttons/PrimaryButton';
import AppointmentSVG from '../../Assets/images/ApointmentSVG.svg';
import IconRoom from "../../Assets/images/room.svg";
import PaymentPage from '../RightSidebar/PaymentPage';
import ConfirmationPage from '../RightSidebar/ConfirmationPage';
import BookingPaymentDetails from '../BookingPaymentDetails/BookingPaymentDetails';
import { useInvoiceEmailCooldown } from '../../Utils/useInvoiceEmailCooldown';
import { downloadFileWithAuth } from '../../Utils/downloadFileWithAuth';
import logger from '../../Utils/logger';
import {
    getCheckoutCustomerIsPasswordSet,
    resolveMembershipPasswordIsSet,
    setCheckoutCustomerIsPasswordSet,
    shouldPromptSetMembershipPasswordAfterPurchase,
    withMembershipPasswordSetFlag,
} from '../../Utils/customerPasswordSession';
import {
    findPendingMembershipTransactionSale,
    findAnyOpenSaleForCustomer,
    getOpenMembershipRecoveryIds,
    getOpenMembershipSaleAmount,
    getMembershipSaleId,
    isOpenMembershipSale,
} from '../../Utils/openMembershipSale';
import BookingNotes from '../BookingNotes/BookingNotes';
import { SaleCancelReason } from '../SaleCancelReason/SaleCancelReason';

// Generate consistent color based on user ID or name
const getConsistentColor = (userId, name = '') => {
    // Use user ID if available, otherwise use name
    const seed = userId ? String(userId) : (name || 'default');
    
    // Simple hash function to convert string to number
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        const char = seed.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Generate consistent RGB values with better visibility (darker, more saturated)
    // Range: 80-200 (more visible than 127-254)
    const r = Math.abs(hash % 120) + 80;
    const g = Math.abs((hash * 7) % 120) + 80;
    const b = Math.abs((hash * 13) % 120) + 80;
    
    return `rgb(${r},${g},${b})`;
};

const CLIENT_DETAILS_ACTIVE_TAB_STORAGE_KEY = 'nl_client_details_active_tab';

const INITIAL_MEMBERSHIP_HISTORY_PAGINATION = {
    currentPage: 1,
    lastPage: 1,
};

const getMembershipHistoryTransactionKey = (transaction) => String(
    transaction?.id ??
    transaction?.transaction_id ??
    `${transaction?.created_at || transaction?.date || ''}-${transaction?.sale_id || ''}-${transaction?.action || ''}-${transaction?.amount || ''}`
);

const getStoredClientDetailsActiveTab = () => {
    try {
        const stored = sessionStorage.getItem(CLIENT_DETAILS_ACTIVE_TAB_STORAGE_KEY);
        return stored === 'membership' ? 'membership' : 'timeline';
    } catch {
        return 'timeline';
    }
};

const storeClientDetailsActiveTab = (tab) => {
    try {
        sessionStorage.setItem(
            CLIENT_DETAILS_ACTIVE_TAB_STORAGE_KEY,
            tab === 'membership' ? 'membership' : 'timeline'
        );
    } catch {
        // ignore storage errors
    }
};

const CustomerDetailsSidebar = ({
    customer,
    isOpen,
    onClose,
    autoOpenMembershipFlow = null,
    membershipFlowOnly = false,
    membershipFromNewCheckout = false,
    recoveryMembershipSale = null,
    onMembershipPaymentSuccess = null,
    skipSetMembershipPasswordAfterPayment = false,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { selectedOutlet, setSelectedOutlet, selectedDate, setSelectedDate, loggedOutletID, serviceTotal, setServiceTotal, paymentSummary, amountDue, setAmountDue, amountToCharge, setAmountToCharge, paymentHistory, setPaymentHistory, currentPaymentMethod, selectedOutletData, setIsSidebarLoading, loggedUser } = useGlobalContext();
    const {
        startCooldown: startInvoiceEmailCooldown,
        getRemaining: getInvoiceEmailCooldownRemaining,
    } = useInvoiceEmailCooldown();
    
    // Get placeholder based on selected country using libphonenumber-js
    const getMobilePlaceholder = (country) => {
        const countryCode = country && typeof country === 'object' ? country.code : country;
        if (!countryCode) return 'Mobile number';
        try {
            // Get example number for the country
            const exampleNumber = getExampleNumber(countryCode, examples);
            if (exampleNumber) {
                // Format the example number and remove country code
                const formatted = exampleNumber.formatNational();
                return formatted || 'Mobile number';
            }
        } catch (error) {
            console.error('Error getting example number:', error);
        }
        return 'Mobile number';
    };

    const [activeTab, setActiveTabState] = useState(getStoredClientDetailsActiveTab);

    const setActiveTab = useCallback((tab) => {
        const normalized = tab === 'membership' ? 'membership' : 'timeline';
        storeClientDetailsActiveTab(normalized);
        setActiveTabState(normalized);
    }, []);
    const [activeFilter, setActiveFilter] = useState('all');
    const [customerDetails, setCustomerDetails] = useState(null);
    const [customerTimeline, setCustomerTimeline] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isTimelineLoading, setIsTimelineLoading] = useState(false);

    // Edit form states
    const [editForm, setEditForm] = useState({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        gender: '',
        dateOfBirth: null,
        newsletter: false,
        country_id: null
    });
    const [editErrors, setEditErrors] = useState({});
    const editFormInitializedRef = useRef(false);
    const [avatarColor, setAvatarColor] = useState('#9F9F9F');
    const [displayPhoneNumber, setDisplayPhoneNumber] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('SG'); // Country object or ISO code while countries load

    // Notes states
    const [isNotesMode, setIsNotesMode] = useState(false);
    const [notesText, setNotesText] = useState('');
    const [isSavingNotes, setIsSavingNotes] = useState(false);
    const [isEditingNote, setIsEditingNote] = useState(false);
    const [existingNotes, setExistingNotes] = useState('');
    const [isLoadingNotes, setIsLoadingNotes] = useState(false);
    const notesTextareaRef = useRef(null);

    // Menu and delete states
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isMembershipMenuOpen, setIsMembershipMenuOpen] = useState(false);
    const [isMembershipHistoryOpen, setIsMembershipHistoryOpen] = useState(false);
    const [membershipTransactions, setMembershipTransactions] = useState([]);
    const [membershipHistoryPagination, setMembershipHistoryPagination] = useState(
        INITIAL_MEMBERSHIP_HISTORY_PAGINATION
    );
    const [isMembershipHistoryLoading, setIsMembershipHistoryLoading] = useState(false);
    const [membershipHistoryLoadMoreError, setMembershipHistoryLoadMoreError] = useState(false);
    const membershipHistoryRequestIdRef = useRef(0);
    const membershipHistoryLoadMorePendingRef = useRef(false);
    const membershipHistoryAutoLoadPageRef = useRef(null);
    const membershipHistoryTableRef = useRef(null);
    const [isMembershipTransactionPanelOpen, setIsMembershipTransactionPanelOpen] = useState(false);
    const [membershipTransactionPanelId, setMembershipTransactionPanelId] = useState(null);
    /** Shown in "Sale #…" for the transaction panel; prefers API `sale_id` over internal transaction `id`. */
    const [membershipTransactionPanelSaleDisplay, setMembershipTransactionPanelSaleDisplay] = useState(null);
    const [membershipTransactionPanelTitlePrefix, setMembershipTransactionPanelTitlePrefix] = useState('Sale');
    const [membershipTransactionDetail, setMembershipTransactionDetail] = useState(null);
    const [membershipTransactionNote, setMembershipTransactionNote] = useState('');
    const [isSalesSidebarOpen, setIsSalesSidebarOpen] = useState(false);
    const [salesSidebarId, setSalesSidebarId] = useState(null);
    const [salesSidebarSale, setSalesSidebarSale] = useState(null);
    const [isMembershipSalePanelOpen, setIsMembershipSalePanelOpen] = useState(false);
    const [membershipSalePanelDetail, setMembershipSalePanelDetail] = useState(null);
    const [membershipSalePanelTitle, setMembershipSalePanelTitle] = useState('');
    const [membershipSalePanelNote, setMembershipSalePanelNote] = useState('');
    const [membershipSalePanelSaleId, setMembershipSalePanelSaleId] = useState(null);
    const [openMembershipSale, setOpenMembershipSale] = useState(null);
    const [isOpenMembershipSaleLoading, setIsOpenMembershipSaleLoading] = useState(false);
    const [isMembershipSaleNotePopupOpen, setIsMembershipSaleNotePopupOpen] = useState(false);
    const [isMembershipSaleNoteProcessing, setIsMembershipSaleNoteProcessing] = useState(false);
    const [isMembershipTopupPreviewOpen, setIsMembershipTopupPreviewOpen] = useState(false);
    const [membershipTopupPreviewPanelId, setMembershipTopupPreviewPanelId] = useState(null);
    const [membershipTopupPreviewSaleDisplay, setMembershipTopupPreviewSaleDisplay] = useState(null);
    const [membershipTopupPreviewDetail, setMembershipTopupPreviewDetail] = useState(null);
    const [membershipTopupPreviewNote, setMembershipTopupPreviewNote] = useState('');
    const hasAutoOpenedMembershipFlowRef = useRef(false);
    /** When opening membership payment from product checkout (membershipFlowOnly), preserve checkout totals so we can restore global context after top-up / on dismiss. */
    const preservedExternalServiceTotalRef = useRef(null);
    const preservedExternalAmountDueRef = useRef(null);
    const preservedExternalAmountToChargeRef = useRef(null);
    const [isMembershipNotePopupOpen, setIsMembershipNotePopupOpen] = useState(false);
    const [isMembershipNoteProcessing, setIsMembershipNoteProcessing] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteErrorMessage, setDeleteErrorMessage] = useState(null);
    
    // Mobile left panel toggle state
    const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(false);

    // Check-in info dropdown states
    const [isCheckinInfoOpen, setIsCheckinInfoOpen] = useState(false);
    const [guestCheckinInfoOpen, setGuestCheckinInfoOpen] = useState({});
    const [paymentsDetailsOpen, setPaymentsDetailsOpen] = useState({});
    const [guestDetails, setGuestDetails] = useState({}); // Store guest details by userId
    const [timelinePage, setTimelinePage] = useState(1);
    const [timelineHasMore, setTimelineHasMore] = useState(true);
    const [isLoadingMoreTimeline, setIsLoadingMoreTimeline] = useState(false);
    const [openTimelineMenuId, setOpenTimelineMenuId] = useState(null);
    const [isPurchaseMembershipModalOpen, setIsPurchaseMembershipModalOpen] = useState(false);
    const [isLoadingMembershipPackages, setIsLoadingMembershipPackages] = useState(false);
    const [isLoadingGiftCategories, setIsLoadingGiftCategories] = useState(false);
    const [membershipPackages, setMembershipPackages] = useState([]);
    const [selectedMembershipPackageId, setSelectedMembershipPackageId] = useState(null);
    const [giftSections, setGiftSections] = useState([]);
    const [selectedGiftOptionBySection, setSelectedGiftOptionBySection] = useState({});
    const [purchaseMembershipError, setPurchaseMembershipError] = useState('');
    const [insuranceCertificateNumber, setInsuranceCertificateNumber] = useState('');
    const [voucherSerialBySection, setVoucherSerialBySection] = useState({});
    const [giftRemarksBySection, setGiftRemarksBySection] = useState({});
    const [isPurchaseClientDetailsOpen, setIsPurchaseClientDetailsOpen] = useState(false);
    const [isSavingPurchaseClient, setIsSavingPurchaseClient] = useState(false);
    const [purchaseMembershipStep, setPurchaseMembershipStep] = useState('details');
    const [purchaseMembershipFlowType, setPurchaseMembershipFlowType] = useState('purchase');
    const [purchaseBookingDataFromAPI, setPurchaseBookingDataFromAPI] = useState({ booking_id: null, deposit: 0 });
    /** Persisted after first purchase-topup API success; needed for partial-payment follow-ups when customer has no membership_detail yet. */
    const [purchaseFollowupMembershipId, setPurchaseFollowupMembershipId] = useState(null);
    /** Internal membership record id for menu "Set Password" — opens SetMembershipPasswordPopup. */
    const [membershipMenuSetPopupId, setMembershipMenuSetPopupId] = useState(null);
    /** First-time purchase: defer toast/refresh/checkout close until Skip or Set Password. */
    const [setPasswordPopupFromFirstPurchase, setSetPasswordPopupFromFirstPurchase] = useState(false);
    const pendingPurchaseFinalizeRef = useRef(null);
    /** Internal membership record id for menu "Reset Password" — opens reusable MembershipPasswordPopup. */
    const [membershipMenuResetPopupId, setMembershipMenuResetPopupId] = useState(null);
    const [purchaseClientForm, setPurchaseClientForm] = useState({
        firstName: '',
        lastName: '',
        nric: '',
        email: ''
    });
    const [purchaseClientNricError, setPurchaseClientNricError] = useState('');
    const timelineContainerRef = useRef(null);
    const sidebarContentRef = useRef(null);
    const purchaseMembershipPanelRef = useRef(null);
    const menuRef = useRef(null);
    const membershipMenuRef = useRef(null);

    const [isPanelEntered, setIsPanelEntered] = useState(false);
    const isOverlayVisible = isOpen || isPanelEntered;
    // Fetch guest details from API
    const fetchGuestDetails = async (guestCheckinId) => {
        if (!guestCheckinId || guestDetails[guestCheckinId]) return; // Skip if already fetched
        
        try {
            const response = await getGuestCheckinDetails(guestCheckinId);
            if (response?.data?.success) {
                const guestData = response.data.data;
                // Map the API response to match the format used in the UI
                const mappedData = {
                    name: guestData.first_name || '',
                    lastname: guestData.last_name || '',
                    contact_number: guestData.contact_number || '',
                    passport_number: guestData.passport_number || '',
                    nric: guestData.passport_number || '', // Use passport_number as nric fallback
                    nationality: guestData.nationality || 'N/A'
                };
                
                setGuestDetails(prev => ({
                    ...prev,
                    [guestCheckinId]: mappedData
                }));
            }
        } catch (error) {
            console.error('Error fetching guest details:', error);
        }
    };

    // Toggle guest check-in info and fetch details if not already loaded
    const toggleGuestCheckinInfo = (guestKey, guestCheckinId) => {
        setGuestCheckinInfoOpen(prev => ({
            ...prev,
            [guestKey]: !prev[guestKey]
        }));
        
        // Fetch guest details when opening dropdown
        if (!guestCheckinInfoOpen[guestKey] && guestCheckinId) {
            fetchGuestDetails(guestCheckinId);
        }
    };

    const togglePaymentsDetails = (itemKey) => {
        setPaymentsDetailsOpen((prev) => ({
            ...prev,
            [itemKey]: !prev[itemKey]
        }));
    };

    // Function to get custom initials based on input
    // Only takes first word from firstName and first word from lastName
    const getCustomInitials = (firstName, lastName) => {
        // Extract only the first word from firstName and lastName
        const first = firstName.trim().split(/\s+/)[0] || '';
        const last = lastName.trim().split(/\s+/)[0] || '';
        
        if (!first && !last) return '';
        
        if (first && !last) {
            // Only first name: show first and second letter of first word
            return first.length >= 2 ? first.substring(0, 2).toUpperCase() : first.toUpperCase();
        }
        
        if (first && last) {
            // Both names: show first letter of first word of first name + first letter of first word of last name
            return (first.charAt(0) + last.charAt(0)).toUpperCase();
        }
        
        if (!first && last) {
            // Only last name: show first letter of first word of last name
            return last.charAt(0).toUpperCase();
        }
        
        return '';
    };

    // Helper function to get lastname - respects empty string from API
    const getLastName = () => {
        if (customerDetails !== null) {
            return customerDetails?.lastname || '';
        }
        return customer?.lastname || '';
    };

    // Function to update avatar based on name input
    const updateAvatar = (firstName, lastName) => {
        if (firstName.trim() || lastName.trim()) {
            // Use consistent color based on customer ID or name
            const customerId = customer?.id || customerDetails?.id;
            const customerName = `${firstName} ${lastName}`.trim() || customerDetails?.name || customer?.name;
            setAvatarColor(getConsistentColor(customerId, customerName));
        } else {
            // If no name is entered, use default Neutral/500 color
            setAvatarColor('#6B7280');
        }
    };

    // Fetch customer details
    const fetchCustomerDetails = async (customerId, { silent = false } = {}) => {
        if (!customerId) return;

        try {
            if (!silent) setLoading(true);
            const response = await getCustomerDetails(customerId);

            if (response?.data?.success) {
                let details = response.data.data;
                if (getCheckoutCustomerIsPasswordSet(customerId) === true) {
                    details = withMembershipPasswordSetFlag(details);
                }
                setCustomerDetails(details);
                // Extract notes from customer details response
                const notes = details?.notes || '';
                setExistingNotes(notes);
                return details;
            } else {
                console.warn('Customer details API returned no data, using fallback');
                // Use the customer prop as fallback
                setCustomerDetails(customer);
                setExistingNotes('');
                return customer;
            }
        } catch (error) {
            console.error('Error fetching customer details:', error);
            // Use the customer prop as fallback instead of showing error
            setCustomerDetails(customer);
            setExistingNotes('');
            return customer;
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const refreshMembershipPaymentState = async (customerId, options = {}) => {
        if (!customerId) return null;

        const { fallbackOpenSale = null, refreshTimeline = true } = options;
        let refreshedDetails = null;

        try {
            refreshedDetails = await fetchCustomerDetails(customerId);
        } catch (refreshError) {
            console.error('Failed to refresh customer membership details:', refreshError);
        }

        const refreshedMembershipDetail =
            refreshedDetails?.membership_detail ||
            refreshedDetails?.membershipDetail ||
            membershipDetail;

        try {
            const sale = await findAnyOpenSaleForCustomer(
                customerId,
                selectedOutlet || loggedOutletID || null,
                refreshedMembershipDetail
            );
            setOpenMembershipSale(sale || fallbackOpenSale);
        } catch (saleError) {
            console.error('Failed to refresh open membership sale:', saleError);
            setOpenMembershipSale(fallbackOpenSale);
        }

        if (refreshTimeline && !membershipFlowOnly) {
            try {
                await fetchCustomerTimeline(customerId, 1);
            } catch (timelineError) {
                console.error('Failed to refresh customer timeline after membership payment:', timelineError);
            }
        }

        return refreshedDetails;
    };

    const refreshCustomerListAfterMembershipPayment = async () => {
        try {
            await getCustomerList({
                panel: 'outlet',
                pagination: 1,
                cursor_paginate: 1,
                page: 1,
            });
            try {
                window.dispatchEvent(new CustomEvent('refreshClientList'));
            } catch (eventError) {
                console.error('Failed to dispatch client list refresh event:', eventError);
            }
        } catch (error) {
            console.error('Failed to refresh customer list after membership payment:', error);
        }
    };



    // Fetch customer timeline/activity
    const mergeTimelineItems = (existingItems, newItems) => {
        const map = new Map();
        [...existingItems, ...newItems].forEach((item) => {
            const key = `${item.type}-${item.id}`;
            if (!map.has(key)) {
                map.set(key, item);
            }
        });
        const merged = Array.from(map.values());
        merged.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            if (isNaN(dateA.getTime())) return 1;
            if (isNaN(dateB.getTime())) return -1;
            return dateB - dateA;
        });
        return merged;
    };

    const parseTimelineAmount = (value) => {
        const amount = Number(String(value ?? '').replace(/,/g, ''));
        return Number.isFinite(amount) ? amount : 0;
    };

    const normalizeTimelineDate = (dateValue) => {
        if (!dateValue || typeof dateValue !== 'string') {
            return dateValue;
        }
        return dateValue.includes(' ') && !dateValue.includes('T')
            ? dateValue.replace(' ', 'T')
            : dateValue;
    };

    const buildServiceDateTimeFromSale = (sale, serviceItem) => {
        if (serviceItem?.service_at) {
            return normalizeTimelineDate(serviceItem.service_at);
        }

        const saleDate = String(sale?.date_time || '').split(/[ T]/)[0];
        const startTime = String(serviceItem?.start_time || '').trim();
        if (!saleDate || !startTime) {
            return normalizeTimelineDate(sale?.date_time);
        }

        const twelveHourMatch = startTime.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (twelveHourMatch) {
            let hours = Number(twelveHourMatch[1]);
            const minutes = twelveHourMatch[2];
            const ampm = twelveHourMatch[3].toUpperCase();
            if (ampm === 'PM' && hours !== 12) hours += 12;
            if (ampm === 'AM' && hours === 12) hours = 0;
            return `${saleDate}T${String(hours).padStart(2, '0')}:${minutes}:00`;
        }

        const timeMatch = startTime.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
        if (timeMatch) {
            return `${saleDate}T${String(timeMatch[1]).padStart(2, '0')}:${timeMatch[2]}:${timeMatch[3] || '00'}`;
        }

        return normalizeTimelineDate(sale?.date_time);
    };

    const normalizeSalePayments = (payments) => Array.isArray(payments) ? payments : [];

    const shouldShowSaleTimelineItem = (sale) => {
        const summary = sale?.summary || {};
        const payments = normalizeSalePayments(summary.payments);
        const paidAmount = parseTimelineAmount(summary.paid_amount);
        const status = String(sale?.status || '').toLowerCase();
        return Boolean(
            sale?.parent_sale_id ||
            sale?.child_sale_id ||
            payments.length > 0 ||
            paidAmount > 0 ||
            (status.includes('progress') && (payments.length > 0 || paidAmount > 0)) ||
            status.includes('refund')
        );
    };

    const normalizeClientSaleServices = (sale) => {
        const services = [];
        const customers = [];
        (sale?.pax_groups || []).forEach((group) => {
            const customerName = group?.display_name || group?.customer_name || sale?.client?.name || 'Unknown';
            if (customerName && !customers.includes(customerName)) {
                customers.push(customerName);
            }
            (group?.items || []).forEach((item) => {
                const quantity = parseTimelineAmount(item?.quantity) || 1;
                const unitPrice = parseTimelineAmount(item?.unit_price ?? item?.list_price);
                const totalPrice = parseTimelineAmount(item?.total_price);
                const durationValue = item?.duration_mins ?? parseInt(item?.duration, 10);
                services.push({
                    id: item?.booking_item_id || item?.id,
                    booking_id: item?.booking_id || sale?.booking_id || null,
                    booking_item_id: item?.booking_item_id || item?.id,
                    service: item?.name || 'Service',
                    price: totalPrice || (unitPrice * quantity),
                    addons_total: parseTimelineAmount(item?.addons_total),
                    addons: Array.isArray(item?.addons) ? item.addons : [],
                    therapist: item?.therapist,
                    therapist_pager_number: item?.therapist_pager_number || item?.therapist_id,
                    therapist_gender: item?.therapist_gender || item?.gender || '',
                    duration: Number.isFinite(Number(durationValue)) ? Number(durationValue) : '',
                    requested_person: item?.requested_person || 0,
                    service_request: item?.service_request || '',
                    service_at: buildServiceDateTimeFromSale(sale, item),
                    is_calendar_expansion: item?.is_calendar_expansion || 0,
                    customerName,
                    customer_id: sale?.client?.id,
                    guest_checkin_id: item?.guest_checkin_id || group?.guest_checkin_id || null,
                    requested_room: item?.requested_room || 0,
                    room_name: item?.room_name || item?.room,
                    room_id: item?.room_id || null,
                    room_items: item?.room_items || (item?.room ? [{ room_name: item.room }] : [])
                });
            });
        });
        return { services, customers };
    };

    const normalizeBookingServices = (booking) => {
        const services = [];
        const customers = [];
        Object.entries(booking?.booking_item || {}).forEach(([customerName, items]) => {
            if (customerName && !customers.includes(customerName)) {
                customers.push(customerName);
            }
            (items || []).forEach((item) => {
                const quantity = parseTimelineAmount(item?.quantity) || 1;
                const unitPrice = parseTimelineAmount(item?.price);
                services.push({
                    id: item?.id,
                    booking_id: item?.booking_id || booking?.id || null,
                    booking_item_id: item?.id,
                    service: item?.service || 'Service',
                    price: unitPrice * quantity,
                    addons_total: parseTimelineAmount(item?.addons_total),
                    addons: Array.isArray(item?.addons) ? item.addons : [],
                    therapist: item?.therapist,
                    therapist_pager_number: item?.therapist_pager_number || item?.therapist_id,
                    therapist_gender: item?.therapist_gender || item?.gender || '',
                    duration: item?.duration,
                    requested_person: item?.requested_person,
                    service_request: item?.service_request,
                    service_at: normalizeTimelineDate(item?.service_at),
                    is_calendar_expansion: item?.is_calendar_expansion,
                    customerName: item?.customer_name || customerName,
                    customer_id: item?.customer_id || item?.user_id || booking?.user_id,
                    guest_checkin_id: item?.guest_checkin_id,
                    requested_room: item?.requested_room,
                    room_name: item?.room_name,
                    room_id: item?.room_id,
                    room_items: item?.room_items
                });
            });
        });
        return { services, customers };
    };

    const normalizeBookingToTimelineItem = (booking) => {
        const { services, customers } = normalizeBookingServices(booking);
        if (services.length === 0) {
            return null;
        }

        const firstServiceDate = services.find((service) => service.service_at)?.service_at;
        const displayDate = firstServiceDate || normalizeTimelineDate(booking.created_at);
        const outlet = Array.isArray(booking.outlet) ? booking.outlet[0] : booking.outlet;

        return {
            id: booking.id,
            booking_id: booking.id,
            booking_item_id: services[0]?.booking_item_id || null,
            sale_id: booking.sale_id || booking.sale?.id || null,
            type: 'booking',
            date: displayDate,
            title: `Appointment ${booking.status || ''}`,
            services,
            customers,
            subtotal: parseTimelineAmount(booking.sub_total),
            gst: parseTimelineAmount(booking.gst_amount),
            total: parseTimelineAmount(booking.grand_total),
            deposit: parseTimelineAmount(booking.deposit),
            payment_method: booking.payment_type || 'Cash',
            payment_methods: null,
            outlet: outlet?.name || 'Outlet',
            outlet_id: outlet?.id || booking.outlet_id || null,
            status: String(booking.status || ''),
            child_sale_id: booking.child_sale_id || null,
            parent_sale_id: booking.parent_sale_id || null,
            payment_status_id: booking.payment_status_id,
            booking_type: booking.bookingtype?.[0]?.name || 'Booking',
            source: booking.source || 'Walk-in',
            note: booking.note || null,
            linked_sale_status: booking.sale?.payment_status || '',
            is_voided_or_refunded: ['voided', 'refunded', 'refund'].includes(String(booking.status || '').toLowerCase())
        };
    };

    const createSaleTimelineBase = (sale) => {
        const summary = sale?.summary || {};
        const payments = normalizeSalePayments(summary.payments);
        return {
            id: sale.id,
            sale_id: sale.id,
            sale_reference: sale.sale_reference,
            date: normalizeTimelineDate(sale.date_time),
            title: `Sale #${sale.sale_reference || sale.id} ${sale.status || ''}`,
            subtotal: parseTimelineAmount(summary.sub_total),
            gst: parseTimelineAmount(summary.gst_amount),
            total: parseTimelineAmount(summary.grand_total),
            deposit: parseTimelineAmount(summary.deposit),
            payment_method: payments[0]?.method || 'Cash',
            payment_methods: payments,
            outlet: sale.outlet?.name || 'Outlet',
            outlet_id: sale.outlet?.id || null,
            status: String(sale.status || ''),
            child_sale_id: sale.child_sale_id,
            parent_sale_id: sale.parent_sale_id,
            payment_status_id: sale.payment_status_id || null,
            booking_type: sale.sale_type || 'sale',
            source: sale.channel || 'Walk-in',
            note: sale.sales_note || null,
            cancel_reason: sale.cancel_reason || null,
            cancel_remark: sale.cancel_remark || null
        };
    };

    const normalizeClientSaleToTimelineItems = (sale, salesById = new Map()) => {
        const timelineItems = [];
        const baseSaleItem = createSaleTimelineBase(sale);
        const { services, customers } = normalizeClientSaleServices(sale);
        const relatedSale = salesById.get(sale?.parent_sale_id) || salesById.get(sale?.child_sale_id) || null;
        const { services: relatedServices } = relatedSale ? normalizeClientSaleServices(relatedSale) : { services: [] };
        const saleDisplayServices = services.length > 0 ? services : relatedServices;
        const saleDisplayItems = (sale.items || []).length > 0 ? sale.items : (relatedSale?.items || []);

        if (sale.sale_type === 'booking' && services.length > 0) {
            const appointmentBookingId = sale?.booking_id || services[0]?.booking_id || services[0]?.id || sale.id;
            timelineItems.push({
                ...baseSaleItem,
                id: appointmentBookingId,
                booking_id: appointmentBookingId,
                booking_item_id: services[0]?.booking_item_id || services[0]?.id || null,
                sale_id: sale.id,
                type: 'booking',
                date: services[0]?.service_at || baseSaleItem.date,
                title: `Appointment ${sale.booking_status || sale.status || ''}`,
                services,
                customers,
                status: String(sale.booking_status || sale.status || ''),
                linked_sale_status: sale.status || '',
                is_voided_or_refunded: ['voided', 'refunded', 'refund'].includes(String(sale.booking_status || sale.status || '').toLowerCase())
            });
        }

        if (shouldShowSaleTimelineItem(sale)) {
            const saleProducts = saleDisplayItems.map((item) => {
                const quantity = parseInt(item?.quantity, 10) || 1;
                const itemType = String(item?.type || sale.sale_type || 'product').toLowerCase();
                return {
                    product_id: item?.id,
                    product_name: item?.name || 'Product',
                    price: parseTimelineAmount(item?.unit_price ?? item?.total_price),
                    quantity,
                    item_type: itemType,
                    serial_number: item?.serial_number || '',
                    customer_name: sale?.client?.name,
                    item_number: item?.item_number || null,
                    guest_checkin_id: item?.guest_checkin_id || null
                };
            });

            const bookingSaleProducts = saleProducts.length > 0
                ? saleProducts
                : saleDisplayServices.map((service) => ({
                    product_id: service.id,
                    product_name: service.service,
                    price: parseTimelineAmount(service.price),
                    quantity: 1,
                    customer_name: service.customerName,
                    item_number: null,
                    guest_checkin_id: service.guest_checkin_id || null,
                    duration: service.duration,
                    therapist: service.therapist,
                    therapist_pager_number: service.therapist_pager_number,
                    therapist_gender: service.therapist_gender,
                    room_name: service.room_name,
                    room_items: service.room_items,
                    service_at: service.service_at,
                    is_calendar_expansion: service.is_calendar_expansion,
                    requested_person: service.requested_person,
                    requested_room: service.requested_room
                }));

            timelineItems.push({
                ...baseSaleItem,
                type: 'product_sale',
                products: bookingSaleProducts,
                guest_checkin_id: bookingSaleProducts[0]?.guest_checkin_id || null,
                is_booking_sale: sale.sale_type === 'booking' || saleDisplayServices.length > 0
            });
        }

        return timelineItems;
    };

    const fetchCustomerTimeline = async (customerId, page = 1) => {
        if (!customerId) return;

        // Create unique fetch ID to detect stale responses
        const fetchId = Symbol(`timeline-fetch-${customerId}-${page}`);
        activeTimelineFetchRef.current = fetchId;

        try {
            if (page === 1) {
                setError(null);
                setIsTimelineLoading(true);
            } else {
                setIsLoadingMoreTimeline(true);
            }
            const params = {
                pagination: 1,
                page: page,
                panel: 'outlet',
                types: 'all'
            };

            // Add timeout to prevent hanging
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Request timeout')), 10000)
            );

            const responsePromise = getClientSalesList(customerId, params);
            const response = await Promise.race([responsePromise, timeoutPromise]);

            // Ignore stale responses if new fetch was started
            if (activeTimelineFetchRef.current !== fetchId) return;

            if (response?.data?.success) {
                setError(null);
                const list = response.data.data?.list || {};
                const bookings = list.bookings || [];
                const sales = list.sales || [];
                const pagination = list.pagination;
                const salesPagination = list.sales_pagination;
                const salesById = new Map(sales.map((sale) => [sale.id, sale]));
                const bookingTimelineData = bookings
                .map(normalizeBookingToTimelineItem)
                .filter(Boolean);
                const salesTimelineData = sales.flatMap((sale) => normalizeClientSaleToTimelineItems(sale, salesById));
                const timelineData = [...bookingTimelineData, ...salesTimelineData];

                setCustomerTimeline((prevItems) =>
                    mergeTimelineItems(page === 1 ? [] : prevItems, timelineData)
                );
                if (pagination || salesPagination) {
                    const bookingCurrentPage = pagination?.currentPage || page;
                    const bookingLastPage = pagination?.lastPage || page;
                    const salesCurrentPage = salesPagination?.currentPage || page;
                    const salesLastPage = salesPagination?.lastPage || page;
                    setTimelinePage(Math.max(bookingCurrentPage, salesCurrentPage));
                    setTimelineHasMore(
                        bookingCurrentPage < bookingLastPage ||
                        salesCurrentPage < salesLastPage
                    );
                } else {
                    setTimelineHasMore(false);
                }
            } else {
                if (activeTimelineFetchRef.current === fetchId) {
                    const status = response?.status ?? response?.response?.status;
                    const timelineErrorMessage = status === 404 ? 'This customer has been deleted and is no longer available.'
                            : ( response?.response?.data?.message || 'Failed to fetch customer timeline' );
                    setError(timelineErrorMessage);
                }
            }
        } catch (error) {
            console.error('Error fetching customer timeline:', error);
            if (page === 1) {
                setCustomerTimeline([]);
            } else {
                setTimelineHasMore(false);
            }
        } finally {
            if (page === 1) {
                setIsTimelineLoading(false);
            } else {
                setIsLoadingMoreTimeline(false);
            }
        }
    };

    // Fetch data when customer prop changes (keep active tab when switching clients)
    useEffect(() => {
        if (isOpen) {
            if (customer?.id) {
                // Clear all old data immediately to prevent stale state from showing
                setError(null);
                setOpenMembershipSale(null);
                setCustomerTimeline([]);
                setCustomerDetails(null);
                setIsTimelineLoading(true);
                if (membershipFlowOnly) {
                    // Don't set customerDetails immediately; wait for API response to avoid flicker
                    setLoading(true);
                }
                setTimelinePage(1);
                setTimelineHasMore(true);
                fetchCustomerDetails(customer.id);
                if (!membershipFlowOnly) {
                    fetchCustomerTimeline(customer.id, 1);
                }
            } else {
                // If no customer ID, just use the customer prop data
                setCustomerDetails(customer);
                setCustomerTimeline([]);
                setExistingNotes('');
                setTimelineHasMore(false);
            }
        }
    }, [customer?.id, isOpen]);

    const previousCustomerIdRef = useRef(null);
    const activeTimelineFetchRef = useRef(null);

    const resetMembershipSalePanelState = () => {
        setIsMembershipSalePanelOpen(false);
        setMembershipSalePanelDetail(null);
        setMembershipSalePanelTitle('');
        setMembershipSalePanelNote('');
        setMembershipSalePanelSaleId(null);
        setIsMembershipSaleNotePopupOpen(false);
        setIsMembershipSaleNoteProcessing(false);
    };

    const resetSalesSidebarState = () => {
        setIsSalesSidebarOpen(false);
        setSalesSidebarId(null);
        setSalesSidebarSale(null);
    };

    const openSalesSidebarFromSale = useCallback((saleToOpen) => {
        const relatedSaleId = String(
            saleToOpen?.sale_id ??
            saleToOpen?.id ??
            saleToOpen?.booking_id ??
            ''
        ).replace(/^#/, '').trim();

        if (!relatedSaleId) {
            return;
        }

        setSalesSidebarId(relatedSaleId);
        setSalesSidebarSale(saleToOpen || null);
        setIsSalesSidebarOpen(true);
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const customerId = customer?.id ?? customerDetails?.id;
        if (!isOpen || !customerId || membershipFlowOnly) {
            setOpenMembershipSale(null);
            setIsOpenMembershipSaleLoading(false);
            return;
        }

        let cancelled = false;
        const fetchOpenMembershipSale = async () => {
            setIsOpenMembershipSaleLoading(true);
            try {
                const membershipDet = customerDetails?.membership_detail || customer?.membership_detail || null;
                const sale = await findAnyOpenSaleForCustomer(
                    customerId,
                    selectedOutlet || loggedOutletID || null,
                    membershipDet
                );
                if (!cancelled) {
                    setOpenMembershipSale(sale);
                }
            } catch (error) {
                console.error('Error fetching open membership sale:', error);
                if (!cancelled) {
                    setOpenMembershipSale(null);
                }
            } finally {
                if (!cancelled) {
                    setIsOpenMembershipSaleLoading(false);
                }
            }
        };

        fetchOpenMembershipSale();
        return () => {
            cancelled = true;
        };
    }, [customer?.id, isOpen, membershipFlowOnly]);

    // After payment or refund/void in the sales sidebar, refresh open-sale banner,
    // credit balance (membership_detail), timeline, and membership history if open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!isOpen) return;
        const customerId = customer?.id ?? customerDetails?.id;
        if (!customerId || membershipFlowOnly) return;

        const handlePaymentSuccess = async () => {
            try {
                const membershipDet = customerDetails?.membership_detail || customer?.membership_detail || null;
                const sale = await findAnyOpenSaleForCustomer(
                    customerId,
                    selectedOutlet || loggedOutletID || null,
                    membershipDet
                );
                setOpenMembershipSale(sale);
            } catch {
                setOpenMembershipSale(null);
            }
            // Refresh customer details and timeline so membership status / balance updates
            try { await fetchCustomerDetails(customerId, { silent: true }); } catch {}
            try { fetchCustomerTimeline(customerId, 1); } catch {}
            if (isMembershipHistoryOpen) {
                try {
                    await handleViewMembershipHistory({
                        silent: true,
                        page: 1,
                    });
                } catch {}
            }
        };

        window.addEventListener('salesPaymentSuccess', handlePaymentSuccess);
        window.addEventListener('voidSaleCompleted', handlePaymentSuccess);
        return () => {
            window.removeEventListener('salesPaymentSuccess', handlePaymentSuccess);
            window.removeEventListener('voidSaleCompleted', handlePaymentSuccess);
        };
    }, [isOpen, customer?.id, customerDetails?.id, membershipFlowOnly, selectedOutlet, loggedOutletID, isMembershipHistoryOpen]);

    // Close top-up / purchase panel when user picks another client from the list
    useEffect(() => {
        if (!isOpen) {
            previousCustomerIdRef.current = null;
            return;
        }
        const currentId = customer?.id ?? null;
        if (
            previousCustomerIdRef.current != null &&
            currentId != null &&
            previousCustomerIdRef.current !== currentId
        ) {
            setIsPurchaseMembershipModalOpen(false);
            setPurchaseMembershipError('');
            setPurchaseMembershipStep('details');
            setPurchaseBookingDataFromAPI({ booking_id: null, deposit: 0 });
            setPurchaseFollowupMembershipId(null);
            setPurchaseClientNricError('');
            setIsPurchaseClientDetailsOpen(false);
            setMembershipPackages([]);
            setSelectedMembershipPackageId(null);
            setGiftSections([]);
            setSelectedGiftOptionBySection({});
            setInsuranceCertificateNumber('');
            setVoucherSerialBySection({});
            setGiftRemarksBySection({});
            setIsMembershipHistoryOpen(false);
            setMembershipTransactions([]);
            setMembershipHistoryPagination(INITIAL_MEMBERSHIP_HISTORY_PAGINATION);
            setIsMembershipHistoryLoading(false);
            setMembershipHistoryLoadMoreError(false);
            membershipHistoryRequestIdRef.current += 1;
            membershipHistoryLoadMorePendingRef.current = false;
            membershipHistoryAutoLoadPageRef.current = null;
            setIsMembershipMenuOpen(false);
            setIsMembershipTransactionPanelOpen(false);
            setMembershipTransactionPanelId(null);
            setMembershipTransactionPanelSaleDisplay(null);
            setMembershipTransactionDetail(null);
            setMembershipTransactionNote('');
            setIsMembershipNotePopupOpen(false);
            resetMembershipSalePanelState();
            resetSalesSidebarState();
            setIsMembershipTopupPreviewOpen(false);
            setMembershipTopupPreviewPanelId(null);
            setMembershipTopupPreviewSaleDisplay(null);
            setMembershipTopupPreviewDetail(null);
            setMembershipTopupPreviewNote('');
        }
        previousCustomerIdRef.current = currentId;
    }, [customer?.id, isOpen]);

    // Restore last selected tab when opening client details
    useEffect(() => {
        if (isOpen && !membershipFlowOnly) {
            setActiveTabState(getStoredClientDetailsActiveTab());
        }
    }, [isOpen, membershipFlowOnly]);

    // Reset edit and notes states when sidebar closes
    useEffect(() => {
        if (!isOpen) {
            setIsEditMode(false);
            setIsNotesMode(false);
            setNotesText('');
            setEditErrors({});
            setIsMenuOpen(false);
            setIsMembershipMenuOpen(false);
            setIsMembershipHistoryOpen(false);
            setMembershipTransactions([]);
            setMembershipHistoryPagination(INITIAL_MEMBERSHIP_HISTORY_PAGINATION);
            setIsMembershipHistoryLoading(false);
            setMembershipHistoryLoadMoreError(false);
            membershipHistoryRequestIdRef.current += 1;
            membershipHistoryLoadMorePendingRef.current = false;
            membershipHistoryAutoLoadPageRef.current = null;
            setIsMembershipTransactionPanelOpen(false);
            setMembershipTransactionPanelId(null);
            setMembershipTransactionPanelSaleDisplay(null);
            setMembershipTransactionDetail(null);
            setMembershipTransactionNote('');
            setIsMembershipNotePopupOpen(false);
            resetMembershipSalePanelState();
            resetSalesSidebarState();
            setIsMembershipTopupPreviewOpen(false);
            setMembershipTopupPreviewPanelId(null);
            setMembershipTopupPreviewSaleDisplay(null);
            setMembershipTopupPreviewDetail(null);
            setMembershipTopupPreviewNote('');
            setShowDeleteConfirm(false);
            setIsLeftPanelOpen(false);
            setOpenTimelineMenuId(null);
            setTimelinePage(1);
            setTimelineHasMore(true);
            setIsPurchaseMembershipModalOpen(false);
            setMembershipPackages([]);
            setSelectedMembershipPackageId(null);
            setGiftSections([]);
            setSelectedGiftOptionBySection({});
            setPurchaseMembershipError('');
            setInsuranceCertificateNumber('');
            setVoucherSerialBySection({});
            setGiftRemarksBySection({});
            // Clear all data when sidebar closes to prevent stale state
            setCustomerTimeline([]);
            setOpenMembershipSale(null);
            setCustomerDetails(null);
        }
    }, [isOpen]);

    // Slide panel in from the right after open; keep backdrop visible until slide-out finishes
    useEffect(() => {
        if (!isOpen) {
            setIsPanelEntered(false);
            return undefined;
        }
        if (membershipFlowOnly) {
            setIsPanelEntered(true);
            return undefined;
        }
        let rafEnter1;
        let rafEnter2;
        rafEnter1 = requestAnimationFrame(() => {
            rafEnter2 = requestAnimationFrame(() => setIsPanelEntered(true));
        });
        return () => {
            cancelAnimationFrame(rafEnter1);
            if (rafEnter2) cancelAnimationFrame(rafEnter2);
        };
    }, [isOpen, membershipFlowOnly]);

    const sidebarOverlayClassName = `${styles.sidebarOverlay} ${isOverlayVisible ? styles.open : ''} ${membershipFlowOnly ? styles.membershipFlowOverlay : ''} ${isPurchaseMembershipModalOpen ? styles.purchaseMembershipOpen : ''}`;
    const sidebarContentClassName = membershipFlowOnly
        ? `${styles.sidebarContent} ${styles.membershipFlowHost}`
        : `${styles.sidebarContent} ${styles.customerDetailsPanel} ${isPanelEntered ? styles.customerDetailsPanelEntered : ''}`;

    const handleTimelineScroll = (event) => {
        const target = event.currentTarget;
        if (!target) return;
        const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 200;
        const customerId = customer?.id || customerDetails?.id;
        if (nearBottom && timelineHasMore && !isLoadingMoreTimeline && !loading && customerId) {
            fetchCustomerTimeline(customerId, timelinePage + 1);
        }
    };

    const getMembershipRegistrationOutletId = () => {
        const outletId =
            membershipDetail?.registration_outlet_id ??
            membershipDetail?.outlet_id ??
            customerDetails?.membership_detail?.registration_outlet_id ??
            customer?.membership_detail?.registration_outlet_id ??
            null;
        return outletId != null && String(outletId).trim() !== ''
            ? String(outletId).trim()
            : null;
    };

    const getMembershipTransactionOutletId = (transaction) => {
        const outletId =
            transaction?.registration_outlet_id ??
            transaction?.outlet_id ??
            transaction?.outlet ??
            getMembershipRegistrationOutletId() ??
            selectedOutlet;
        return outletId != null && String(outletId).trim() !== ''
            ? String(outletId).trim()
            : selectedOutlet;
    };

    // Close menus when clicking outside (sidebar stops propagation, so listen on sidebar content)
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMenuOpen && menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
            if (isMembershipMenuOpen && membershipMenuRef.current && !membershipMenuRef.current.contains(event.target)) {
                setIsMembershipMenuOpen(false);
            }
        };

        const sidebarEl = sidebarContentRef.current;
        if ((isMenuOpen || isMembershipMenuOpen) && sidebarEl) {
            sidebarEl.addEventListener('click', handleClickOutside);
            return () => sidebarEl.removeEventListener('click', handleClickOutside);
        }
    }, [isMenuOpen, isMembershipMenuOpen]);

    const handleViewMembershipHistory = async ({ silent = false, page = 1, append = false } = {}) => {
        const requestedPage = Math.max(1, Number(page) || 1);
        const requestId = membershipHistoryRequestIdRef.current + 1;
        membershipHistoryRequestIdRef.current = requestId;
        if (!append) {
            membershipHistoryLoadMorePendingRef.current = false;
            membershipHistoryAutoLoadPageRef.current = null;
        }
        setMembershipHistoryLoadMoreError(false);
        setIsMembershipHistoryLoading(true);
        if (!silent) setIsSidebarLoading(true);
        try {
            // Transactions endpoint expects internal membership record id in path.
            let membershipId = membershipDetail?.id;
            if (membershipId == null || membershipId === '') {
                membershipId = membershipDetail?.membership_id;
            }
            membershipId = membershipId != null && membershipId !== ''
                ? String(membershipId).trim().replace(/^#/, '')
                : '';
            if (!membershipId) {
                if (requestId === membershipHistoryRequestIdRef.current) {
                    setMembershipTransactions([]);
                    setMembershipHistoryPagination(INITIAL_MEMBERSHIP_HISTORY_PAGINATION);
                    setIsMembershipHistoryOpen(true);
                }
                return;
            }
            const registrationOutletId = getMembershipRegistrationOutletId();
            const response = await getMembershipTransactions(membershipId, {
                page: requestedPage,
                ...(registrationOutletId ? { outlet: registrationOutletId } : {}),
            });
            const responseFailed =
                !response ||
                response instanceof Error ||
                response?.isAxiosError ||
                response?.data?.success === false ||
                response?.success === false;
            if (responseFailed) {
                const message =
                    response?.response?.data?.message ||
                    response?.data?.message ||
                    response?.message ||
                    'Unable to load membership credit history.';
                throw new Error(message);
            }
            const payloadData = response?.data?.data ?? response?.data ?? [];
            const normalizedData = Array.isArray(payloadData) ? payloadData : [payloadData];
            const membershipHistoryData = normalizedData[0] || {};
            const transactions = membershipHistoryData?.transactions;
            const allTransactions = Array.isArray(transactions) ? transactions : [];
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
            const datedTransactions = allTransactions
                .map((transaction) => ({
                    transaction,
                    date: new Date(transaction?.created_at || transaction?.date),
                }))
                .filter(({ date }) => !Number.isNaN(date.getTime()));
            const isNewestFirst = datedTransactions.every(
                ({ date }, index) => index === 0 || datedTransactions[index - 1].date >= date
            );
            // History is returned newest-first. Once an ordered page crosses the
            // one-year display cutoff, later server pages cannot add visible rows.
            const reachedHistoryCutoff = isNewestFirst && datedTransactions.some(
                ({ date }) => date < oneYearAgo
            );
            const filteredTransactions = datedTransactions
                .filter(({ date }) => date >= oneYearAgo)
                .map(({ transaction }) => transaction);
            const pagination = membershipHistoryData?.pagination || {};
            const currentPage = Math.max(
                1,
                Number(pagination?.currentPage ?? pagination?.current_page ?? requestedPage) || requestedPage
            );
            const lastPage = Math.max(
                1,
                Number(pagination?.lastPage ?? pagination?.last_page ?? currentPage) || currentPage
            );

            // A payment/refund can shrink the result set during a load-more request.
            if (requestedPage > lastPage && requestId === membershipHistoryRequestIdRef.current) {
                setMembershipHistoryPagination((previous) => ({
                    currentPage: Math.min(previous.currentPage, lastPage),
                    lastPage,
                }));
                return;
            }

            if (requestId === membershipHistoryRequestIdRef.current) {
                setMembershipTransactions((previousTransactions) => {
                    if (!append) return filteredTransactions;

                    const knownTransactions = new Set(
                        previousTransactions.map(getMembershipHistoryTransactionKey)
                    );
                    const nextTransactions = filteredTransactions.filter((transaction) => {
                        const key = getMembershipHistoryTransactionKey(transaction);
                        if (knownTransactions.has(key)) return false;
                        knownTransactions.add(key);
                        return true;
                    });
                    return [...previousTransactions, ...nextTransactions];
                });
                setMembershipHistoryPagination({
                    currentPage: Math.min(currentPage, lastPage),
                    lastPage: reachedHistoryCutoff
                        ? Math.min(currentPage, lastPage)
                        : lastPage,
                });
                setIsMembershipHistoryOpen(true);
                if (!append && membershipHistoryTableRef.current) {
                    membershipHistoryTableRef.current.scrollTop = 0;
                }
            }
        } catch (error) {
            console.error('Failed to fetch membership transactions:', error);
            if (requestId === membershipHistoryRequestIdRef.current) {
                if (!silent) {
                    setMembershipTransactions([]);
                    setMembershipHistoryPagination(INITIAL_MEMBERSHIP_HISTORY_PAGINATION);
                    setIsMembershipHistoryOpen(true);
                } else if (append) {
                    setMembershipHistoryLoadMoreError(true);
                }
                toast.error(error?.message || 'Unable to load membership credit history.');
            }
        } finally {
            if (requestId === membershipHistoryRequestIdRef.current) {
                setIsMembershipHistoryLoading(false);
            }
            if (!silent) setIsSidebarLoading(false);
        }
    };

    const handleLoadMoreMembershipHistory = async () => {
        const nextPage = membershipHistoryPagination.currentPage + 1;
        if (
            membershipHistoryLoadMorePendingRef.current ||
            isMembershipHistoryLoading ||
            nextPage > membershipHistoryPagination.lastPage
        ) {
            return;
        }

        membershipHistoryLoadMorePendingRef.current = true;
        try {
            await handleViewMembershipHistory({
                page: nextPage,
                silent: true,
                append: true,
            });
        } finally {
            membershipHistoryLoadMorePendingRef.current = false;
        }
    };

    const handleMembershipHistoryScroll = (event) => {
        const target = event.currentTarget;
        if (!target) return;

        const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 120;
        if (nearBottom) {
            membershipHistoryAutoLoadPageRef.current =
                membershipHistoryPagination.currentPage + 1;
            handleLoadMoreMembershipHistory();
        }
    };

    // Fill tall viewports automatically until the table becomes scrollable. From
    // that point onward, the scroll handler loads the next page near the bottom.
    useEffect(() => {
        const table = membershipHistoryTableRef.current;
        const nextPage = membershipHistoryPagination.currentPage + 1;
        const hasMore = nextPage <= membershipHistoryPagination.lastPage;
        if (
            !isMembershipHistoryOpen ||
            !table ||
            !hasMore ||
            isMembershipHistoryLoading ||
            membershipHistoryLoadMoreError ||
            membershipHistoryAutoLoadPageRef.current === nextPage
        ) {
            return;
        }

        if (table.scrollHeight <= table.clientHeight + 80) {
            membershipHistoryAutoLoadPageRef.current = nextPage;
            handleLoadMoreMembershipHistory();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isMembershipHistoryOpen,
        isMembershipHistoryLoading,
        membershipHistoryLoadMoreError,
        membershipHistoryPagination.currentPage,
        membershipHistoryPagination.lastPage,
        membershipTransactions.length,
    ]);

    const handleCloseMembershipHistory = () => {
        membershipHistoryRequestIdRef.current += 1;
        membershipHistoryLoadMorePendingRef.current = false;
        membershipHistoryAutoLoadPageRef.current = null;
        setIsMembershipHistoryLoading(false);
        setMembershipHistoryLoadMoreError(false);
        setIsMembershipHistoryOpen(false);
    };

    const formatMembershipHistoryDate = (value) => {
        if (!value) return '-';
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return String(value);
        const day = String(parsed.getDate()).padStart(2, '0');
        const month = parsed.toLocaleString('en-US', { month: 'short' });
        const year = parsed.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const formatMembershipHistoryAmount = (value) => {
        const amount = Number(value || 0);
        if (!Number.isFinite(amount)) return '$0.00';
        const isNegative = amount < 0;
        const absoluteAmount = Math.abs(amount).toFixed(2);
        return `${isNegative ? '-' : ''}$${absoluteAmount}`;
    };
    const getMembershipHistoryAmount = (transaction) =>
        transaction?.amount ??
        transaction?.credit_amount ??
        transaction?.purchase_amount ??
        transaction?.paid_amount ??
        transaction?.value ??
        0;

    const formatMembershipHistoryActionName = (transaction) => {
        const raw = transaction?.action;
        if (raw != null && String(raw).trim() !== '') {
            const s = String(raw).trim().toLowerCase();
            if (s === 'topup') return 'Top-up';
            if (s === 'refund') return 'Refund';
            if (s === 'purchase') return 'Purchase';
            return s
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (char) => char.toUpperCase());
        }
        return (
            transaction?.type_label ||
            transaction?.transaction_type ||
            transaction?.type ||
            transaction?.description ||
            '—'
        );
    };

    const isMembershipTopupTransaction = (transaction) => {
        const raw = String(transaction?.action || '').trim().toLowerCase();
        if (raw === 'topup' || raw === 'top_up') return true;
        const label = String(
            transaction?.action_label ||
            transaction?.type_label ||
            formatMembershipHistoryActionName(transaction)
        ).toLowerCase();
        return label.includes('top-up') || label.includes('top up');
    };

    const isMembershipRefundTransaction = (transaction) => {
        const raw = String(transaction?.action || '').trim().toLowerCase();
        if (raw === 'refund_membership' || raw === 'refund') return true;
        const label = String(
            transaction?.action_label ||
            transaction?.type_label ||
            formatMembershipHistoryActionName(transaction)
        ).toLowerCase();
        return label.includes('refund');
    };

    const getMembershipRefundActionText = (transaction) => String(
        transaction?.action_label ||
        transaction?.type_label ||
        transaction?.transaction_type ||
        transaction?.type ||
        transaction?.description ||
        formatMembershipHistoryActionName(transaction)
    ).trim().toLowerCase();

    const isMembershipCreditRefundTransaction = (transaction) => {
        const raw = String(transaction?.action || '').trim().toLowerCase();
        const label = getMembershipRefundActionText(transaction);
        return raw === 'refund_membership' ||
            label.includes('refund membership') ||
            label.includes('membership refund');
    };

    const hasProductSaleDetail = (saleDetail = null, saleDetailData = null) => {
        const saleType = String(saleDetail?.type || '').trim().toLowerCase();
        if (saleType === 'product') return true;
        if (Array.isArray(saleDetail?.product) && saleDetail.product.length > 0) return true;
        if (Array.isArray(saleDetailData?.bookingData) && saleDetailData.bookingData.length > 0) return true;
        if (Array.isArray(saleDetailData?.product) && saleDetailData.product.length > 0) return true;
        return false;
    };

    const hasServiceSaleDetail = (saleDetailData = null) => {
        const serviceBookings = saleDetailData?.booking?.bookings || saleDetailData?.bookings;
        return Array.isArray(serviceBookings) && serviceBookings.length > 0;
    };

    const isMembershipProductRefundTransaction = (transaction, saleDetail = null, saleDetailData = null) => {
        const label = getMembershipRefundActionText(transaction);
        return label.includes('refund product') ||
            label.includes('product refund') ||
            hasProductSaleDetail(saleDetail, saleDetailData);
    };

    const isMembershipServiceRefundTransaction = (transaction, saleDetail = null, saleDetailData = null) => {
        const label = String(
            transaction?.action_label ||
            transaction?.type_label ||
            formatMembershipHistoryActionName(transaction)
        ).toLowerCase();
        const saleType = String(saleDetail?.type || transaction?.type || '').trim().toLowerCase();
        return label.includes('refund service') ||
            saleType === 'booking' ||
            saleType === 'service' ||
            hasServiceSaleDetail(saleDetailData);
    };

    const normalizeMembershipSaleId = (value) => {
        if (value == null || String(value).trim() === '') return null;
        const normalized = String(value).replace(/^#/, '').trim();
        return normalized || null;
    };

    const getMembershipHistoryReferenceId = (transaction) => {
        const saleId = transaction?.sale_id;
        if (saleId != null && String(saleId).trim() !== '') {
            return `#${String(saleId).replace(/^#/, '').trim()}`;
        }
        const id = transaction?.id;
        if (id != null && String(id).trim() !== '') {
            return `#${String(id).replace(/^#/, '').trim()}`;
        }
        const saleNumber = transaction?.sale_number;
        if (saleNumber != null && String(saleNumber).trim() !== '') {
            return `#${String(saleNumber).replace(/^#/, '').trim()}`;
        }
        return null;
    };

    const getMembershipHistoryOutlet = (transaction) => {
        const action = String(transaction?.action || '').trim().toLowerCase();

        // Expiry is always done by system
        if (action === 'expiry' || action === 'expired') {
            return 'System';
        }

        // Reinstatement: by system (auto on top-up) or admin (manual)
        if (action === 'reinstatement' || action === 'reinstate') {
            const createdBy = (transaction?.created_by_name || '').trim();
            if (!createdBy || createdBy.toLowerCase() === 'system') {
                return 'System';
            }
            return `${createdBy} (Admin)`;
        }

        // Adjustment: always by admin
        if (action === 'adjustment' || action === 'adjust') {
            const createdBy = (transaction?.created_by_name || '').trim();
            return createdBy ? `${createdBy} (Admin)` : 'Admin';
        }

        const outlet = transaction?.outlet;
        const outletName =
            transaction?.outlet_name ??
            (typeof outlet === 'string' ? outlet : null) ??
            (Array.isArray(outlet) ? outlet[0]?.name : null) ??
            (outlet && typeof outlet === 'object' ? outlet.name : null) ??
            transaction?.registration_outlet_name ??
            transaction?.outlet_title;
        if (outletName != null && String(outletName).trim() !== '') {
            return String(outletName).trim();
        }
        return '—';
    };

    const getMembershipHistoryBalanceBefore = (transaction) => {
        const balance =
            transaction?.balance_before ??
            transaction?.opening_balance ??
            transaction?.credit_balance_before;
        if (balance == null || balance === '') return null;
        const numeric = Number(balance);
        return Number.isFinite(numeric) ? numeric : null;
    };

    const getMembershipHistoryBalanceAfter = (transaction) => {
        const balance =
            transaction?.balance_after ??
            transaction?.closing_balance ??
            transaction?.credit_balance_after;
        if (balance == null || balance === '') return null;
        const numeric = Number(balance);
        return Number.isFinite(numeric) ? numeric : null;
    };

    const getMembershipHistoryCreditDelta = (transaction) => {
        const before = getMembershipHistoryBalanceBefore(transaction);
        const after = getMembershipHistoryBalanceAfter(transaction);
        if (before == null || after == null) return null;
        return after - before;
    };

    const formatMembershipHistoryCreditDelta = (delta) => {
        if (delta == null) return null;
        const abs = Math.abs(delta).toFixed(2);
        if (delta > 0) return `+$${abs}`;
        if (delta < 0) return `-$${abs}`;
        return `$${abs}`;
    };

    const isMembershipHistoryRowClickable = (transaction) => {
        const id = transaction?.id ?? transaction?.transaction_id;
        return id != null && String(id).trim() !== '';
    };

    const handleOpenMembershipSalePanelForTopup = async (transaction) => {
        const saleId = transaction?.sale_id ?? transaction?.id;
        if (saleId == null || String(saleId).trim() === '') {
            return;
        }
        const normalizedSaleId = String(saleId).replace(/^#/, '').trim();
        const transactionOutletId = getMembershipTransactionOutletId(transaction);
        setIsSidebarLoading(true);
        try {
            // Use getSalesList API (same as Sales page) to fetch membership sale details
            const requestItem = {
                pagination: 1,
                per_page: 1,
                page: 1,
                sales_id: normalizedSaleId,
                outlet: transactionOutletId,
            };

            const response = await getSalesList(requestItem);
            const list = response?.data?.data?.list || {};
            const allBookings = list.bookings || [];
            const detail = allBookings.length > 0 ? allBookings[0] : null;
            if (!detail?.id) {
                toast.error('Could not load top-up sale details.');
                return;
            }
            const titleId = normalizedSaleId;
            // Normalize status like Bookings.js does - check status first, then payment_status
            const normalizedStatus = String(detail.status || detail.payment_status || '').trim().toLowerCase();
            const rawPaymentStatus = String(detail.payment_status || '').toLowerCase();
            const normalizedAction = String(detail.action || '').trim().toLowerCase();
            const normalizedActionLabel = String(detail.action_label || '').trim().toLowerCase();

            // Refund detection - check status, action, and action_label
            const isRefunded =
              normalizedStatus === 'refunded' ||
              normalizedAction === 'refund_membership' ||
              normalizedAction === 'refund' ||
              normalizedActionLabel.includes('refund');

            const displayStatus = rawPaymentStatus === 'paid' ? 'closed' : rawPaymentStatus;
            const statusSuffix = displayStatus ? ` (${displayStatus})` : '';
            const titlePrefix = isRefunded ? 'Refund' : 'Sale';

            setMembershipSalePanelSaleId(detail.id);
            setMembershipSalePanelTitle(`${titlePrefix} #${titleId}${statusSuffix}`);
            setMembershipSalePanelDetail(detail);
            setMembershipSalePanelNote(
                detail.sale_note != null && String(detail.sale_note).trim() !== ''
                    ? String(detail.sale_note).trim()
                    : ''
            );
            setIsMembershipSalePanelOpen(true);
        } catch (error) {
            console.error('Membership top-up sale detail:', error);
            toast.error('Could not load top-up sale details.');
        } finally {
            setIsSidebarLoading(false);
        }
    };

    const handleMembershipTransactionSaleClick = async (transaction) => {
        // Note: Top-ups are now handled in handleMembershipHistoryRowClick via SideBarRight
        const actionText = String(transaction?.action_label || transaction?.action || '').toLowerCase();
        if (actionText.includes('expiry')) {
            setMembershipTransactionPanelTitlePrefix('Expiry');
        } else if (actionText.includes('reinstatement')) {
            setMembershipTransactionPanelTitlePrefix('Reinstatement');
        } else {
            setMembershipTransactionPanelTitlePrefix('Sale');
        }
        const rawId = transaction?.id ?? transaction?.transaction_id;
        if (rawId == null || rawId === '') {
            return;
        }
        const idNum = Number(rawId);
        if (!Number.isFinite(idNum)) {
            return;
        }
        setMembershipTransactionPanelId(idNum);
        const rawSaleId = transaction?.sale_id;
        if (rawSaleId != null && String(rawSaleId).trim() !== '') {
            setMembershipTransactionPanelSaleDisplay(String(rawSaleId).replace(/^#/, '').trim());
        } else {
            setMembershipTransactionPanelSaleDisplay(String(rawId).replace(/^#/, '').trim());
        }
        setMembershipTransactionNote('');
        setIsSidebarLoading(true);
        try {
            const response = await getMembershipTransactionDetail(idNum);
            const detail = response?.data?.data ?? response?.data ?? null;
            setMembershipTransactionDetail(detail);
            const txn = detail?.membership_transaction || detail || {};
            setMembershipTransactionNote(txn?.notes ?? txn?.note ?? detail?.notes ?? detail?.note ?? '');
        } catch (error) {
            console.error('Membership transaction detail:', error);
            setMembershipTransactionDetail(null);
        } finally {
            setIsSidebarLoading(false);
        }
        setIsMembershipTransactionPanelOpen(true);
    };

    const handleMembershipHistoryRowClick = async (transaction) => {
        if (!isMembershipHistoryRowClickable(transaction)) return;

        const hasSaleId = transaction?.sale_id != null && String(transaction.sale_id).trim() !== '';
        const isTopup = isMembershipTopupTransaction(transaction);

        // Non-sale transactions (no sale_id) → open transaction detail panel
        if (!hasSaleId) {
            await handleMembershipTransactionSaleClick(transaction);
            return;
        }

        // All sale-backed transactions (top-ups, refunds, utilisation) → open SideBarRight (same as Sales page)
        const saleId = normalizeMembershipSaleId(transaction.sale_id ?? transaction.id ?? transaction.transaction_id);

        if (!saleId) {
            console.error('Could not determine sale ID for transaction:', transaction);
            toast.error('Could not open sale details.');
            return;
        }

        // Fetch complete sale details to ensure all data is available
        const transactionOutletId = getMembershipTransactionOutletId(transaction);
        setIsSidebarLoading(true);
        try {
            const requestItem = {
                pagination: 1,
                per_page: 1,
                page: 1,
                sales_id: saleId,
                outlet: transactionOutletId,
            };

            const response = await getSalesList(requestItem);
            const list = response?.data?.data?.list || {};
            const allBookings = list.bookings || [];
            const saleDetail = allBookings.length > 0 ? allBookings[0] : null;
            const isRefund = isMembershipRefundTransaction(transaction);

            if (!saleDetail?.id) {
                toast.error('Could not load sale details.');
                return;
            }

            // Determine sale type and status
            // Extract string name from bookingtype object (API returns { id, name })
            const bookingTypeObj = saleDetail?.bookingtype?.[0];
            let saleType = saleDetail?.type
                || (typeof bookingTypeObj === 'string'
                ? bookingTypeObj
                : bookingTypeObj?.name?.toLowerCase())
                || transaction?.type
                || 'booking';
            let status = saleDetail?.status || saleDetail?.payment_status || transaction?.status || 'closed';

            let refundSaleDetailData = null;
            const shouldResolveRefundSaleType =
                isRefund &&
                !isTopup &&
                !isMembershipCreditRefundTransaction(transaction) &&
                !hasProductSaleDetail(saleDetail);

            if (shouldResolveRefundSaleType) {
                try {
                    const detailResponse = await getSalesDetails(saleId);
                    refundSaleDetailData = detailResponse?.data?.data ?? detailResponse?.data ?? null;
                } catch (detailError) {
                    console.error('Error resolving refund sale type:', detailError);
                }
            }

            const isProductRefund = isRefund && isMembershipProductRefundTransaction(transaction, saleDetail, refundSaleDetailData);
            const isServiceRefund = isRefund && !isProductRefund && isMembershipServiceRefundTransaction(transaction, saleDetail, refundSaleDetailData);

            // Membership top-ups/refunds use the membership details endpoint.
            // Product/service refunds paid with membership credit must stay on their original sale type.
            if (isProductRefund) {
                saleType = 'product';
            } else if (isServiceRefund) {
                saleType = saleDetail?.type || 'booking';
            } else if (isTopup || (isRefund && !isServiceRefund)) {
                saleType = 'membership';
            }

            const sidebarDetailId = saleDetail?.booking_id ?? saleDetail?.id ?? saleId;
            const displaySaleId = saleDetail?.sale_id ?? saleId;
            status = isRefund ? 'refunded' : status;
            setSalesSidebarId(String(sidebarDetailId).replace(/^#/, '').trim());
            setSalesSidebarSale({
                ...saleDetail,
                type: saleType,
                sale_id: displaySaleId,
                status,
            });
            setIsSalesSidebarOpen(true);
        } catch (error) {
            console.error('Error fetching sale details:', error);
            toast.error('Could not load sale details.');
        } finally {
            setIsSidebarLoading(false);
        }
    };

    const handleMembershipSalePanelAddNote = async (noteText) => {
        const saleNoteSaleId = membershipSalePanelDetail?.sale_id ?? membershipSalePanelSaleId;
        if (!saleNoteSaleId) return;
        setIsMembershipSaleNoteProcessing(true);
        try {
            const normalizedNote = noteText.trim();
            const response = await addSaleNote({
                id: String(saleNoteSaleId),
                panel: 'outlet',
                note: normalizedNote,
                updated_by: loggedUser,
            });
            if (response?.data?.success) {
                setMembershipSalePanelNote(normalizedNote);
                setMembershipSalePanelDetail((prev) => (
                    prev ? { ...prev, sale_note: normalizedNote } : prev
                ));
                setIsMembershipSaleNotePopupOpen(false);
                toast.success('Note added successfully!');
            } else {
                toast.error('Failed to add note. Please try again.');
            }
        } catch (error) {
            console.error('Error adding sale note:', error);
            toast.error('Error adding note. Please try again.');
        } finally {
            setIsMembershipSaleNoteProcessing(false);
        }
    };

    const getMembershipSalePanelInvoiceId = () => (
        membershipSalePanelDetail?.sale_id
        ?? membershipSalePanelDetail?.sale_id
        ?? membershipSalePanelSaleId
    );

    const handleMembershipTransactionAddNote = async (note) => {
        if (!membershipTransactionPanelId) return;
        setIsMembershipNoteProcessing(true);
        try {
            const normalizedNote = note.trim();
            const response = await postMembershipTransactionNote(String(membershipTransactionPanelId), {
                notes: normalizedNote,
            });
            const isSuccess = response?.success === true || response?.data?.success === true;

            if (isSuccess) {
                setMembershipTransactionNote(normalizedNote);
                setIsMembershipNotePopupOpen(false);
            } else {
                toast.error('Failed to add note. Please try again.', {
                    containerId: 'customerDetailsModal',
                    position: 'bottom-center',
                });
            }
        } catch (error) {
            console.error('Error adding note:', error);
            toast.error('Error adding note. Please try again.', {
                containerId: 'customerDetailsModal',
                position: 'bottom-center',
            });
        } finally {
            setIsMembershipNoteProcessing(false);
        }
    };

    // Populate edit form once when entering edit mode (not on every keystroke)
    useEffect(() => {
        if (!isEditMode) {
            editFormInitializedRef.current = false;
            return;
        }
        if (!(customerDetails || customer) || editFormInitializedRef.current) {
            return;
        }
        editFormInitializedRef.current = true;

        const lastName =
            customerDetails !== null
                ? (customerDetails?.lastname || '')
                : (customer?.lastname || '');
        const phone = customerDetails?.contact_number || customer?.contact_number || '';
            const toBoolPref = (value) => {
                if (value === true || value === 1 || value === '1') return true;
                if (value === false || value === 0 || value === '0') return false;
                // Fallback for unexpected types/values
                return Boolean(value);
            };
            const newsletterValue =
                (customerDetails?.newsletter ?? customer?.newsletter) ??
                (customerDetails?.marketing ?? customer?.marketing) ??
                0;
            setEditForm({
                firstName: customerDetails?.name || customer?.name || '',
                lastName,
                phone: phone,
                email: customerDetails?.email || customer?.email || '',
                gender: customerDetails?.gender ?? customer?.gender ?? '',
                dateOfBirth: customerDetails?.dob? new Date(customerDetails.dob) : null,
                newsletter: toBoolPref(newsletterValue),
                country_id: customerDetails?.country_id || customer?.country_id || null
            })
            setEditErrors({});
            // Set avatar color based on existing data using consistent color
            const firstName = customerDetails?.name || customer?.name || '';
            if (firstName || lastName) {
                const customerId = customer?.id || customerDetails?.id;
                const customerName = `${firstName} ${lastName}`.trim();
                setAvatarColor(getConsistentColor(customerId, customerName));
            } else {
                setAvatarColor('#6B7280');
            }
            
            // Parse phone number to set selectedCountry and displayPhoneNumber
            if (phone) {
                try {
                    const phoneNumber = parsePhoneNumber(phone);
                    if (phoneNumber) {
                        if (phoneNumber.country) {
                            setSelectedCountry(phoneNumber.country);
                        }
                        setDisplayPhoneNumber(getLocalPhoneDigits(phone, phoneNumber));
                    }
                } catch (e) {
                    // If parsing fails, try to extract digits
                    const digitsOnly = phone.replace(/\D/g, '');
                    const countryCode = getCountryCallingCode('SG');
                    if (digitsOnly.startsWith(countryCode)) {
                        setDisplayPhoneNumber(digitsOnly.substring(countryCode.length));
                    } else {
                        setDisplayPhoneNumber(digitsOnly);
                    }
                    setSelectedCountry('SG');
                }
            } else {
                setDisplayPhoneNumber('');
                setSelectedCountry('SG');
            }
    }, [customer, customerDetails, isEditMode]);

    // Warm the shared cache. The selector displays loading errors and provides retry.
    useEffect(() => {
        fetchCountries().catch(() => {});
    }, []);

    // Clear display when phone is cleared. Do not rewrite display from nationalNumber
    // while typing — that caused digits (e.g. leading zeros) to be trimmed.
    // Country detection from full numbers is handled on paste / country selector.
    // Initial edit-mode load still parses phone into displayPhoneNumber separately.
    useEffect(() => {
        if (!editForm.phone) {
            setDisplayPhoneNumber('');
        }
    }, [editForm.phone]);

    const purchaseSummary = {
        subtotal: Number(paymentSummary?.subtotal || 0),
        gst: Number(paymentSummary?.gst || 0),
        tip: Number(paymentSummary?.tip || 0),
        total: Number(paymentSummary?.total || 0)
    };

    const customerFirstName = customerDetails?.name || customer?.name || '-';
    const customerLastName = getLastName() || '-';
    const customerNric = customerDetails?.nric || customer?.nric || customerDetails?.passport_number || customer?.passport_number || '-';
    const customerEmail = customerDetails?.email || customer?.email || '-';
    const hasCustomerEmail = Boolean(String(customerDetails?.email || customer?.email || '').trim());
    const getEmailInvoiceLabel = (bookingId) => {
        const cooldownRemaining = getInvoiceEmailCooldownRemaining(bookingId);
        return cooldownRemaining > 0 ? `Email Invoice (${cooldownRemaining}s)` : 'Email Invoice';
    };
    const getEmailInvoiceTooltip = (bookingId) => {
        const cooldownRemaining = getInvoiceEmailCooldownRemaining(bookingId);
        if (cooldownRemaining > 0) {
            return 'Invoice emailed. Please wait before sending again.';
        }
        return !hasCustomerEmail ? 'Customer has no email address.' : '';
    };
    const isEmailInvoiceDisabled = (bookingId) => (
        !hasCustomerEmail || getInvoiceEmailCooldownRemaining(bookingId) > 0
    );
    const membershipDetail = customerDetails?.membership_detail || customer?.membership_detail || null;
    const customerMembershipId = membershipDetail?.membership_id || '-';
    const hasCustomerMembershipId = customerMembershipId != null && String(customerMembershipId).trim() !== '' && String(customerMembershipId).trim() !== '-';
    const hasMembershipDetail = Boolean(
        membershipDetail &&
        typeof membershipDetail === 'object' &&
        Object.keys(membershipDetail).length > 0
    );
    const rawMembershipFlag =
        customerDetails?.has_membership ??
        customer?.has_membership ??
        customerDetails?.membership ??
        customer?.membership;
    const rawMembershipStatus =
        membershipDetail?.status_label ??
        membershipDetail?.status ??
        customerDetails?.membership_status ??
        customer?.membership_status ??
        '';
    const normalizedMembershipStatus = String(rawMembershipStatus || '').trim().toLowerCase();
    const isMembershipActiveStatus =
        normalizedMembershipStatus === 'active' ||
        normalizedMembershipStatus === '1';
    const hasMembershipBadge = normalizedMembershipStatus
        ? isMembershipActiveStatus
        : (
            // hasMembershipDetail ||
            rawMembershipFlag === true ||
            rawMembershipFlag === 1 ||
            rawMembershipFlag === '1'
        );

    useEffect(() => {
        if (!isPurchaseMembershipModalOpen) return;
        setPurchaseClientForm({
            firstName: customerFirstName === '-' ? '' : customerFirstName,
            lastName: customerLastName === '-' ? '' : customerLastName,
            nric: customerNric === '-' ? '' : sanitizeNricInput(customerNric),
            email: customerEmail === '-' ? '' : customerEmail
        });
        setPurchaseClientNricError('');
    }, [isPurchaseMembershipModalOpen, customerFirstName, customerLastName, customerNric, customerEmail]);

    const isPurchaseClientFormDirty = (
        purchaseClientForm.firstName.trim() !== (customerFirstName === '-' ? '' : String(customerFirstName).trim()) ||
        purchaseClientForm.lastName.trim() !== (customerLastName === '-' ? '' : String(customerLastName).trim()) ||
        purchaseClientForm.nric.trim() !== (customerNric === '-' ? '' : String(customerNric).trim()) ||
        purchaseClientForm.email.trim() !== (customerEmail === '-' ? '' : String(customerEmail).trim())
    );

    const handlePurchaseClientFormReset = () => {
        setPurchaseClientForm({
            firstName: customerFirstName === '-' ? '' : customerFirstName,
            lastName: customerLastName === '-' ? '' : customerLastName,
            nric: customerNric === '-' ? '' : sanitizeNricInput(customerNric),
            email: customerEmail === '-' ? '' : customerEmail
        });
        setPurchaseClientNricError('');
    };

    useEffect(() => {
        if (!isOpen) {
            hasAutoOpenedMembershipFlowRef.current = false;
            return;
        }
        if (!autoOpenMembershipFlow || hasAutoOpenedMembershipFlowRef.current) {
            return;
        }
        hasAutoOpenedMembershipFlowRef.current = true;
        openMembershipModal(String(autoOpenMembershipFlow).toLowerCase() === 'topup' ? 'topup' : 'purchase');
    }, [isOpen, autoOpenMembershipFlow, customer?.id]);

    // Track if recovery membership sale has been processed to avoid re-running effect
    const recoveryMembershipSaleProcessedRef = useRef(false);

    useEffect(() => {
        if (!isOpen || !membershipFlowOnly || !recoveryMembershipSale) {
            recoveryMembershipSaleProcessedRef.current = false;
            return;
        }

        // Guard: only process once when recoveryMembershipSale is first provided
        if (recoveryMembershipSaleProcessedRef.current) return;
        recoveryMembershipSaleProcessedRef.current = true;

        const { transactionId, membershipId } = getOpenMembershipRecoveryIds(recoveryMembershipSale);
        const amount = getOpenMembershipSaleAmount(recoveryMembershipSale);
        const saleId = getMembershipSaleId(recoveryMembershipSale);

        if (!transactionId || !membershipId) {
            toast.error('Could not open the membership payment.');
            return;
        }

        preserveExternalCheckoutTotalsForMembershipFlow();
        setPurchaseMembershipFlowType('topup');
        setIsPurchaseMembershipModalOpen(true);
        setIsPurchaseClientDetailsOpen(false);
        setPurchaseMembershipStep('payment');
        setPurchaseBookingDataFromAPI({
            booking_id: String(transactionId).replace(/^#/, '').trim(),
            sale_id: saleId || null,
            deposit: 0,
            membership_total: amount,
        });
        setPurchaseFollowupMembershipId(String(membershipId).replace(/^#/, '').trim());
        setServiceTotal(amount);
        setPurchaseMembershipError('');
    }, [isOpen, membershipFlowOnly, recoveryMembershipSale]);

    useEffect(() => focusRefWhenReady(notesTextareaRef, { active: isNotesMode }), [isNotesMode]);

    if (!customer && !customerDetails && !isOverlayVisible && !membershipFlowOnly) return null;

    const formatDate = (dateString) => {
        if (dateString instanceof Date) {
            if (isNaN(dateString.getTime())) {
                return 'Invalid Date';
            }
            return dateString.toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }

        // Fix invalid date format like "2025-08-22 14:45:PM"
        let fixedDateString = dateString;
        if (typeof dateString === 'string' && dateString.includes('PM')) {
            // Convert "2025-08-22 14:45:PM" to "2025-08-22 14:45:00"
            fixedDateString = dateString.replace(':PM', ':00').replace(':AM', ':00');
        }
        
        const date = new Date(fixedDateString);
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        
        return date.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatShortDate = (dateString) => {
        if (dateString instanceof Date) {
            if (isNaN(dateString.getTime())) {
                return 'Invalid Date';
            }
            return dateString.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
        }

        // Fix invalid date format like "2025-08-22 14:45:PM"
        let fixedDateString = dateString;
        if (typeof dateString === 'string' && dateString.includes('PM')) {
            // Convert "2025-08-22 14:45:PM" to "2025-08-22 14:45:00"
            fixedDateString = dateString.replace(':PM', ':00').replace(':AM', ':00');
        }
        
        const date = new Date(fixedDateString);
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }
        
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    const parseTimelineDate = (dateString) => {
        if (!dateString) return null;
        if (dateString instanceof Date) {
            return isNaN(dateString.getTime()) ? null : new Date(dateString);
        }
        if (typeof dateString !== 'string') return null;

        let fixedDateString = dateString;
        if (fixedDateString.includes('PM') || fixedDateString.includes('AM')) {
            fixedDateString = fixedDateString.replace(':PM', ':00').replace(':AM', ':00');
        }
        if (fixedDateString.includes(' ') && !fixedDateString.includes('T')) {
            fixedDateString = fixedDateString.replace(' ', 'T');
        }

        const parsedDate = new Date(fixedDateString);
        return isNaN(parsedDate.getTime()) ? null : parsedDate;
    };

    const addDaysToDateString = (dateString, daysToAdd = 0) => {
        const parsedDate = parseTimelineDate(dateString);
        if (!parsedDate) return null;
        const nextDate = new Date(parsedDate);
        nextDate.setDate(nextDate.getDate() + daysToAdd);
        return nextDate;
    };

    const getOrdinalSuffix = (day) => {
        if (day >= 11 && day <= 13) return 'th';
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    };

    const formatExpansionBracketDate = (dateString) => {
        const nextDate = addDaysToDateString(dateString, 1);
        if (!nextDate) return '';
        const day = nextDate.getDate();
        const month = nextDate.toLocaleDateString('en-US', { month: 'long' }).toLowerCase();
        return `${day}${getOrdinalSuffix(day)} ${month}`;
    };

    const shouldDisplayBookingOnNextDate = (item) => {
        if (item?.type !== 'booking') return false;
        if (!Array.isArray(item?.services) || item.services.length === 0) return false;
        return item.services.every((service) => Number(service?.is_calendar_expansion) === 1);
    };

    const getDisplayDateForTimelineItem = (item) => {
        if (!shouldDisplayBookingOnNextDate(item)) {
            return item?.date;
        }

        return addDaysToDateString(item.services[0]?.service_at || item?.date, 1) || item?.date;
    };

    const formatBookingTime = (dateString, isCalendarExpansion = false) => {
        if (!dateString) return 'N/A';

        if (dateString instanceof Date) {
            if (isNaN(dateString.getTime())) {
                return 'N/A';
            }
            const formattedTime = dateString.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });
            return `${formattedTime}${isCalendarExpansion ? ' +1' : ''}`;
        }
        
        // Handle different date formats
        let fixedDateString = dateString;
        
        // Handle format like "2025-10-24 17:00:00"
        if (typeof dateString === 'string' && dateString.includes(' ')) {
            const [datePart, timePart] = dateString.split(' ');
            if (timePart) {
                const [hours, minutes] = timePart.split(':');
                const hour = parseInt(hours, 10);
                const min = minutes || '00';
                const ampm = hour >= 12 ? 'PM' : 'AM';
                const hour12 = hour % 12 || 12;
                return `${hour12}:${min} ${ampm}${isCalendarExpansion ? ' +1' : ''}`;
            }
        }
        
        // Try to parse as date
        const date = new Date(dateString);
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
            return 'N/A';
        }
        
        const formattedTime = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        return `${formattedTime}${isCalendarExpansion ? ' +1' : ''}`;
    };

    const getBookingTimeDisplay = (service, item) => {
        const isExpandedService = Number(service?.is_calendar_expansion) === 1;
        if (!isExpandedService) {
            return formatBookingTime(service?.service_at, false);
        }

        if (shouldDisplayBookingOnNextDate(item)) {
            return formatBookingTime(service?.service_at, false);
        }

        const hasMixedServices =
            Array.isArray(item?.services) &&
            item.services.length > 1 &&
            item.services.some((timelineService) => Number(timelineService?.is_calendar_expansion) === 0);

        if (hasMixedServices) {
            const baseTime = formatBookingTime(service?.service_at, false);
            const nextDateLabel = formatExpansionBracketDate(service?.service_at);
            return nextDateLabel ? `${baseTime} (${nextDateLabel})` : baseTime;
        }

        return formatBookingTime(service?.service_at, false);
    };

    const getServiceAddons = (service) => {
        return Array.isArray(service?.addons) ? service.addons : [];
    };

    const getAddonTotalPrice = (addon) => {
        const explicitTotal = parseFloat(addon?.total_price);
        if (Number.isFinite(explicitTotal)) {
            return explicitTotal;
        }

        const unitPrice = parseFloat(addon?.unit_price);
        const quantity = parseFloat(addon?.quantity);
        const normalizedQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;

        return Number.isFinite(unitPrice) ? unitPrice * normalizedQuantity : 0;
    };

    const getServiceAddonsTotal = (service) => {
        const addons = getServiceAddons(service);
        if (addons.length > 0) {
            return addons.reduce((sum, addon) => sum + getAddonTotalPrice(addon), 0);
        }

        const fallbackTotal = parseFloat(service?.addons_total);
        return Number.isFinite(fallbackTotal) ? fallbackTotal : 0;
    };

    const getServiceTotalPrice = (service) => {
        const basePrice = parseFloat(service?.price) || 0;
        return basePrice + getServiceAddonsTotal(service);
    };

    const renderServiceAddons = (service) => {
        const addons = getServiceAddons(service);
        if (addons.length === 0) {
            return null;
        }

        const addonLabels = addons.map((addon) => {
            const quantity = parseFloat(addon?.quantity);
            const normalizedQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
            return normalizedQuantity > 1
                ? `${addon?.name || 'Add-on'} x${normalizedQuantity}`
                : (addon?.name || 'Add-on');
        });

        return (
            <div className={styles.addonsList}>
                <div className={styles.addonRow}>
                    <span className={styles.addonName}>Add-ons - {addonLabels.join(', ')}</span>
                </div>
            </div>
        );
    };

    const getTherapistPagerColor = (gender) => {
        return String(gender || '').toLowerCase() === 'male' ? '#3B82F6' : '#EC4899';
    };

    const isSaleTimelineItem = (item) => item?.type === 'product_sale';

    const normalizeTimelineSaleStatusKey = (value) =>
        String(value || '').trim().toLowerCase().replace(/\s+/g, '');

    const isClosedSaleStatus = (status) => {
        const statusKey = normalizeTimelineSaleStatusKey(status);
        return statusKey === 'closed' || statusKey === 'paid' || statusKey === 'completed';
    };

    const canShowSaleInvoiceMenu = (item) =>
        isSaleTimelineItem(item) && isClosedSaleStatus(item?.status);

    const isAppointmentClickDisabled = (item) => {
        const status = String(item?.status || '').trim().toLowerCase();
        return item?.is_voided_or_refunded ||
            status === 'voided' ||
            status === 'refunded' ||
            status === 'refund';
    };

    const getInvoiceBaseUrl = () => (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');

    const openInvoiceDocument = async (bookingId, format) => {
        if (!bookingId) {
            toast.error('Sale ID not found.', { containerId: 'customerDetailsModal' });
            return;
        }

        const invoiceBaseUrl = getInvoiceBaseUrl();
        if (!invoiceBaseUrl) {
            toast.error('Invoice base URL is not configured.', { containerId: 'customerDetailsModal' });
            return;
        }

        try {
            await downloadFileWithAuth(
                `${invoiceBaseUrl}/sales/${bookingId}/invoice/${format}`,
                `invoice.${format}`
            );
        } catch (error) {
            logger.error('Invoice download failed:', error);
            toast.error(error?.message || 'Failed to download invoice', {
                containerId: 'customerDetailsModal',
            });
        }
    };

    const handleEmailInvoice = async (bookingId) => {
        if (!bookingId) {
            toast.error('Sale ID not found.', { containerId: 'customerDetailsModal' });
            return;
        }

        try {
            const response = await emailBookingInvoice(bookingId);
            const payload = response?.response?.data ?? response?.data ?? response ?? {};
            const nestedPayload = payload?.data && typeof payload.data === 'object' ? payload.data : {};
            const isExplicitFailure = [payload?.success, nestedPayload?.success, payload?.status, nestedPayload?.status, ].some((value) => value === false || value === 'false' || value === 0 || value === '0');
            const isSuccess =
                payload?.success === true ||
                nestedPayload?.success === true ||
                payload?.status === true || nestedPayload?.status === true ||
                (
                    payload?.success !== false &&
                    nestedPayload?.success !== false &&
                    payload?.status !== false &&  nestedPayload?.status !== false &&
                    !payload?.errors &&
                    !nestedPayload?.errors
                );
            const successMessage = payload?.message || nestedPayload?.message || 'Invoice emailed successfully.';
            const errorMessage = payload?.message || nestedPayload?.message || 'Failed to email invoice.';
            const isMissingEmail = errorMessage === 'Customer has no email address.';

            if (!isExplicitFailure && isSuccess) {
                toast.success(successMessage, { containerId: 'customerDetailsModal' });
                startInvoiceEmailCooldown(bookingId);
                return;
            }

            if (isMissingEmail) {
                toast.info(errorMessage, { containerId: 'customerDetailsModal' });
                return;
            }

            toast.error(errorMessage, { containerId: 'customerDetailsModal' });
        } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to email invoice.';

            if (errorMessage === 'Customer has no email address.') {
                toast.info(errorMessage, { containerId: 'customerDetailsModal' });
                return;
            }

            toast.error(
                errorMessage,
                { containerId: 'customerDetailsModal' }
            );
        }
    };



    const getLastAppointmentDate = () => {
        // Check if customerDetails or customer has last_appointment_date field
        const lastAppointmentDate = customerDetails?.last_appointment_date || customer?.last_appointment_date;
        
        if (lastAppointmentDate) {
            
            try {
                // Parse date from "2025-10-07" format (YYYY-MM-DD)
                // Split the date string to avoid timezone issues
                const dateParts = lastAppointmentDate.split('-');
                if (dateParts.length === 3) {
                    const year = parseInt(dateParts[0], 10);
                    const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed
                    const day = parseInt(dateParts[2], 10);
                    
                    // Create date object using local timezone
                    const date = new Date(year, month, day);
                    
                    // Check if date is valid
                    if (isNaN(date.getTime())) {
                        return 'N/A';
                    }
                    
                    // Format as "10 Sep 2025" (day month year)
                    const formattedDay = date.getDate();
                    const formattedMonth = date.toLocaleDateString('en-US', { month: 'short' });
                    const formattedYear = date.getFullYear();
                    
                    return `${formattedDay} ${formattedMonth} ${formattedYear}`;
                } else {
                    // Fallback: try parsing as-is
                    const date = new Date(lastAppointmentDate);
                    if (!isNaN(date.getTime())) {
                        const formattedDay = date.getDate();
                        const formattedMonth = date.toLocaleDateString('en-US', { month: 'short' });
                        const formattedYear = date.getFullYear();
                        return `${formattedDay} ${formattedMonth} ${formattedYear}`;
                    }
                    return 'N/A';
                }
            } catch (error) {
                console.error('Error formatting last appointment date:', error);
                return 'N/A';
            }
        }
        
        // Return N/A if no last_appointment_date data is available
        return 'N/A';
    };

    const getFilteredTimeline = () => {
        let filteredTimeline = customerTimeline;
        
        if (activeFilter === 'appointments') {
            filteredTimeline = customerTimeline.filter(item => item.type === 'booking');
        } else if (activeFilter === 'sales') {
            filteredTimeline = customerTimeline.filter(item => item.type === 'product_sale');
        }
        
        // Categorize into upcoming and past
        const now = new Date();
        const upcoming = [];
        const past = [];

        filteredTimeline.forEach(item => {
            // Product sales are always completed transactions, so they should always be in Past
            if (item.type === 'product_sale') {
                past.push(item);
                return;
            }

            // Parse date using robust logic
            const parseDate = (dateStr) => {
                if (!dateStr) return null;
                const str = String(dateStr).trim();
                
                // Try SQL format: YYYY-MM-DD HH:mm:ss, allowing for optional parts
                // Matches YYYY-MM-DD HH:mm:ss or YYYY-MM-DD HH:mm
                const sqlRegex = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[\sT]+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?/;
                const sqlMatch = str.match(sqlRegex);
                if (sqlMatch) {
                    const [_, y, m, d, h, min, s] = sqlMatch;
                    return new Date(
                        parseInt(y, 10),
                        parseInt(m, 10) - 1,
                        parseInt(d, 10),
                        parseInt(h, 10),
                        parseInt(min, 10),
                        parseInt(s || '0', 10)
                    );
                }

                // Handling for AM/PM if needed...
                // ... (simplified fallout to Date.parse)
                
                // Try standard Date constructor as fallback (handles ISO and many other formats)
                const d = new Date(str);
                if (!isNaN(d.getTime())) return d;
                
                return null;
            };

            const eventDateTime = parseDate(item.date);
            
            if (!eventDateTime || isNaN(eventDateTime.getTime())) {
                // If date is invalid, treat as past
                past.push(item);
            } else if (eventDateTime > now) {
                upcoming.push(item);
            } else {
                past.push(item);
            }
        });

        return { upcoming: upcoming.reverse(), past };
    };

    // Edit form validation
    const validateEditField = (fieldName, value) => {
        const errors = { ...editErrors };
        
        switch (fieldName) {
            case 'firstName':
                if (!value.trim()) {
                    errors[fieldName] = 'First name is required';
                } else {
                    delete errors[fieldName];
                }
                break;
            case 'lastName':
                // Last name is optional, no validation needed
                delete errors[fieldName];
                break;
            case 'phone':
                if (!value.trim()) {
                    errors[fieldName] = 'Mobile number is required';
                } else if (!isValidPhoneNumber(value)) {
                    errors[fieldName] = 'Invalid phone number';
                } else {
                    delete errors[fieldName];
                }
                break;
            case 'email':
                if (value.trim() && !/\S+@\S+\.\S+/.test(value)) {
                    errors[fieldName] = 'Invalid email format';
                } else {
                    delete errors[fieldName];
                }
                break;
            default:
                break;
        }
        
        setEditErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateEditForm = () => {
        const errors = {};
        
        if (!editForm.firstName.trim()) errors.firstName = 'First name is required';
        // Last name is optional, no validation needed
        
        // Check if phone is empty first
        if (!editForm.phone.trim()) {
            errors.phone = 'Mobile number is required';
        } else if (!isValidPhoneNumber(editForm.phone)) {
            errors.phone = 'Invalid phone number';
        }
        
        if (editForm.email.trim() && !/\S+@\S+\.\S+/.test(editForm.email)) {
            errors.email = 'Invalid email format';
        }
        
        setEditErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleEditSave = async () => {
        if (!validateEditForm()) {
            return;
        }

        setIsSaving(true);
        try {
            await fetchCountries().catch(() => null);
            const countryCode = typeof selectedCountry === 'object' ? selectedCountry?.code : selectedCountry;
            const country = findCountryByCodeSync(countryCode);
            if (!country?.id) {
                setEditErrors((prev) => ({ ...prev, phone: 'Unable to load the selected country. Please try again.' }));
                return;
            }
            // Format DOB to YYYY-MM-DD using local date (not UTC) to avoid timezone shift
            let formattedDob = null;
            if (editForm.dateOfBirth) {
                const year = editForm.dateOfBirth.getFullYear();
                const month = String(editForm.dateOfBirth.getMonth() + 1).padStart(2, '0');
                const day = String(editForm.dateOfBirth.getDate()).padStart(2, '0');
                formattedDob = `${year}-${month}-${day}`;
            }
            
            const payload = {
                name: editForm.firstName.trim(),
                last_name: editForm.lastName.trim(),
                contact_number: editForm.phone,
                email: editForm.email.trim() || null,
                gender: editForm.gender,
                dob: formattedDob,
                // membership: customerDetails?.membership || customer?.membership || 0,
                newsletter: editForm.newsletter ? 1 : 0,
                status: customerDetails?.status || customer?.status || 1,
                country_id: country.id
            };

            const response = await updateCustomerData(customer.id, payload);
            
            if (response?.data?.success) {
                // Construct updated customer data from form and existing customer data
                const updatedCustomerData = {
                    ...(customerDetails || customer),
                    id: customer.id,
                    name: editForm.firstName.trim(),
                    lastname: editForm.lastName.trim(),
                    contact_number: editForm.phone,
                    country_id: country.id,
                    email: editForm.email.trim() || null,
                    gender: editForm.gender,
                    dob: formattedDob,
                    date_of_birth: formattedDob,
                    // Keep both fields in sync for compatibility across API/UI variants
                    marketing: editForm.newsletter ? 1 : 0,
                    newsletter: editForm.newsletter ? 1 : 0,
                    membership: customerDetails?.membership ?? customer?.membership ?? 1,
                    status: customerDetails?.status ?? customer?.status ?? 1,
                };
                
                // Update local state directly instead of refetching
                setCustomerDetails(updatedCustomerData);
                
                // Trigger a custom event to refresh the customer details with updated data
                window.dispatchEvent(new CustomEvent('customerUpdated', {
                    detail: { customer: updatedCustomerData }
                }));
                toast.success('Profile updated successfully', { containerId: 'customerDetailsModal' });
                setIsEditMode(false);
            } else {
                // Handle API errors
                console.error('Failed to update customer:', response);
                const apiErrors = response?.errors || {};
                
                // Show toast for specific error types
                if (apiErrors.email) {
                    toast.error(apiErrors.email[0], { containerId: 'customerDetailsModal' });
                } else if (apiErrors.contact_number) {
                    toast.error(apiErrors.contact_number[0], { containerId: 'customerDetailsModal' });
                } else if (apiErrors.name) {
                    toast.error(apiErrors.name[0], { containerId: 'customerDetailsModal' });
                } else if (apiErrors.last_name) {
                    toast.error(apiErrors.last_name[0], { containerId: 'customerDetailsModal' });
                } else {
                    // Generic error message
                    toast.error('Failed to update customer. Please try again.', { containerId: 'customerDetailsModal' });
                }
                
                // Map API errors to our field names for form display
                const mappedErrors = {};
                if (apiErrors.name) mappedErrors.firstName = apiErrors.name[0];
                if (apiErrors.last_name) mappedErrors.lastName = apiErrors.last_name[0];
                if (apiErrors.contact_number) mappedErrors.phone = apiErrors.contact_number[0];
                if (apiErrors.email) mappedErrors.email = apiErrors.email[0];
                setEditErrors(mappedErrors);
            }
        } catch (error) {
            console.error('Error updating customer:', error);
            toast.error('Error updating customer. Please try again.', { containerId: 'customerDetailsModal' });
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditCancel = () => {
        setIsEditMode(false);
        setEditErrors({});
        // Reset phone number display and country when canceling
        const phone = customerDetails?.contact_number || customer?.contact_number || '';
        if (phone) {
            try {
                const phoneNumber = parsePhoneNumber(phone);
                if (phoneNumber) {
                    if (phoneNumber.country) {
                        setSelectedCountry(phoneNumber.country);
                    }
                    setDisplayPhoneNumber(getLocalPhoneDigits(phone, phoneNumber));
                }
            } catch (e) {
                setDisplayPhoneNumber('');
                setSelectedCountry('SG');
            }
        } else {
            setDisplayPhoneNumber('');
            setSelectedCountry('SG');
        }
    };

    // Notes handling functions
    const handleAddNotes = () => {
        setIsNotesMode(true);
        setNotesText('');
        setIsEditingNote(false); // Set to false for adding
    };

    const handleEditNotes = () => {
        setIsNotesMode(true);
        setNotesText(existingNotes);
        setIsEditingNote(true); // Set to true for editing
    };

    const handleNotesCancel = () => {
        setIsNotesMode(false);
        setNotesText('');
        setIsEditingNote(false);
    };

    const handleNotesSave = async () => {
        setIsSavingNotes(true);
        try {
            const normalizedNotes = notesText.trim();
            const payload = {
                notes: normalizedNotes
            };

            const response = await postUserNotes(customer.id, payload);

            if (response?.data?.success) {
                toast.success(
                    isEditingNote ? 'Notes edited successfully' : 'Notes added successfully',
                    { containerId: 'customerDetailsModal' }
                );
                
                // Update the existing notes directly instead of refetching
                setExistingNotes(normalizedNotes);
                
                // Update customer details state with new notes
                if (customerDetails) {
                    setCustomerDetails({
                        ...customerDetails,
                        notes: normalizedNotes
                    });
                }
                
                setIsNotesMode(false);
                setNotesText('');
                setIsEditingNote(false);
                
                // Note: We don't need to refetch as we've updated the state directly
                // This prevents the duplicate API call
                window.dispatchEvent(new CustomEvent('customerNotesUpdated', {
                    detail: { customerId: customer.id, notes: normalizedNotes }
                }));
            } else {
                console.error('Failed to add note:', response);
                const apiMessage =
                    response?.data?.message ||
                    response?.message ||
                    response?.data?.errors?.notes?.[0] ||
                    'Failed to add note. Please try again.';
                toast.error(apiMessage, { containerId: 'customerDetailsModal' });
            }
        } catch (error) {
            console.error('Error adding note:', error);
            const apiMessage =
                error?.response?.data?.message ||
                error?.response?.data?.errors?.notes?.[0] ||
                'Error adding note. Please try again.';
            toast.error(apiMessage, { containerId: 'customerDetailsModal' });
        } finally {
            setIsSavingNotes(false);
        }
    };

    const switchOutletIfNeeded = (targetOutletId) => {
        const normalizedTargetOutletId = Number(targetOutletId);
        if (!normalizedTargetOutletId || Number.isNaN(normalizedTargetOutletId)) {
            return;
        }
        const currentOutletId = Number(selectedOutlet || loggedOutletID || 0);
        if (currentOutletId === normalizedTargetOutletId) {
            return;
        }
        setSelectedOutlet(normalizedTargetOutletId);
        if (typeof window !== 'undefined') {
            localStorage.setItem('outlet_id', String(normalizedTargetOutletId));
        }
    };
    const redirectWithOutlet = (path, outletId = null) => {
        switchOutletIfNeeded(outletId);
        onClose();
        // Let outlet state persist before route-driven data fetching starts.
        setTimeout(() => navigate(path), 0);
    };

    const timelineOpenMembershipSale = customerTimeline.find((item) => {
        const normalizedItem = {
            ...item,
            type: item?.type === 'membership_sale' ? 'membership' : item?.type,
        };
        return isOpenMembershipSale(normalizedItem);
    });
    const hasRecoverableSuspendedMembership =
        hasMembershipDetail &&
        !hasCustomerMembershipId &&
        membershipDetail?.payment_pending === true &&
        (
            normalizedMembershipStatus === 'suspended' ||
            normalizedMembershipStatus === 'inactive'
        );
    const effectiveOpenMembershipSale = openMembershipSale || timelineOpenMembershipSale || null;
   
    const hasOpenSales = Boolean(effectiveOpenMembershipSale) || hasRecoverableSuspendedMembership;

    const fetchPartialMembershipTransactionSale = async () => {
        return findPendingMembershipTransactionSale(
            membershipDetail,
            getMembershipRegistrationOutletId()
        );
    };

    const handleGoToOpenMembershipSale = async (saleToOpen = effectiveOpenMembershipSale) => {
        const openSaleId = getMembershipSaleId(saleToOpen);
        if (openSaleId) {
            openSalesSidebarFromSale({
                ...(saleToOpen || {}),
                id: openSaleId,
                sale_id: openSaleId,
                type: 'membership',
                initialStep: 'payment',
            });
            return;
        }

        setIsOpenMembershipSaleLoading(true);
        try {
            const partialTransaction = await fetchPartialMembershipTransactionSale();
            if (!partialTransaction) {
                toast.error('Could not find the open membership transaction.');
                return;
            }
            const transactionSaleId = normalizeMembershipSaleId(partialTransaction?.sale_id);
            if (!transactionSaleId) {
                await handleMembershipTransactionSaleClick(partialTransaction);
                return;
            }
            openSalesSidebarFromSale({
                ...partialTransaction,
                id: transactionSaleId,
                sale_id: transactionSaleId,
                type: 'membership',
                initialStep: 'payment',
            });
        } catch (error) {
            console.error('Failed to find open membership sale:', error);
            toast.error('Could not find the open membership sale.');
        } finally {
            setIsOpenMembershipSaleLoading(false);
        }
    };

    const openExistingMembershipSaleIfPresent = async () => {
        const customerId = customerDetails?.id || customer?.id || '';
        if (!customerId) return false;

        if (effectiveOpenMembershipSale) {
            await handleGoToOpenMembershipSale(effectiveOpenMembershipSale);
            return true;
        }

        if (hasRecoverableSuspendedMembership) {
            await handleGoToOpenMembershipSale(null);
            return true;
        }

        // If the initial open-sale check (on modal mount) already completed
        // and found nothing, skip the redundant API call — no open sale exists.
        if (!isOpenMembershipSaleLoading) return false;

        setIsOpenMembershipSaleLoading(true);
        try {
            const sale = await findAnyOpenSaleForCustomer(
                customerId,
                selectedOutlet || loggedOutletID || null,
                membershipDetail
            );
            if (!sale) return false;
            setOpenMembershipSale(sale);
            await handleGoToOpenMembershipSale(sale);
            return true;
        } catch (error) {
            console.error('Error checking existing membership sale:', error);
            return false;
        } finally {
            setIsOpenMembershipSaleLoading(false);
        }
    };

    const renderMembershipOpenSalesBanner = (iconVariant = 'default') => {
        if (!hasOpenSales) return null;

        return (
            <div className={styles.membershipOpenSalesBanner}>
                <div className={styles.membershipOpenSalesMessage}>
                    <span className={styles.membershipOpenSalesIcon} aria-hidden="true">
                        {iconVariant === 'empty' ? (
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M4.75 13.35L13.35 4.75H22.25V13.65L13.65 22.25L4.75 13.35Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
                                <path d="M9.75 13.35L18.35 4.75" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                                <circle cx="19" cy="8.7" r="1.45" fill="currentColor" />
                            </svg>
                        ) : (
                            <svg width="23" height="18" viewBox="0 0 23 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M18.1055 3.4375C17.5977 3.4375 17.168 3.86719 17.168 4.375C17.168 4.92188 17.5977 5.3125 18.1055 5.3125C18.6523 5.3125 19.043 4.92188 19.043 4.375C19.043 3.86719 18.6523 3.4375 18.1055 3.4375ZM5.64453 7.57812C4.78516 8.4375 4.78516 9.80469 5.64453 10.6641L11.8555 16.875C12.2852 17.3047 12.832 17.5 13.3789 17.5C13.9648 17.5 14.5117 17.3047 14.9414 16.875L21.8555 9.96094C22.207 9.60938 22.4805 8.90625 22.4805 8.39844V1.09375C22.4805 0.507812 22.0117 0 21.3867 0H14.1211C13.6133 0 12.9102 0.3125 12.5586 0.664062L5.64453 7.57812ZM6.54297 9.80469C6.34766 9.60938 6.23047 9.375 6.23047 9.14062C6.23047 8.86719 6.34766 8.63281 6.54297 8.47656L13.457 1.5625C13.5742 1.44531 13.9258 1.25 14.1211 1.25H21.2305V8.39844C21.2305 8.59375 21.0742 8.94531 20.957 9.0625L14.043 15.9766C13.8867 16.1719 13.6523 16.25 13.3789 16.25C13.1445 16.25 12.9102 16.1719 12.7148 15.9766L6.54297 9.80469ZM0.644531 7.57812C-0.214844 8.4375 -0.214844 9.80469 0.644531 10.6641L7.28516 17.3438C7.55859 17.5781 7.94922 17.5781 8.18359 17.3438C8.41797 17.1094 8.41797 16.6797 8.18359 16.4453L1.54297 9.80469C1.34766 9.60938 1.23047 9.375 1.23047 9.14062C1.23047 8.86719 1.34766 8.63281 1.54297 8.47656L8.45703 1.5625C8.61328 1.40625 8.88672 1.25 9.12109 1.25H9.35547C9.70703 1.25 9.98047 0.976562 9.98047 0.625C9.98047 0.3125 9.70703 0 9.35547 0H9.12109C8.61328 0 7.91016 0.3125 7.55859 0.664062L0.644531 7.57812Z" fill="#222222" />
                            </svg>
                        )}
                    </span>
                    <span>There are open sales for this client.</span>
                </div>
                <button
                    type="button"
                    className={styles.membershipOpenSalesLink}
                    disabled={isOpenMembershipSaleLoading}
                    onClick={() => {
                        handleGoToOpenMembershipSale(effectiveOpenMembershipSale);
                    }}
                >
                    {isOpenMembershipSaleLoading ? 'Checking sales...' : 'Go to open sales'}
                </button>
            </div>
        );
    };

    const formatMembershipExpiryDate = (value) => {
        if (!value) return '-';
        const parsed = new Date(value);
        if (Number.isNaN(parsed.getTime())) return String(value);
        return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getRegistrationOutletName = () => {
        const outletName =
            membershipDetail?.registration_outlet_name ||
            membershipDetail?.outlet_name ||
            customerDetails?.outlet_name ||
            customer?.outlet_name;
        return outletName || '-';
    };

    const shouldUseDefaultLinkNavigation = (event) => ( event.defaultPrevented || event.button !== 0 ||event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);

    const getSaleLinkHref = (item) => {
        const saleId = item?.sale_id || item?.id;
        if (!saleId) return null;
        const params = new URLSearchParams({ search: String(saleId) });
        if (item?.outlet_id) {
            params.set('outlet', String(item.outlet_id));
        }
        return `/sales?${params.toString()}`;
    };

    const getAppointmentLinkHref = (item) => {
        const bookingId = item?.booking_id || item?.id;
        if (!bookingId) return null;
        const serviceDate = String(item.services?.[0]?.service_at || item.date || '').split(/[ T]/)?.[0] || '';
        const params = new URLSearchParams({ bookingId: String(bookingId) });
        if (serviceDate) {
            params.set('date', serviceDate);
        }
        return `/?${params.toString()}`;
    };

    const getAppointmentLabel = (item) => {
        const appointmentId = item?.booking_id || item?.id;
        return appointmentId ? `Appointment(#${appointmentId})` : 'Appointment';
    };

    const handleTimelineLinkClick = (event, action) => {
        if (shouldUseDefaultLinkNavigation(event)) return;
        event.preventDefault();
        action();
    };

    // Handle sale ID click - redirect to sales page with search
    const handleSaleClick = (item) => {
        const saleId = item?.sale_id || item?.id;
        if (!saleId) return;
        redirectWithOutlet(`/sales?search=${saleId}`, item.outlet_id);
    };

    const handlePaymentGoToSale = (saleId) => {
        const resolvedSaleId = String(saleId || '').replace(/^#/, '').trim();
        if (!resolvedSaleId) {
            onClose?.();
            return;
        }
        redirectWithOutlet(`/sales?search=${encodeURIComponent(resolvedSaleId)}`);
    };

    // Handle appointment click - redirect to home/calendar page
    const handleAppointmentClick = (item) => {
        const bookingId = item?.booking_id || item?.id;
        if (!bookingId) return;
        const parsedDate = parseCalendarRedirectDate(item.services?.[0]?.service_at || item.date);
        // Only update selectedDate when the calendar day actually changes.
        // A new Date for the same day still retriggers Home's loader gate and can stick forever.
        if (parsedDate) {
            const isSameSelectedDay =
                selectedDate instanceof Date &&
                !isNaN(selectedDate.getTime()) &&
                selectedDate.getFullYear() === parsedDate.getFullYear() &&
                selectedDate.getMonth() === parsedDate.getMonth() &&
                selectedDate.getDate() === parsedDate.getDate();
            if (!isSameSelectedDay) {
                setSelectedDate(parsedDate);
            }
        }
        const serviceDate = parsedDate ? formatDateToYMD(parsedDate) : '';
        sessionStorage.setItem("skipCurrentTimeAutoScroll", "1");
        redirectWithOutlet(`/?bookingId=${bookingId}&date=${serviceDate}`, item.outlet_id);
    };

    // Handle delete customer
    const handleDeleteCustomer = async () => {
        if (!customer?.id) {
            toast.error('Unable to delete customer. Customer ID not found.', { containerId: 'customerDetailsModal' });
            return;
        }

        setIsDeleting(true);
        try {
            const response = await deleteCustomer(customer.id);
            
          
            
            // Check for success in different response formats
            const isSuccess = response?.success === true || response?.data?.success === true;
            
            if (isSuccess) {
                // First close the confirmation popup
                setShowDeleteConfirm(false);
                
                // Show success toast
                toast.success('Client deleted successfully!', { containerId: 'customerDetailsModal' });
                
                // Then close the modal after a short delay
                setTimeout(() => {
                    onClose();
                    // Trigger a custom event to refresh the clients list
                    window.dispatchEvent(new Event('customerDeleted'));
                }, 100);
            } else if (response?.data?.has_bookings || response?.data?.data?.has_bookings) {
                // Handle case where customer has upcoming bookings
                const message = response?.message || response?.data?.message || "Cannot delete customer with upcoming bookings.";
                setDeleteErrorMessage(message);
                // Do not close popup here, let user see the message and close it
            } else {
                const errorMessage = response?.message || response?.data?.message || 'Failed to delete client. Please try again.';
                toast.error(errorMessage, { containerId: 'customerDetailsModal' });
                setShowDeleteConfirm(false);
            }
        } catch (error) {
            console.error('Error deleting customer:', error);
            toast.error('Error deleting client. Please try again.', { containerId: 'customerDetailsModal' });
            setShowDeleteConfirm(false);
        } finally {
            setIsDeleting(false);
        }
    };

    const normalizeToArray = (response) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.data)) return response.data;
        if (Array.isArray(response?.data?.list)) return response.data.list;
        if (Array.isArray(response?.data?.list?.list)) return response.data.list.list;
        if (Array.isArray(response?.data?.data?.list)) return response.data.data.list;
        if (Array.isArray(response?.data?.data?.list?.list)) return response.data.data.list.list;
        if (Array.isArray(response?.data?.data)) return response.data.data;
        if (Array.isArray(response?.results)) return response.results;
        if (Array.isArray(response?.items)) return response.items;
        if (Array.isArray(response?.data?.items)) return response.data.items;
        return [];
    };

    const normalizeMembershipPackages = (response) => {
        
        const looksLikeMembershipPackage = (item) => {
            if (!item || typeof item !== 'object') return false;
            const idOk = item.id !== undefined || item.membership_package_id !== undefined || item.membership_packages_id !== undefined;
            const title = item.title ?? item.name ?? item.package_name ?? item.membership_package_name;
            const titleOk = typeof title === 'string' && title.trim().startsWith('$');
            return idOk && titleOk;
        };

        const findArray = (obj, depth = 0, visited = new Set()) => {
            if (!obj || typeof obj !== 'object') return null;
            if (visited.has(obj)) return null;
            visited.add(obj);
            if (depth > 8) return null;

            if (Array.isArray(obj)) {
                if (obj.some(looksLikeMembershipPackage)) return obj;
                
                for (const el of obj) {
                    const found = findArray(el, depth + 1, visited);
                    if (found) return found;
                }
                return null;
            }

            for (const key of Object.keys(obj)) {
                const found = findArray(obj[key], depth + 1, visited);
                if (found) return found;
            }
            return null;
        };

        return findArray(response) || [];
    };

    const getMembershipPackageId = (membershipPackage, index) => {
        return membershipPackage?.id ?? membershipPackage?.membership_package_id ?? membershipPackage?.membership_packages_id ?? `membership-package-${index + 1}`;
    };

    const getMembershipPackageLabel = (membershipPackage, index) => {
        const amountValue = membershipPackage?.amount ?? membershipPackage?.price ?? membershipPackage?.value;

        const formatMoney = (value) => {
            if (value === undefined || value === null || value === '') return null;
            if (typeof value === 'number') return `$${value}`;
            const str = String(value).trim();
            if (!str) return null;
            
            if (str.startsWith('$')) return str;
            const numeric = Number(str.replace(/[^0-9.]/g, ''));
            if (!Number.isNaN(numeric)) return `$${numeric}`;
            return str;
        };

        const formattedMoney = formatMoney(amountValue);
        if (formattedMoney) return formattedMoney;

        return (
            membershipPackage?.title ||
            membershipPackage?.name ||
            membershipPackage?.package_name ||
            membershipPackage?.membership_package_name ||
            `Package ${index + 1}`
        );
    };

    const getMembershipPackageAmount = (membershipPackage) => {
        if (!membershipPackage || typeof membershipPackage !== 'object') return 0;
        const amountSource =
            membershipPackage?.credit_value ??
            membershipPackage?.creditValue ??
            membershipPackage?.amount ??
            membershipPackage?.price ??
            membershipPackage?.value;
        if (amountSource === undefined || amountSource === null) return 0;
        const parsedAmount = Number(String(amountSource).replace(/[^0-9.]/g, ''));
        return Number.isNaN(parsedAmount) ? 0 : parsedAmount;
    };

    const getGiftSectionLabel = (giftSection, index) => {
        return giftSection?.title || giftSection?.name || giftSection?.label || giftSection?.category_name || giftSection?.gift_category_name || `Gift ${String.fromCharCode(65 + index)}`;
    };

    const getGiftOptionLabel = (giftOption, index) => {
        return giftOption?.title || giftOption?.name || giftOption?.label || giftOption?.gift_name || giftOption?.voucher_name || `Option ${index + 1}`;
    };

    const ensureDidNotIssueOption = (options, sectionId) => {
        const hasDidNotIssue = options.some((option) => /did\s*not\s*issue/i.test(String(option?.label || '')));
        if (hasDidNotIssue) {
            return options;
        }
        return [
            ...options,
            {
                id: `${sectionId}-did-not-issue`,
                label: 'Did not issue',
                raw: { is_did_not_issue: true }
            }
        ];
    };

    const normalizeGiftSections = (giftPayload) => {
        const giftList = normalizeToArray(giftPayload);
        if (!giftList.length) {
            return [];
        }

        const sectionBasedPayload = giftList.some((giftSection) =>
            Array.isArray(giftSection?.options) ||
            Array.isArray(giftSection?.gifts) ||
            Array.isArray(giftSection?.items) ||
            Array.isArray(giftSection?.gift_options)
        );

        if (sectionBasedPayload) {
            return giftList.map((giftSection, sectionIndex) => {
                const sectionId = giftSection?.id ?? giftSection?.gift_category_id ?? giftSection?.category_id ?? `gift-section-${sectionIndex + 1}`;
                const sectionOptionsRaw = giftSection?.options || giftSection?.gifts || giftSection?.items || giftSection?.gift_options || [];
                const sectionOptions = sectionOptionsRaw.map((giftOption, optionIndex) => ({
                    id: giftOption?.id ?? giftOption?.gift_id ?? giftOption?.voucher_id ?? `${sectionId}-option-${optionIndex + 1}`,
                    label: getGiftOptionLabel(giftOption, optionIndex),
                    raw: giftOption
                }));
                return {
                    id: sectionId,
                    label: getGiftSectionLabel(giftSection, sectionIndex),
                    options: ensureDidNotIssueOption(sectionOptions, sectionId)
                };
            }).filter((giftSection) => giftSection.options.length > 0);
        }

        const canGroupByCategory = giftList.every((giftItem) => {
            const categoryLabel = giftItem?.category_name || giftItem?.gift_category_name || giftItem?.category_title || giftItem?.gift_category?.name || giftItem?.gift_category?.title;
            return Boolean(categoryLabel);
        });

        if (canGroupByCategory) {
            const groupedByCategory = giftList.reduce((accumulator, giftItem) => {
                const sectionLabel = giftItem?.category_name || giftItem?.gift_category_name || giftItem?.category_title || giftItem?.gift_category?.name || giftItem?.gift_category?.title;
                const sectionId = giftItem?.category_id || giftItem?.gift_category_id || giftItem?.gift_category?.id || sectionLabel;
                if (!accumulator[sectionId]) {
                    accumulator[sectionId] = {
                        id: sectionId,
                        label: sectionLabel,
                        options: []
                    };
                }
                accumulator[sectionId].options.push({
                    id: giftItem?.id ?? giftItem?.gift_id ?? giftItem?.voucher_id ?? `${sectionId}-option-${accumulator[sectionId].options.length + 1}`,
                    label: getGiftOptionLabel(giftItem, accumulator[sectionId].options.length),
                    raw: giftItem
                });
                return accumulator;
            }, {});

            return Object.values(groupedByCategory)
                .map((giftSection) => ({
                    ...giftSection,
                    options: ensureDidNotIssueOption(giftSection.options, giftSection.id)
                }))
                .filter((giftSection) => giftSection.options.length > 0);
        }

        return [
            {
                id: 'gift-section-1',
                label: 'Gift',
                options: ensureDidNotIssueOption(giftList.map((giftOption, optionIndex) => ({
                    id: giftOption?.id ?? giftOption?.gift_id ?? giftOption?.voucher_id ?? `gift-option-${optionIndex + 1}`,
                    label: getGiftOptionLabel(giftOption, optionIndex),
                    raw: giftOption
                })), 'gift-section-1')
            }
        ];
    };

    const loadGiftCategories = async (membershipPackageId) => {
        if (!membershipPackageId) {
            setGiftSections([]);
            setSelectedGiftOptionBySection({});
            setVoucherSerialBySection({});
            setGiftRemarksBySection({});
            return;
        }
        setIsLoadingGiftCategories(true);
        setPurchaseMembershipError('');
        try {
            const giftCategoriesResponse = await getGiftCategoriesByMembershipPackage(membershipPackageId);
            const normalizedGiftSections = normalizeGiftSections(giftCategoriesResponse);
            const defaultGiftSelection = normalizedGiftSections.reduce((selectionMap, giftSection) => {
                if (giftSection.options.length > 0) {
                    selectionMap[giftSection.id] = giftSection.options[0].id;
                }
                return selectionMap;
            }, {});
            setGiftSections(normalizedGiftSections);
            setSelectedGiftOptionBySection(defaultGiftSelection);
            setVoucherSerialBySection({});
            setGiftRemarksBySection({});
        } catch (giftCategoriesError) {
            setGiftSections([]);
            setSelectedGiftOptionBySection({});
            setVoucherSerialBySection({});
            setGiftRemarksBySection({});
            setPurchaseMembershipError('Unable to load gift options. Please try again.');
        } finally {
            setIsLoadingGiftCategories(false);
        }
    };

    const preserveExternalCheckoutTotalsForMembershipFlow = () => {
        if (!membershipFlowOnly) return;
        preservedExternalServiceTotalRef.current =
            typeof serviceTotal === 'number' && !Number.isNaN(serviceTotal)
                ? serviceTotal
                : Number(serviceTotal) || 0;
        preservedExternalAmountDueRef.current = amountDue ?? null;
        preservedExternalAmountToChargeRef.current = amountToCharge ?? null;
    };

    const openMembershipModal = async (flowType = 'purchase') => {
        // Check for existing open sale first (fast — optimized to 1 API call).
        // If found, open the sale sidebar directly and skip the purchase modal.
        const openedExistingSale = await openExistingMembershipSaleIfPresent();
        if (openedExistingSale) return;

        // No open sale — open the purchase modal and load packages.
        preserveExternalCheckoutTotalsForMembershipFlow();
        setPurchaseMembershipFlowType(flowType);
        setIsPurchaseClientDetailsOpen(false);
        setPurchaseMembershipStep('details');
        setPurchaseBookingDataFromAPI({ booking_id: null, deposit: 0 });
        setPurchaseFollowupMembershipId(null);
        setIsLoadingMembershipPackages(true);
        setIsPurchaseMembershipModalOpen(true);
        setPurchaseMembershipError('');
        setInsuranceCertificateNumber('');
        setVoucherSerialBySection({});
        setGiftRemarksBySection({});
        try {
            const membershipPackagesResponse = await getMembershipPackages();
            const membershipPackageList = normalizeMembershipPackages(membershipPackagesResponse);
            setMembershipPackages(membershipPackageList);
            if (membershipPackageList.length === 0) {
                setSelectedMembershipPackageId(null);
                setGiftSections([]);
                setSelectedGiftOptionBySection({});
                setPurchaseMembershipError('No member package found.');
                return;
            }
            const firstMembershipPackageId = getMembershipPackageId(membershipPackageList[0], 0);
            setSelectedMembershipPackageId(firstMembershipPackageId);
            await loadGiftCategories(firstMembershipPackageId);
        } catch (membershipPackagesError) {
            setMembershipPackages([]);
            setSelectedMembershipPackageId(null);
            setGiftSections([]);
            setSelectedGiftOptionBySection({});
            setPurchaseMembershipError('Unable to load member packages. Please try again.');
        } finally {
            setIsLoadingMembershipPackages(false);
        }
    };

    const handleOpenPurchaseMembershipModal = async () => {
        await openMembershipModal('purchase');
    };

    const handleOpenTopupCreditsModal = async () => {
        await openMembershipModal('topup');
    };

    const handleContinueMembershipTopupFromPreview = async () => {
        setIsMembershipTopupPreviewOpen(false);
        setMembershipTopupPreviewPanelId(null);
        setMembershipTopupPreviewSaleDisplay(null);
        setMembershipTopupPreviewDetail(null);
        setMembershipTopupPreviewNote('');
        await openMembershipModal('topup');
    };

    const restorePreservedExternalCheckoutTotals = () => {
        if (preservedExternalServiceTotalRef.current != null) {
            setServiceTotal(preservedExternalServiceTotalRef.current);
            preservedExternalServiceTotalRef.current = null;
        }
        if (preservedExternalAmountDueRef.current != null) {
            setAmountDue(preservedExternalAmountDueRef.current);
            preservedExternalAmountDueRef.current = null;
        }
        if (preservedExternalAmountToChargeRef.current != null) {
            setAmountToCharge(preservedExternalAmountToChargeRef.current);
            preservedExternalAmountToChargeRef.current = null;
        }
    };

    const restorePreservedExternalServiceTotal = () => {
        restorePreservedExternalCheckoutTotals();
    };

    const handleClosePurchaseMembershipModal = () => {
        if (membershipFlowOnly) {
            hasAutoOpenedMembershipFlowRef.current = false;
            restorePreservedExternalServiceTotal();
            onClose?.();
            return;
        }
        setIsPurchaseMembershipModalOpen(false);
        setPurchaseMembershipError('');
        setPurchaseMembershipStep('details');
        setPurchaseBookingDataFromAPI({ booking_id: null, deposit: 0 });
        setPurchaseFollowupMembershipId(null);
    };

    const handleMembershipPackageChange = async (membershipPackageId) => {
        setSelectedMembershipPackageId(membershipPackageId);
        await loadGiftCategories(membershipPackageId);
    };

    const handleGoToPurchasePayment = () => {
        const selectedPackage = membershipPackages.find((membershipPackage, packageIndex) => {
            const membershipPackageId = getMembershipPackageId(membershipPackage, packageIndex);
            return String(membershipPackageId) === String(selectedMembershipPackageId);
        });
        const membershipAmount = getMembershipPackageAmount(selectedPackage);
        if (membershipFlowOnly) {
            preserveExternalCheckoutTotalsForMembershipFlow();
        } else {
            preservedExternalServiceTotalRef.current = null;
            preservedExternalAmountDueRef.current = null;
            preservedExternalAmountToChargeRef.current = null;
        }
        setServiceTotal(membershipAmount);
        setPurchaseMembershipStep('payment');
    };

    const formatPurchaseClientDobForApi = () => {
        const raw =
            customerDetails?.dob ??
            customer?.dob ??
            customerDetails?.date_of_birth ??
            customer?.date_of_birth;
        if (raw == null || raw === '') return null;
        if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
            return raw.slice(0, 10);
        }
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return null;
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const handlePurchaseClientSaveUpdate = async () => {
        const customerId = customer?.id || customerDetails?.id;
        if (!customerId) {
            toast.error('Client not found.', { containerId: 'customerDetailsModal' });
            return;
        }
        if (!purchaseClientForm.firstName.trim()) {
            toast.error('First name is required.', { containerId: 'customerDetailsModal' });
            return;
        }
        const emailTrimmed = purchaseClientForm.email.trim();
        if (emailTrimmed && !/\S+@\S+\.\S+/.test(emailTrimmed)) {
            toast.error('Invalid email format.', { containerId: 'customerDetailsModal' });
            return;
        }

        const nricValidation = validateNricOrPassport(purchaseClientForm.nric, { required: false });
        if (!nricValidation.valid) {
            setPurchaseClientNricError(nricValidation.error);
            toast.error(nricValidation.error, { containerId: 'customerDetailsModal' });
            return;
        }
        setPurchaseClientNricError('');

        setIsSavingPurchaseClient(true);
        try {
            const newsletterRaw =
                customerDetails?.newsletter ??
                customerDetails?.marketing ??
                customer?.newsletter ??
                customer?.marketing ??
                0;
            const newsletter =
                newsletterRaw === true || newsletterRaw === 1 || newsletterRaw === '1' ? 1 : 0;

            const genderVal = customerDetails?.gender ?? customer?.gender ?? '';
            const payload = {
                contact_number: customerDetails?.contact_number || customer?.contact_number || '',
                country_id: customerDetails?.country_id ?? customer?.country_id ?? null,
                dob: formatPurchaseClientDobForApi(),
                email: emailTrimmed || null,
                gender: genderVal === '' ? null : genderVal,
                last_name: purchaseClientForm.lastName.trim(),
                name: purchaseClientForm.firstName.trim(),
                newsletter,
                status: customerDetails?.status ?? customer?.status ?? 1
            };

            const nricTrimmed = sanitizeNricInput(purchaseClientForm.nric);
            if (nricTrimmed) {
                payload.passport_number = nricTrimmed;
            }

            const response = await updateCustomerData(customerId, payload);
            const ok = Boolean(response?.data?.success ?? response?.success);

            if (ok) {
                const updatedCustomerData = {
                    ...(customerDetails || customer),
                    id: customerId,
                    name: payload.name,
                    lastname: payload.last_name,
                    email: payload.email,
                    contact_number: payload.contact_number,
                    country_id: payload.country_id,
                    gender: payload.gender,
                    dob: payload.dob,
                    date_of_birth: payload.dob,
                    marketing: newsletter,
                    newsletter,
                    status: payload.status
                };
                if (nricTrimmed) {
                    updatedCustomerData.passport_number = nricTrimmed;
                    updatedCustomerData.nric = nricTrimmed;
                }
                setCustomerDetails(updatedCustomerData);
                window.dispatchEvent(
                    new CustomEvent('customerUpdated', { detail: { customer: updatedCustomerData } })
                );
                toast.success('Profile updated successfully', { containerId: 'customerDetailsModal' });
            } else {
                const apiErrors = response?.errors || response?.data?.errors || {};
                const msg =
                    response?.message ||
                    response?.data?.message ||
                    (apiErrors.email && apiErrors.email[0]) ||
                    'Failed to update client. Please try again.';
                toast.error(typeof msg === 'string' ? msg : 'Failed to update client.', {
                    containerId: 'customerDetailsModal'
                });
            }
        } catch (error) {
            console.error('Purchase client save error:', error);
            toast.error('Error updating client.', { containerId: 'customerDetailsModal' });
        } finally {
            setIsSavingPurchaseClient(false);
        }
    };

    const formatYmdFromDate = (date) => {
        if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return null;
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    const parseYmdToDate = (ymd) => {
        if (!ymd || typeof ymd !== 'string') return null;
        const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (!m) return null;
        const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        return Number.isNaN(dt.getTime()) ? null : dt;
    };

    const formatPaymentMethodForMembershipApi = (method) => {
        const raw = String(method || 'CASH').trim();
        if (!raw) return 'Cash';
        const upper = raw.toUpperCase();
        const map = {
            CASH: 'Cash',
            CASHLESS: 'Cashless',
            VISA: 'Visa',
            MASTERCARD: 'Mastercard',
            AMEX: 'Amex',
            PAYNOW: 'Paynow',
            'WECHAT PAY': 'Wechat Pay',
            'ALIPAY+': 'Alipay+',
            'BANK TRANSFER': 'Bank Transfer',
            'CASH VOUCHER': 'Cash Voucher',
            VOUCHER: 'Voucher',
            'MBS REWARD': 'Mbs Reward',
            'CAPITAL VOUCHERS': 'Capital Vouchers',
            'CHANGI PAY': 'Changi Pay',
            'PROMO VOUCHER': 'Promo Voucher',
            'MBS CASINO': 'Mbs Casino',
            OTHER: 'Other'
        };
        if (map[upper]) return map[upper];
        return raw
            .split(/\s+/)
            .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
            .join(' ');
    };

    const buildMembershipPurchaseTopupGifts = () => {
        const gifts = [];
        giftSections.forEach((giftSection) => {
            const selectedOptionId = selectedGiftOptionBySection[giftSection.id];
            if (selectedOptionId == null) return;
            const option = giftSection.options.find((o) => String(o.id) === String(selectedOptionId));
            if (!option) return;
            const raw = option.raw || {};
            const isDidNotIssue = raw.is_did_not_issue === true;
            const giftCategoryId = Number(raw.gift_category_id ?? raw.category_id ?? giftSection.id);
            if (!Number.isFinite(giftCategoryId)) return;

            if (isDidNotIssue) {
                const remarks = String(giftRemarksBySection[giftSection.id] ?? '').trim();
                gifts.push({
                    gift_category_id: giftCategoryId,
                    quantity: 1,
                    voucher_serial_number: '',
                    remarks
                });
                return;
            }

            const giftId = Number(raw.gift_id ?? raw.id ?? option.id);
            if (!Number.isFinite(giftId)) return;

            const type = raw.type == null ? null : String(raw.type).trim().toLowerCase();
            const shouldUseSerial = type === 'inhouse' || type === 'third_party';
            const typedSerial = String(voucherSerialBySection[giftSection.id] ?? '').trim();
            const typedRemarks = String(giftRemarksBySection[giftSection.id] ?? '').trim();
            const serial = shouldUseSerial ? (typedSerial || raw.voucher_serial_number || '') : (raw.voucher_serial_number || '');
            const remarks =
                type === null || type === 'others'
                    ? typedRemarks || raw.remarks || raw.gift_name || option.label || ''
                    : raw.remarks || raw.gift_name || option.label || '';

            gifts.push({
                gift_category_id: giftCategoryId,
                gift_id: giftId,
                quantity: 1,
                voucher_serial_number: serial,
                remarks
            });
        });
        return gifts;
    };

    const extractMembershipIdFromPurchaseResponse = (apiResponse) => {
        const root = apiResponse?.data ?? apiResponse;
        const body = root?.data && typeof root.data === 'object' ? root.data : root;
        const txn = body?.membership_transaction ?? root?.membership_transaction;
        const candidates = [
            txn?.membership_id,
            txn?.membership?.id,
            body?.membership?.id,
            root?.membership?.id,
            body?.membership_id,
            txn?.membership_detail?.id,
            body?.membership_detail?.id,
        ];
        for (let index = 0; index < candidates.length; index += 1) {
            const candidate = candidates[index];
            if (candidate == null || candidate === '') continue;
            const normalized = String(candidate).trim().replace(/^#/, '').trim();
            if (normalized) return normalized;
        }
        return null;
    };

    const getMembershipPaymentSuccessMessage = (flowType, apiResponse = null) => {
        const isTopup = String(flowType).toLowerCase() === 'topup';
        if (!isTopup) {
            return 'Membership purchase completed';
        }

        const fallback = 'Membership topped up successfully';
        const root = apiResponse?.data ?? apiResponse;
        const messageCandidates = [
            root?.message,
            root?.data?.message,
            root?.data?.data?.message,
            apiResponse?.message,
            apiResponse?.data?.message,
            apiResponse?.data?.data?.message,
        ];
        for (let index = 0; index < messageCandidates.length; index += 1) {
            const candidate = messageCandidates[index];
            if (candidate == null) continue;
            const normalized = String(candidate).trim();
            if (normalized) return normalized;
        }
        return fallback;
    };

    const handlePurchaseMembershipConfirm = async (paymentData) => {
        try {
            const selectedPackage = membershipPackages.find((membershipPackage, packageIndex) => {
                const membershipPackageId = getMembershipPackageId(membershipPackage, packageIndex);
                return String(membershipPackageId) === String(selectedMembershipPackageId);
            });

            const subtotal = Number(paymentSummary?.subtotal || getMembershipPackageAmount(selectedPackage) || 0);
            const totalAmount = Number(paymentSummary?.total || subtotal || 0);
            const gstAmount = Number(paymentSummary?.gst || 0);
            const paidAmount = parseFloat(paymentData?.amount || totalAmount || 0);
            if (Number.isNaN(paidAmount) || paidAmount <= 0) {
                return { success: false, message: 'Enter a valid payment amount.' };
            }

            const outletId = selectedOutlet || loggedOutletID;
            const normalizedOutletId =
                outletId != null && outletId !== '' ? Number(outletId) : null;

            const customerId = customerDetails?.id || customer?.id || '';

            const remainingAfterPayment = Number(amountDue || totalAmount) - paidAmount;
            const isPaymentComplete = remainingAfterPayment <= 0;

            let startDateStr =
                selectedPackage?.start_date ||
                selectedPackage?.membership_start_date ||
                formatYmdFromDate(new Date());
            if (startDateStr && !/^\d{4}-\d{2}-\d{2}$/.test(String(startDateStr))) {
                const parsed = new Date(startDateStr);
                startDateStr = formatYmdFromDate(parsed) || formatYmdFromDate(new Date());
            }
            if (!startDateStr) startDateStr = formatYmdFromDate(new Date());

            let expiryDateStr =
                selectedPackage?.expiry_date ||
                selectedPackage?.end_date ||
                selectedPackage?.membership_expiry_date ||
                null;
            if (expiryDateStr && !/^\d{4}-\d{2}-\d{2}$/.test(String(expiryDateStr))) {
                const parsedExp = new Date(expiryDateStr);
                expiryDateStr = formatYmdFromDate(parsedExp);
            }
            if (!expiryDateStr) {
                const startD = parseYmdToDate(startDateStr) || new Date();
                const exp = new Date(startD);
                exp.setFullYear(exp.getFullYear() + 1);
                expiryDateStr = formatYmdFromDate(exp);
            }

            const membershipNumber =
                selectedPackage?.membership_number ||
                selectedPackage?.membership_no ||
                selectedPackage?.number ||
                null;

            const remarksParts = [
                purchaseMembershipFlowType === 'topup' ? 'Top-up credits' : 'New membership'
            ];
            if (insuranceCertificateNumber?.trim()) {
                remarksParts.push(`Insurance cert: ${insuranceCertificateNumber.trim()}`);
            }

            const amountValue = Number(Number(subtotal).toFixed(2));

            const payload = {
                mode: 'topup',
                user_id: Number(customerId),
                membership_package_id: Number(selectedMembershipPackageId) || 0,
                purchase_amount: amountValue,
                amount: amountValue,
                gst_amount: Number(Number(gstAmount).toFixed(2)),
                start_date: startDateStr,
                expiry_date: expiryDateStr,
                registration_outlet_id: normalizedOutletId,
                outlet_id: normalizedOutletId,
                status: 1,
                remarks: remarksParts.join(' — '),
                paid_amount: Number(Number(paidAmount).toFixed(2)),
                payment_method: formatPaymentMethodForMembershipApi(paymentData?.method || currentPaymentMethod),
                payment_reference: String(paymentData?.reference || '').trim() || '',
                payment_method_id: Number(paymentData?.payment_method_id || 0),
                amount_currency: 'SGD',
                panel: 'outlet',
                gifts: buildMembershipPurchaseTopupGifts()
            };

            const normalizeMembershipIdForApi = (raw) => {
                if (raw == null || raw === '') return '';
                let s = String(raw).trim();
                if (s.startsWith('#')) s = s.slice(1).trim();
                return s;
            };
            // Always prefer membership_detail.id for purchase-topup endpoint.
            let topupMembershipId = normalizeMembershipIdForApi(membershipDetail?.id);
            if (!topupMembershipId) {
                topupMembershipId = normalizeMembershipIdForApi(membershipDetail?.membership_id);
            }
            if (topupMembershipId) {
                const topupMembershipNumericId = Number(topupMembershipId);
                if (!Number.isFinite(topupMembershipNumericId)) {
                    return { success: false, message: 'Membership ID is invalid for top-up.' };
                }
                payload.membership_id = topupMembershipNumericId;
            } else if (purchaseMembershipFlowType === 'topup' || hasMembershipDetail) {
                return { success: false, message: 'Membership ID is required for top-up.' };
            }

            if (membershipNumber) {
                payload.membership_number = membershipNumber;
            }

            const rawExistingTxnId = purchaseBookingDataFromAPI?.booking_id;
            const existingMembershipTransactionId =
                rawExistingTxnId != null && String(rawExistingTxnId).trim() !== ''
                    ? String(rawExistingTxnId).replace(/^#/, '').trim()
                    : '';

            let response;
            if (existingMembershipTransactionId) {
                const txnIdNumeric = Number(existingMembershipTransactionId);
                if (!Number.isFinite(txnIdNumeric)) {
                    return { success: false, message: 'Invalid membership transaction for follow-up payment.' };
                }
                let followupMembershipIdStr = topupMembershipId;
                if (!followupMembershipIdStr && purchaseFollowupMembershipId != null && String(purchaseFollowupMembershipId).trim() !== '') {
                    followupMembershipIdStr = normalizeMembershipIdForApi(purchaseFollowupMembershipId);
                }
                const followupMembershipId = Number(followupMembershipIdStr);
                if (!Number.isFinite(followupMembershipId) || followupMembershipId <= 0) {
                    return { success: false, message: 'Membership ID is invalid for follow-up payment.' };
                }
                const paymentMethodId = Number(paymentData?.payment_method_id || 0);
                if (!Number.isFinite(paymentMethodId) || paymentMethodId <= 0) {
                    return { success: false, message: 'Payment method is required.' };
                }
                const followUpPayload = {
                    membership_transaction_id: txnIdNumeric,
                    amount: Number(Number(paidAmount).toFixed(2)),
                    payment_method_id: paymentMethodId,
                    payment_reference: String(paymentData?.reference || '').trim() || '',
                    panel: 'outlet',
                };
                response = await postMembershipTransactionPayment(followupMembershipId, followUpPayload);
            } else {
                response = await postMembershipPurchaseTopup(payload);
            }
            const normalizedPaymentResponse =
                response?.data && typeof response.data === 'object' ? response.data : response;
            const isPaymentSuccess = Boolean(
                normalizedPaymentResponse?.success ?? response?.success
            );

            if (!isPaymentSuccess) {
                // Return the full response (carries top-level `message` + `errors`,
                // e.g. errors.payment_reference). `normalizedPaymentResponse` can be the
                // inner `data: []` payload, which would drop the validation details.
                return response || normalizedPaymentResponse || { success: false, message: 'Payment failed. Please try again.' };
            }

            const extractedMembershipId = extractMembershipIdFromPurchaseResponse(response);
            if (extractedMembershipId) {
                setPurchaseFollowupMembershipId(extractedMembershipId);
            }

            const membershipTransactionId =
                response?.data?.membership_transaction?.id ||
                response?.data?.data?.membership_transaction?.id ||
                null;

            const responseSaleId =
                response?.data?.membership_transaction?.sale_id ||
                response?.data?.data?.membership_transaction?.sale_id ||
                response?.data?.sale_id ||
                response?.data?.data?.sale_id ||
                null;

            let currentBookingId = membershipTransactionId
                ? String(membershipTransactionId)
                : purchaseBookingDataFromAPI?.booking_id || null;

            let resolvedSaleId = responseSaleId ? String(responseSaleId) : (purchaseBookingDataFromAPI?.sale_id || null);

            if (!resolvedSaleId && currentBookingId) {
                try {
                    const txnDetailResponse = await getMembershipTransactionDetail(currentBookingId);
                    const txnDetail = txnDetailResponse?.data?.data ?? txnDetailResponse?.data ?? null;
                    const txn = txnDetail?.membership_transaction || txnDetail || {};
                    const fetchedSaleId = txn?.sale_id;
                    if (fetchedSaleId != null && String(fetchedSaleId).trim() !== '') {
                        resolvedSaleId = String(fetchedSaleId);
                    }
                } catch (saleIdError) {
                    console.error('Failed to fetch sale_id from transaction detail:', saleIdError);
                }
            }

            if (currentBookingId) {
                setPurchaseBookingDataFromAPI((previousValue) => ({
                    ...(previousValue || {}),
                    booking_id: currentBookingId,
                    sale_id: resolvedSaleId || previousValue?.sale_id || null,
                    deposit: 0,
                }));
            }

            // const nextDue = membershipFlowOnly
            //     ? Math.max(0, Number(totalAmount) - paidAmount)
            //     : Math.max(0, Number(amountDue || totalAmount) - paidAmount);

            const nextDue = isPaymentComplete ? 0 : Math.max(0, remainingAfterPayment);
            if (!membershipFlowOnly) {
                setPaymentHistory([
                    ...(paymentHistory || []),
                    {
                        method: paymentData?.method || currentPaymentMethod || '',
                        amount: paidAmount,
                        id: currentBookingId || null
                    }
                ]);
                setAmountDue(nextDue.toFixed(2));
            }

            if (nextDue > 0) {
                let partialOpenMembershipSale = null;
                if (resolvedSaleId) {
                    partialOpenMembershipSale = {
                        id: resolvedSaleId,
                        sale_id: resolvedSaleId,
                        booking_id: currentBookingId || null,
                        transaction_id: currentBookingId || null,
                        type: 'membership',
                        is_payment_pending: true,
                        status: 'pending payment',
                        payment_status: 'pending payment',
                    };
                    setOpenMembershipSale(partialOpenMembershipSale);
                }
                onMembershipPaymentSuccess?.(
                    membershipFlowOnly ? { restoreCheckoutTotals: false } : undefined
                );
                if (customerId) {
                    try {
                        // Partial payment creates/updates membership_detail even though credit posts
                        // only on full payment, so refresh it now. Otherwise the tab keeps rendering
                        // the pre-payment "No membership found" state until the modal is reopened.
                        await fetchCustomerDetails(customerId, { silent: true });
                        if (!partialOpenMembershipSale) {
                            await refreshMembershipPaymentState(customerId, {
                                fallbackOpenSale: partialOpenMembershipSale,
                            });
                        }
                        void refreshCustomerListAfterMembershipPayment();
                    } catch (refreshError) {
                        console.error('Failed to refresh customer after partial membership payment:', refreshError);
                    }
                }
                setPurchaseMembershipStep('payment');
                return { success: true, partial: true };
            }

            const successToastMessage = getMembershipPaymentSuccessMessage(
                purchaseMembershipFlowType,
                response
            );
            const customerRecordForPassword =
                customerDetails && Object.keys(customerDetails).length > 0
                    ? customerDetails
                    : customer;
            const shouldOpenSetPasswordPopup =
                !skipSetMembershipPasswordAfterPayment &&
                shouldPromptSetMembershipPasswordAfterPurchase(
                    customerRecordForPassword,
                    membershipDetail
                );

            if (membershipFlowOnly) {
                restorePreservedExternalServiceTotal();
            }
            setIsPurchaseMembershipModalOpen(false);
            setPurchaseMembershipStep('details');

            if (shouldOpenSetPasswordPopup) {
                pendingPurchaseFinalizeRef.current = {
                    flowType: purchaseMembershipFlowType,
                    toastMessage: successToastMessage,
                };
                const opened = openSetMembershipPasswordPopup(extractedMembershipId, {
                    fromFirstPurchase: true,
                });
                if (!opened) {
                    pendingPurchaseFinalizeRef.current = null;
                    await finalizeMembershipPurchaseAfterPayment(successToastMessage, {
                        showToast: false,
                    });
                }
            } else {
                await finalizeMembershipPurchaseAfterPayment(successToastMessage);
            }
            return { success: true, partial: false, openSetPassword: shouldOpenSetPasswordPopup };
        } catch (error) {
            console.error('Membership payment error:', error);
            return error?.response?.data || { success: false, message: 'Payment failed. Please try again.' };
        }
    };






    const finalizeMembershipPurchaseAfterPayment = async (
        toastMessageOverride = null,
        { showToast = true, flowType: flowTypeOverride = null } = {}
    ) => {
        const pending = pendingPurchaseFinalizeRef.current;
        const flowType =
            flowTypeOverride ?? pending?.flowType ?? purchaseMembershipFlowType;
        const isTopup = String(flowType).toLowerCase() === 'topup';
        const toastMessage = isTopup
            ? (toastMessageOverride ??
              pending?.toastMessage ??
              'Membership topped up successfully')
            : (toastMessageOverride ?? 'Membership purchase completed');
        pendingPurchaseFinalizeRef.current = null;

        if (showToast && toastMessage) {
            showMembershipPaymentSuccessToast(toastMessage, { position: 'bottom-center' });
        }

        const customerId = customerDetails?.id || customer?.id;
        if (customerId) {
            try {
                // Sale is now fully paid → no open sale. Clear the banner immediately (no loader,
                // no open-sale refetch) and refresh the balance quietly.
                setOpenMembershipSale(null);
                const refreshedCustomer = await fetchCustomerDetails(customerId, { silent: true });
                if (refreshedCustomer) {
                    window.dispatchEvent(new CustomEvent('customerUpdated', {
                        detail: { customer: refreshedCustomer },
                    }));
                }
                if (!membershipFlowOnly) {
                    try { await fetchCustomerTimeline(customerId, 1); } catch {}
                }
                await refreshCustomerListAfterMembershipPayment();
            } catch (refreshError) {
                console.error('Failed to refresh customer after membership purchase:', refreshError);
            }
        }
        if (membershipFlowOnly) {
            restorePreservedExternalCheckoutTotals();
        }
        onMembershipPaymentSuccess?.(
            membershipFlowOnly
                ? {
                      stayOnCheckoutStep: true,
                      flowType: purchaseMembershipFlowType,
                  }
                : undefined
        );
        if (membershipFlowOnly) {
            onClose?.();
        }
    };

    const resolveMembershipIdForSetPassword = (membershipIdOverride = null) => {
        const raw =
            membershipIdOverride ??
            membershipDetail?.id ??
            purchaseFollowupMembershipId;
        const idNum = Number(raw);
        return Number.isFinite(idNum) && idNum > 0 ? idNum : null;
    };

    const showPendingPurchaseSuccessToast = () => {
        const pendingMessage = pendingPurchaseFinalizeRef.current?.toastMessage;
        if (!pendingMessage) return;
        showMembershipPaymentSuccessToast(pendingMessage, { position: 'bottom-center' });
    };

    const openSetMembershipPasswordPopup = (membershipIdOverride = null, options = {}) => {
        const { fromFirstPurchase = false } = options;
        const idNum = resolveMembershipIdForSetPassword(membershipIdOverride);
        if (!idNum) {
            toast.error('Membership ID not found. Please try again.', {
                containerId: 'customerDetailsModal',
                position: 'bottom-center',
            });
            return false;
        }
        setSetPasswordPopupFromFirstPurchase(fromFirstPurchase);
        setMembershipMenuSetPopupId(idNum);
        return true;
    };

    const handleSkipSetMembershipPasswordPopup = async () => {
        setMembershipMenuSetPopupId(null);
        setSetPasswordPopupFromFirstPurchase(false);
        if (pendingPurchaseFinalizeRef.current) {
            showPendingPurchaseSuccessToast();
            await finalizeMembershipPurchaseAfterPayment(null, { showToast: false });
        }
    };

    const handleCloseSetMembershipPasswordPopup = async () => {
        setMembershipMenuSetPopupId(null);
        setSetPasswordPopupFromFirstPurchase(false);
        if (pendingPurchaseFinalizeRef.current) {
            await finalizeMembershipPurchaseAfterPayment(null, { showToast: false });
        }
    };

    const handleOpenMembershipPasswordResetFromMenu = () => {
        const membershipId = membershipDetail?.id;
        if (membershipId == null || membershipId === '') {
            toast.error('Membership ID not found. Please try again.', {
                containerId: 'customerDetailsModal',
                position: 'bottom-center',
            });
            return;
        }
        const idNum = Number(membershipId);
        if (!Number.isFinite(idNum) || idNum <= 0) {
            toast.error('Membership ID not found. Please try again.', {
                containerId: 'customerDetailsModal',
                position: 'bottom-center',
            });
            return;
        }
        setIsMembershipMenuOpen(false);
        setMembershipMenuResetPopupId(idNum);
    };

    // if (loading && !membershipFlowOnly) {
    //     return (
    //         <div className={sidebarOverlayClassName} onClick={handleSidebarOverlayClick}>
    //             {createPortal(<ToastContainer position="top-right" autoClose={3000} />, document.body)}
    //             <div className={`${sidebarContentClassName} prevent-outside-click`} onClick={(e) => e.stopPropagation()}>
    //                 <div className={styles.sidebarHeader}>
    //                     <div className={styles.headerLeft}>
    //                         <span className={styles.closeText} onClick={onClose}>× Client Details</span>
    //                     </div>
    //                     <div className={styles.headerRight}>
    //                         <button className={styles.moreButton}>⋮</button>
    //                     </div>
    //                 </div>
    //                 <div className={styles.sidebarBody}>
    //                     <div className={styles.leftPanel}>
    //                         <div className={styles.loaderContainer}>
    //                             <Loader />
    //                             <div className={styles.loaderText}>Loading customer details...</div>
    //                         </div>
    //                     </div>
    //                     <div className={styles.rightPanel}>
    //                         <div className={styles.loaderContainer}>
    //                             <Loader />
    //                             <div className={styles.loaderText}>Loading timeline...</div>
    //                         </div>
    //                     </div>
    //                 </div>
    //             </div>
    //         </div>
    //     );
    // }

    if (error && !membershipFlowOnly) {
        return (
            <div className={sidebarOverlayClassName}>
                {createPortal(<ToastContainer position="top-right" autoClose={3000} />, document.body)}
                <div className={sidebarContentClassName}>
                    <div className={styles.sidebarHeader}>
                        <div className={styles.headerLeft}>
                            <span className={styles.closeText} onClick={onClose}>× Client Details</span>
                        </div>
                        <div className={styles.headerRight}>
                            <button className={styles.moreButton}>⋮</button>
                        </div>
                    </div>
                    <div className={styles.sidebarBody}>
                        <div className={styles.leftPanel}>
                            <div style={{ textAlign: 'center', padding: '40px 20px', color: 'red' }}>
                                {error}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const normalizedPathname = String(location?.pathname || '').toLowerCase();
    const isHomeRoute = normalizedPathname === '/' || normalizedPathname === '';
    const isSalesRoute = normalizedPathname.includes('/sales');
    const isClientsRoute = normalizedPathname.includes('/clients');
    const isSalesOrClientsRoute = isSalesRoute || isClientsRoute;

    return (
        <div className={sidebarOverlayClassName}>
            <div
                ref={sidebarContentRef}
                className={sidebarContentClassName}
            >
                {/* Header */}
                <div className={styles.sidebarHeader}>
                    <div className={styles.headerLeft}>
                        {/* Hamburger menu for mobile */}
                        <button 
                            className={styles.hamburgerButton}
                            onClick={() => setIsLeftPanelOpen(!isLeftPanelOpen)}
                            aria-label="Toggle client details"
                        >
                            <p className={styles.navbarToggleIcon}>
                                <span></span>
                                <span></span>
                                <span></span>
                            </p>
                        </button>
                        <span className={styles.closeText} onClick={onClose}>× Client Details</span>
                    </div>
                    <div className={styles.headerRight}>
                        {/* <div ref={menuRef} style={{ position: 'relative' }}>
                            <button 
                                className={styles.moreButton}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsMenuOpen(!isMenuOpen);
                                }}
                            >
                                <img src={menuIcon} alt="Menu" style={{ width: '20px', height: '20px' }} />
                            </button>
                            {isMenuOpen && (
                                <div className={styles.dropdownMenu} onClick={(e) => e.stopPropagation()}>
                                    <button
                                        className={styles.dropdownItem}
                                        onClick={() => {
                                            setIsMenuOpen(false);
                                            setShowDeleteConfirm(true);
                                        }}
                                    >
                                        Delete Client
                                    </button>
                                </div>
                            )}
                        </div> */}
                    </div>
                </div>

                <div className={styles.sidebarBody}>
                    {/* Mobile overlay for left panel */}
                    {isLeftPanelOpen && (
                        <div 
                            className={styles.mobileOverlay}
                            onClick={() => setIsLeftPanelOpen(false)}
                        />
                    )}
                    
                    {/* Left Panel - Customer Information */}
                    <div className={`${styles.leftPanel} ${isLeftPanelOpen ? styles.leftPanelOpen : ''}`}>
                        <div className={styles.leftPanelHeader}>
                            <div></div> {/* Empty div for flex spacing */}
                            {!isNotesMode && !isEditMode && (
                            <button 
                                className={styles.editButton}
                                onClick={() => setIsEditMode(true)}
                                title="Edit customer"
                            >
                                <img src={editIcon} alt="Edit" style={{ width: '16px', height: '16px' }} />
                            </button>
                            )}
                        </div>
                        {!isEditMode && !isNotesMode ? (
                        <div className={styles.leftPanelContent}>
                        <div className={styles.customerProfile}>
                            <div 
                                className={styles.profileImage}
                                style={{ backgroundColor: getConsistentColor(customer?.id || customerDetails?.id, `${customerDetails?.name || customer?.name || ''} ${getLastName()}`.trim()) }}
                            >
                                        {getCustomInitials(customerDetails?.name || customer?.name || '', getLastName())}
                                        {hasMembershipBadge && (
                                            <div className={styles.memberIcon}>
                                                <PrimiumMemberIcon size={16} />
                                            </div>
                                        )}
                            </div>
                            <div className={styles.customerName}>
                                        {customerDetails?.contact_number || customer?.contact_number || 'N/A'}
                            </div>
                            <div className={styles.customerName}>
                                        {customerDetails?.name || customer?.name} {getLastName()}
                                        {(
                                            customerDetails?.is_blocked === true ||
                                            Number(customerDetails?.is_blocked) === 1 ||
                                            customer?.is_blocked === true ||
                                            Number(customer?.is_blocked) === 1
                                        ) ? ' (Blocked)' : ''}
                            </div>
                        </div>

                        <div className={styles.customerDetails}>
                            <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Phone:</span>
                                        <span className={styles.detailValue}>{customerDetails?.contact_number || customer?.contact_number || 'N/A'}</span>
                            </div>
                            <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Email:</span>
                                        <span className={styles.detailValue}>{customerDetails?.email || customer?.email || 'N/A'}</span>
                            </div>
                            <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Last appointment date:</span>
                                <span className={styles.detailValue}>
                                    {getLastAppointmentDate()}
                                </span>
                            </div>
                        </div>
                                <div className={styles.customerDetailsSeparator}></div>

                                <div className={`${styles.notesSection} ${existingNotes ? styles.hasNotes : ''}`}>
                                    <div className={styles.notesHeader}>
                            <h3>Notes:</h3>
                                        {existingNotes && (
                                            <button
                                                className={styles.editNotesButton}
                                                onClick={handleEditNotes}
                                                title="Edit notes"
                                            >
                                                <img src={editIcon} alt="Edit" style={{ width: '16px', height: '16px' }} />
                                            </button>
                                        )}
                                    </div>
                                    {existingNotes ? (
                                        <div className={styles.existingNotes}>
                                            {existingNotes}
                                        </div>
                                    ) : (
                                        <button className={styles.addNotesButton} onClick={handleAddNotes}>
                                Add Client Notes
                                        </button>
                                    )}
                                </div>

                                {/* Client Created Information at Bottom */}
                                {customerDetails?.created_at && (
                                    <div className={styles.clientCreatedLeft}>
                                        <div className={styles.clientCreatedTitle}>Client created</div>
                                        <div className={styles.clientCreatedLeftInfo}>
                                            <div className={styles.clientCreatedDateTime}>
                                                {formatDate(customerDetails.created_at)},
                                            </div>
                                            <div className={styles.clientCreatedByLeft}>
                                                Created by <b>{customerDetails.created_by_text}</b>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : isNotesMode ? (
                            <>
                                <div className={styles.notesForm}>
                                    <h4>Add Note</h4>
                                    <textarea
                                        ref={notesTextareaRef}
                                        autoFocus
                                        value={notesText}
                                        onChange={(e) => setNotesText(e.target.value)}
                                        placeholder="Enter your note here..."
                                        className={styles.notesTextarea}
                                        disabled={isSavingNotes}
                                    />
                                </div>

                                <div className={styles.formFooter}>
                                    <BorderedButton
                                        text="Cancel"
                                        onClick={handleNotesCancel}
                                        className={`${styles.footerBtn} ${styles.footerBtnCancel}`}
                                        disabled={isSavingNotes}
                                    />
                                    <PrimaryButton
                                        text={isSavingNotes ? 'Saving...' : 'Save'}
                                        onClick={handleNotesSave}
                                        className={`${styles.footerBtn} ${styles.footerBtnPrimary}`}
                                        disabled={isSavingNotes}
                                    />
                        </div>
                            </>
                        ) : (
                            <>
                                <div className={styles.customerProfile}>
                                    <div className={styles.profileImage} style={{ backgroundColor: avatarColor }}>
                                        {getCustomInitials(editForm.firstName, editForm.lastName)}
                                        {hasMembershipBadge && (
                                            <div className={styles.memberIcon}>
                                                <PrimiumMemberIcon size={16} />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.formContent}>
                                    <div className={styles.formGroup}>
                                        <input
                                            type="text"
                                            value={editForm.firstName}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setEditForm({ ...editForm, firstName: value });
                                                validateEditField('firstName', value);
                                                updateAvatar(value, editForm.lastName);
                                            }}
                                            placeholder="First Name"
                                            className={`${rightStyles.iconFormControl} ${editErrors.firstName ? styles.errorInput : ''}`}
                                            style={{ paddingLeft: '0px', paddingRight: '0px' }}
                                            disabled={isSaving}
                                            autoFocus
                                        />
                                        {editErrors.firstName && (
                                            <div className={`${rightStyles.errorMessage} ${styles.addClientError}`}>
                                                {editErrors.firstName}
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.formGroup}>
                                        <input
                                            type="text"
                                            value={editForm.lastName}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                setEditForm({ ...editForm, lastName: value });
                                                validateEditField('lastName', value);
                                                updateAvatar(editForm.firstName, value);
                                            }}
                                            placeholder="Last Name"
                                            className={`${rightStyles.iconFormControl} ${editErrors.lastName ? styles.errorInput : ''}`}
                                            style={{ paddingLeft: '0px', paddingRight: '0px' }}
                                            disabled={isSaving}
                                        />
                                        {editErrors.lastName && (
                                            <div className={`${rightStyles.errorMessage} ${styles.addClientError}`}>
                                                {editErrors.lastName}
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.formGroup}>
                                        <input
                                            type="email"
                                            value={editForm.email}
                                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                            placeholder="Email"
                                            className={`${rightStyles.iconFormControl} ${editErrors.email ? styles.errorInput : ''}`}
                                            style={{ paddingLeft: '0px', paddingRight: '0px' }}
                                            disabled={isSaving}
                                        />
                                        {editErrors.email && (
                                            <div className={`${rightStyles.errorMessage} ${styles.addClientError}`}>
                                                {editErrors.email}
                                            </div>
                                        )}
                                    </div>
                                    <div className={`${styles.formGroup} ${styles.phoneInputFormGroup}`}>
                                        <div 
                                            className={`${rightStyles.phoneInputWrapper} ${rightStyles.phoneInputWrapperCustom} hide-default-country-selector`}
                                        >
                                            <CountrySelectorWithSearch
                                                value={selectedCountry}
                                                onChange={(country) => {
                                                    // country is now an object with {id, code, name, dial_code, image}
                                                    setSelectedCountry(country);
                                                    const digitsOnly = displayPhoneNumber.replace(/\D/g, '');
                                                    if (digitsOnly && country?.dial_code) {
                                                        const newNumber = `+${country.dial_code}${digitsOnly}`;
                                                        setEditForm(prev => ({
                                                            ...prev,
                                                            phone: newNumber,
                                                            country_id: country.id || null,
                                                        }));
                                                        validateEditField('phone', newNumber);
                                                    } else {
                                                        setEditForm(prev => ({
                                                            ...prev,
                                                            country_id: country?.id || null,
                                                        }));
                                                    }
                                                }}
                                                disabled={isSaving}
                                            />
                                            <div className={rightStyles.phoneInputOnly}>
                                                <input
                                                    type="tel"
                                                    value={displayPhoneNumber}
                                                    onKeyDown={(e) => {
                                                        if (e.key === '+' || e.key === '=') {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onPaste={(e) => {
                                                        e.preventDefault();
                                                        const pastedText = e.clipboardData.getData('text');

                                                        // Paste with +: detect country and show national digits
                                                        if (pastedText.startsWith('+')) {
                                                            try {
                                                                const phoneNumber = parsePhoneNumber(pastedText);
                                                                if (phoneNumber && phoneNumber.country) {
                                                                    const countryObject = findCountryByCodeSync(phoneNumber.country);
                                                                    if (countryObject) {
                                                                        setSelectedCountry(countryObject);
                                                                        setEditForm(prev => ({
                                                                            ...prev,
                                                                            phone: pastedText,
                                                                            country_id: countryObject.id || null,
                                                                        }));
                                                                    } else {
                                                                        setSelectedCountry(phoneNumber.country);
                                                                        setEditForm(prev => ({ ...prev, phone: pastedText, country_id: null }));
                                                                    }
                                                                    setDisplayPhoneNumber(getLocalPhoneDigits(pastedText, phoneNumber));
                                                                    validateEditField('phone', pastedText);
                                                                    return;
                                                                }
                                                            } catch (err) {
                                                                // If parsing fails, continue with digits-only flow
                                                            }
                                                        }

                                                        // Non-international paste: digits only with selected country
                                                        const digitsOnly = pastedText.replace(/\D/g, '');
                                                        if (digitsOnly) {
                                                            const dialCode = selectedCountry && typeof selectedCountry === 'object'
                                                                ? selectedCountry.dial_code
                                                                : getCountryCallingCode(selectedCountry);
                                                            const formattedValue = `+${dialCode}${digitsOnly}`;
                                                            setEditForm(prev => ({ ...prev, phone: formattedValue }));
                                                            setDisplayPhoneNumber(digitsOnly);
                                                            validateEditField('phone', formattedValue);
                                                        }
                                                    }}
                                                    onChange={(e) => {
                                                        // Digits only — no letters, spaces, or +
                                                        const digitsOnly = e.target.value.replace(/\D/g, '');
                                                        setDisplayPhoneNumber(digitsOnly);

                                                        if (!digitsOnly) {
                                                            setEditForm(prev => ({ ...prev, phone: '' }));
                                                            validateEditField('phone', '');
                                                            return;
                                                        }

                                                        const dialCode = selectedCountry && typeof selectedCountry === 'object'
                                                            ? selectedCountry.dial_code
                                                            : getCountryCallingCode(selectedCountry);
                                                        const formattedValue = `+${dialCode}${digitsOnly}`;
                                                        setEditForm(prev => ({ ...prev, phone: formattedValue }));
                                                        validateEditField('phone', formattedValue);
                                                    }}
                                                    placeholder={getMobilePlaceholder(selectedCountry || 'SG')}
                                                    className={`${rightStyles.iconFormControl} ${rightStyles.phoneInputCustom} ${editErrors.phone ? styles.errorInput : ''}`}
                                                    disabled={isSaving}
                                                    autoComplete="tel"
                                                    name="tel"
                                                    style={{ paddingLeft: '0' }}
                                                />
                                            </div>
                                        </div>
                                        {editErrors.phone && (
                                            <div className={`${rightStyles.errorMessage} ${styles.addClientError}`}>
                                                {editErrors.phone}
                                            </div>
                                        )}
                                    </div>

                                    {/* Additional Details Section */}
                                    <div className={styles.formSection}>
                                        <div className={styles.sectionTitle}>Additional Details</div>
                                        <div className={styles.groupInput}>
                                            <CustomRadio
                                                label="Female"
                                                name="gender"
                                                value="female"
                                                checked={editForm.gender === 'female'}
                                                onChange={(e) => {
                                                    setEditForm({ 
                                                        ...editForm, 
                                                        gender: editForm.gender === e.target.value ? '' : e.target.value 
                                                    });
                                                }}
                                                disabled={isSaving}
                                            />
                                            <CustomRadio
                                                label="Male"
                                                name="gender"
                                                value="male"
                                                checked={editForm.gender === 'male'}
                                                onChange={(e) => {
                                                    setEditForm({ 
                                                        ...editForm, 
                                                        gender: editForm.gender === e.target.value ? '' : e.target.value 
                                                    });
                                                }}
                                                disabled={isSaving}
                                            />
                                            <CustomRadio
                                                label="Non-binary"
                                                name="gender"
                                                value="non-binary"
                                                checked={editForm.gender === 'non-binary'}
                                                onChange={(e) => {
                                                    setEditForm({ 
                                                        ...editForm, 
                                                        gender: editForm.gender === e.target.value ? '' : e.target.value 
                                                    });
                                                }}
                                                disabled={isSaving}
                                            />
                                            <CustomRadio
                                                label="Prefer not to say"
                                                name="gender"
                                                value="prefer-not"
                                                checked={editForm.gender === 'prefer-not'}
                                                onChange={(e) => {
                                                    // Toggle behavior: if already selected, unselect
                                                    setEditForm({ 
                                                        ...editForm, 
                                                        gender: editForm.gender === e.target.value ? '' : e.target.value 
                                                    });
                                                }}
                                                disabled={isSaving}
                                            />
                                        </div>

                                        <div className={styles.formGroup}>
                                            <CustomDatePicker
                                                selected={editForm.dateOfBirth}
                                                onChange={(date) => setEditForm({ ...editForm, dateOfBirth: date })}
                                                dateFormat="dd MMM yy"
                                                placeholderText="Date of Birth"
                                                className={`${rightStyles.iconFormControl} ${styles.dateOfBirthInput}`}
                                                disabled={isSaving}
                                                showYearDropdown
                                                showMonthDropdown
                                                dropdownMode="select"
                                                yearDropdownItemNumber={100}
                                                scrollableYearDropdown
                                                maxDate={new Date()}
                                                minDate={new Date(1900, 0, 1)}
                                                openToDate={new Date(new Date().getFullYear() - 25, 0, 1)}
                                            />
                                        </div>
                                    </div>

                                    {/* Messaging Preferences Section */}
                                    <div className={styles.formSection}>
                                        <div className={styles.sectionTitle}>Messaging Preferences</div>
                                        <div className={styles.formGroup}>
                                            <label className={styles.toggleRow}>
                                                <span className={styles.inputLabel}>Marketing</span>
                                                <label className={rightStyles.switch}>
                                                    <input
                                                        type="checkbox"
                                                        checked={!!editForm.newsletter}
                                                        onChange={(e) => setEditForm({ ...editForm, newsletter: e.target.checked })}
                                                        disabled={isSaving}
                                                    />
                                                    <span className={rightStyles.sliderClinetPage}></span>
                                                </label>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className={styles.formFooter}>
                                    <BorderedButton
                                        text="Cancel"
                                        onClick={handleEditCancel}
                                        className={`${styles.footerBtn} ${styles.footerBtnCancel}`}
                                        disabled={isSaving}
                                    />
                                    <PrimaryButton
                                        text={isSaving ? 'Saving...' : 'Save'}
                                        onClick={handleEditSave}
                                        className={`${styles.footerBtn} ${styles.footerBtnPrimary}`}
                                        disabled={isSaving}
                                    />
                                </div>
                            </>
                        )}
                    </div>

                    {/* Right Panel - Timeline */}
                    <div className={styles.rightPanel}>
                        {/* Tabs */}
                        <div className={styles.tabs}>
                            <button 
                                className={`${styles.tab} ${activeTab === 'timeline' ? styles.active : ''}`}
                                onClick={() => {
                                    handleCloseMembershipHistory();
                                    setActiveTab('timeline');
                                }}
                            >
                                Timeline
                            </button>
                            <button 
                                className={`${styles.tab} ${activeTab === 'membership' ? styles.active : ''}`}
                                onClick={() => {
                                    handleCloseMembershipHistory();
                                    setIsMembershipMenuOpen(false);
                                    setActiveTab('membership');
                                }}
                            >
                                Membership Credit
                            </button>
                        </div>

                                                 {/* Filters - Only show for Timeline tab */}
                         {activeTab === 'timeline' && (
                        <div className={styles.filters}>
                            <span className={styles.filterLabel}>Filter by:</span>
                                 <div className={styles.filterButtons}>
                            <button 
                                className={`${styles.filterButton} ${activeFilter === 'all' ? styles.active : ''}`}
                                onClick={() => setActiveFilter('all')}
                            >
                                All
                            </button>
                            <button 
                                className={`${styles.filterButton} ${activeFilter === 'appointments' ? styles.active : ''}`}
                                onClick={() => setActiveFilter('appointments')}
                            >
                                Appointments
                            </button>
                            <button 
                                className={`${styles.filterButton} ${activeFilter === 'sales' ? styles.active : ''}`}
                                onClick={() => setActiveFilter('sales')}
                            >
                                Sales
                            </button>
                        </div>
                             </div>
                         )}

                        {/* Timeline Content */}
                         {activeTab === 'timeline' && (
                        <div
                            className={styles.timelineContainer}
                            onScroll={handleTimelineScroll}
                            ref={timelineContainerRef}
                        >
                                <div className={`${styles.timeline} ${getFilteredTimeline().upcoming.length === 0 && getFilteredTimeline().past.length === 0 ? styles.noData : ''}`}>
                                    {isTimelineLoading && (
                                        <div style={{ padding: '24px', textAlign: 'center', color: '#6B7280' }}>
                                            <Loader />
                                        </div>
                                    )}

                                    {/* Upcoming Section */}
                                    {getFilteredTimeline().upcoming.length > 0 && (
                                        <>
                                            <div
                                                className={styles.timelineSectionHeader}
                                                style={activeFilter === 'sales' ? { visibility: 'hidden' } : undefined}
                                            >
                                                <h3>Upcoming</h3>
                                            </div>
                                            {getFilteredTimeline().upcoming.map((item, index) => (
                                    <div key={`${item.type}-${item.id || item.date}-${index}`} className={`${styles.timelineItem} ${(() => {
                                        // Product sales are ALWAYS sales, regardless of status
                                        if (item.type === 'product_sale') {
                                            return styles.saleItem;
                                        }

                                        return styles.appointmentItem;
                                    })()}`}>
                                    <div className={styles.timelineDot}></div>
                                    <div className={styles.timelineContent}>
                                        <div className={styles.timelineCard}>
                                                {/* Header with date and status */}
                                                <div className={styles.timelineHeader}>
                                                    <h4>
                                                        {formatShortDate(getDisplayDateForTimelineItem(item))} <span className={styles.separator}></span> 
                                                        {(() => {
                                                            // Product sales are ALWAYS sales, regardless of status
                                                            if (item.type === 'product_sale') {
                                                                const saleHref = getSaleLinkHref(item);
                                                                return saleHref ? (
                                                                    <a
                                                                        href={saleHref}
                                                                        className={styles.clickableSaleId}
                                                                        onClick={(event) => handleTimelineLinkClick(event, () => handleSaleClick(item))}
                                                                        title="Click to view sale details"
                                                                    >
                                                                        Sale #{item.sale_id || item.id}
                                                                    </a>
                                                                ) : (
                                                                    <span>Sale #{item.sale_id || item.id}</span>
                                                                );
                                                            }
                                                            
                                                            if (isAppointmentClickDisabled(item)) {
                                                                return <span>{getAppointmentLabel(item)}</span>;
                                                            }

                                                            const appointmentHref = getAppointmentLinkHref(item);
                                                            return appointmentHref ? (
                                                                <a
                                                                    href={appointmentHref}
                                                                    className={styles.clickableSaleId}
                                                                    onClick={(event) => handleTimelineLinkClick(event, () => handleAppointmentClick(item))}
                                                                    title="Click to view appointment details"
                                                                >
                                                                    {getAppointmentLabel(item)}
                                                                </a>
                                                            ) : (
                                                                <span>{getAppointmentLabel(item)}</span>
                                                            );
                                                        })()} <span className={styles.separator}></span> {item.status || 'Closed'}
                                                        <span className={styles.timelineHeaderSpacer}></span>
                                                        {canShowSaleInvoiceMenu(item) && (
                                                            <span
                                                                className={styles.timelineActions}
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className={styles.timelineMoreButton}
                                                                    onClick={() => {
                                                                        const menuId = `${item.type}-${item.id}-upcoming`;
                                                                        setOpenTimelineMenuId(
                                                                            openTimelineMenuId === menuId ? null : menuId
                                                                        );
                                                                    }}
                                                                    aria-label="Sale actions"
                                                                >
                                                                    <img src={menuIcon} alt="Sale actions" />
                                                                </button>
                                                                {openTimelineMenuId === `${item.type}-${item.id}-upcoming` && (
                                                                    <div className={styles.timelineDropdownMenu}>
                                                                        <button
                                                                            className={styles.dropdownItem}
                                                                            onClick={() => {
                                                                                setOpenTimelineMenuId(null);
                                                                                openInvoiceDocument(item.sale_id ?? item.id, 'pdf');
                                                                            }}
                                                                        >
                                                                            Download PDF Invoice
                                                                        </button>
                                                                        {/* <button
                                                                            className={styles.dropdownItem}
                                                                            onClick={() => {
                                                                                setOpenTimelineMenuId(null);
                                                                                openInvoiceDocument(item.sale_id ?? item.id, 'doc');
                                                                            }}
                                                                        >
                                                                            Download Doc Invoice
                                                                        </button> */}
                                                                        {/* <button
                                                                            className={`${styles.dropdownItem} ${isEmailInvoiceDisabled(item.id) ? styles.dropdownItemDisabled : ''}`}
                                                                            disabled={isEmailInvoiceDisabled(item.id)}
                                                                            title={getEmailInvoiceTooltip(item.id)}
                                                                            onClick={async () => {
                                                                                setOpenTimelineMenuId(null);
                                                                                await handleEmailInvoice(item.id);
                                                                            }}
                                                                        >
                                                                            {getEmailInvoiceLabel(item.id)}
                                                                        </button> */}
                                                                    </div>
                                                                )}
                                                            </span>
                                                        )}
                                                        <span className={styles.outletName}>{item.outlet || 'Outlet'}</span>
                                                    </h4>
                                                </div>

                                                {(item?.cancel_reason || item?.note || item?.web_notes?.trim()) && (
                                                    <div className={styles.notesContainer}>
                                                    {item?.cancel_reason && (
                                                        <SaleCancelReason 
                                                        reason={item?.cancel_reason} 
                                                        remark={item?.cancel_remark}
                                                        customClass={styles.cancelReasonContainer}
                                                        />
                                                    )}

                                                    {/* NOTES START */}
                                                    {(item?.note || item?.web_notes?.trim()) && (
                                                        <BookingNotes
                                                            outletNotes={item?.note}
                                                            webCustomerNotes={item?.web_notes}
                                                            customClass={`${styles.bookingNotesContainer} ${item?.cancel_reason ? styles.hasCancelReason : ''}`}
                                                        />
                                                    )}
                                                    {/* NOTES END */}
                                                    </div>
                                                    )}

                                            
                                            {item.type === 'booking' && (() => {
                                                // Check if this is a group booking (multiple customers)
                                                const customerNames = [...new Set(item.services.map(service => service.customerName))];
                                                const isGroupBooking = customerNames.length > 1;
                                                
                                                // Get primary customer's guest_checkin_id
                                                const primaryCustomerName = customerDetails?.name || customer?.name;
                                                const primaryService = item.services.find(service => service.customerName === primaryCustomerName);
                                                const primaryGuestCheckinId = primaryService?.guest_checkin_id;
                                                const primaryGuestData = primaryGuestCheckinId ? guestDetails[primaryGuestCheckinId] : null;
                                                
                                                return (
                                                    <>
                                                        {/* Primary Client Profile - Show for all bookings */}
                                                        <div className={styles.primaryClientProfile}>
                                                            <div 
                                                                className={styles.clientAvatar}
                                                                style={{ backgroundColor: getConsistentColor(customer?.id || customerDetails?.id, `${customerDetails?.name || customer?.name || ''} ${getLastName()}`.trim()) }}
                                                            >
                                                                {getCustomInitials(customerDetails?.name || customer?.name || '', getLastName())}
                                                                {(customerDetails?.membership || customer?.membership) === 1 && (
                                                                    <div className={styles.memberIconSmall}>
                                                                        <img src={IconMember} alt="Member" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className={styles.clientInfo}>
                                                                <div className={styles.clientName}>
                                                                    {customerDetails?.contact_number || customer?.contact_number}
                                                                </div>
                                                                <div className={styles.clientName}>
                                                                    {customerDetails?.name || customer?.name} {getLastName()}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Primary Client Check-in Info Dropdown - Separate Section - Only show if guest_checkin_id exists */}
                                                        {primaryGuestCheckinId && (
                                                        <div className={styles.primaryClientCheckinInfoSection}>
                                                            <button 
                                                                className={styles.primaryClientCheckinInfoToggle}
                                                                onClick={() => toggleGuestCheckinInfo(`${item.id}-primary-upcoming`, primaryGuestCheckinId)}
                                                            >
                                                                <span className={styles.primaryClientCheckinInfoLabel}>View Check-in info</span>
                                                                <img 
                                                                    src={DropArrow} 
                                                                    alt="toggle"
                                                                    className={`${styles.primaryClientCheckinInfoArrow} ${guestCheckinInfoOpen[`${item.id}-primary-upcoming`] ? styles.primaryClientCheckinInfoArrowOpen : ''}`}
                                                                />
                                                            </button>
                                                            
                                                            {guestCheckinInfoOpen[`${item.id}-primary-upcoming`] && (
                                                                <div className={styles.primaryClientCheckinInfoContent}>
                                                                    <span className={styles.primaryClientCheckinInfoItemLabel}>Full Name</span>
                                                                    <span className={styles.primaryClientCheckinInfoItemValue}>
                                                                        {primaryGuestData ? `${primaryGuestData.name || ''} ${primaryGuestData.lastname || ''}`.trim() : `${customerDetails?.name || customer?.name || ''} ${getLastName()}`.trim()}
                                                                    </span>
                                                                    
                                                                    <span className={styles.primaryClientCheckinInfoItemLabel}>NRIC or Passport Number</span>
                                                                    <span className={styles.primaryClientCheckinInfoItemValue}>
                                                                        <span className={styles.maskedText}>
                                                                            {maskNricOrPassport(primaryGuestData ? (primaryGuestData.passport_number || primaryGuestData.nric || 'N/A') : (customerDetails?.passport_number || customer?.passport_number || customerDetails?.nric || customer?.nric || 'N/A'))}
                                                                        </span>
                                                                    </span>
                                                                    
                                                                    <span className={styles.primaryClientCheckinInfoItemLabel}>Mobile Number</span>
                                                                    <span className={styles.primaryClientCheckinInfoItemValue}>
                                                                        {primaryGuestData ? (primaryGuestData.contact_number || 'N/A') : (customerDetails?.contact_number || customer?.contact_number || 'N/A')}
                                                                    </span>
                                                                    
                                                                    <span className={styles.primaryClientCheckinInfoItemLabel}>Nationality</span>
                                                                    <span className={styles.primaryClientCheckinInfoItemValue}>
                                                                        {primaryGuestData ? (primaryGuestData.nationality || 'N/A') : (customerDetails?.nationality || customer?.nationality || 'N/A')}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        )}
                                                        
                                                        {/* Horizontal line */}
                                                        <div className={styles.sectionDivider}></div>
                                                    </>
                                                );
                                            })()}
                                            
                                            {/* Primary Client Section for Product Sales */}
                                            {item.type === 'product_sale' && (() => {
                                                return (
                                                <>
                                                    {/* Primary Client Profile */}
                                                    <div className={styles.primaryClientProfile}>
                                                        <div 
                                                            className={styles.clientAvatar}
                                                            style={{ backgroundColor: getConsistentColor(customer?.id || customerDetails?.id, `${customerDetails?.name || customer?.name || ''} ${getLastName()}`.trim()) }}
                                                        >
                                                            {getCustomInitials(customerDetails?.name || customer?.name || '', getLastName())}
                                                            {(customerDetails?.membership || customer?.membership) === 1 && (
                                                                <div className={styles.memberIconSmall}>
                                                                    <img src={IconMember} alt="Member" />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className={styles.clientInfo}>
                                                            <div className={styles.clientName}>
                                                                {customerDetails?.contact_number || customer?.contact_number}
                                                            </div>
                                                            <div className={styles.clientName}>
                                                                {customerDetails?.name || customer?.name} {getLastName()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Check-in info is never shown on Sale # timeline items */}
                                                    
                                                    {/* Horizontal line */}
                                                    <div className={styles.sectionDivider}></div>
                                                </>
                                                );
                                            })()}
                                            
                                            <div className={styles.serviceDetails}>
                                                {(() => {
                                                    // Check if this is a product sale or service booking
                                                    if (item.type === 'product_sale' && item.products) {
                                                        // Render products
                                                        return item.products.map((product, productIndex) => {
                                                            const unitPrice = product.price; // API provides unit price
                                                            const totalPrice = unitPrice * product.quantity;
                                                            const isBookingSaleService = item.is_booking_sale && product.duration;
                                                            const itemTypeLabel = product.item_type === 'voucher' ? 'Voucher' : 'Product';
                                                            
                                                            return (
                                                                <div key={product.product_id || productIndex} className={styles.serviceRow}>
                                                                    <div className={styles.serviceInfo}>
                                                                        <div className={styles.saleItemNameBlock}>
                                                                            <div className={styles.saleItemTitleRow}>
                                                                                <span className={styles.serviceName}>
                                                                                    {isBookingSaleService ? `${product.duration} Mins ` : ''}
                                                                                    {product.product_name}
                                                                                </span>
                                                                                {!isBookingSaleService && (
                                                                                    <span className={styles.saleItemType}>{itemTypeLabel}</span>
                                                                                )}
                                                                            </div>
                                                                            {!isBookingSaleService && (
                                                                                <div className={styles.saleItemMeta}>
                                                                                    <span className={styles.saleItemMetaPair}>
                                                                                        <span className={styles.saleItemMetaLabel}>Qty:</span>
                                                                                        <span className={styles.saleItemMetaValue}>{product.quantity}</span>
                                                                                    </span>
                                                                                    {product.item_type === 'voucher' && product.serial_number && (
                                                                                        <span className={styles.saleItemMetaPair}>
                                                                                            <span className={styles.saleItemMetaLabel}>Serial No:</span>
                                                                                            <span className={`${styles.saleItemMetaValue} ${styles.saleItemSerial}`}>{product.serial_number}</span>
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <span className={styles.servicePrice}>
                                                                            {!isBookingSaleService && product.quantity > 1 ? (
                                                                                <>${unitPrice.toFixed(2)} × {product.quantity} = ${totalPrice.toFixed(2)}</>
                                                                            ) : (
                                                                                `$${unitPrice.toFixed(2)}`
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                    {isBookingSaleService && (
                                                                        <>
                                                                            <div className={styles.therapistInfo}>
                                                                                <div className={styles.therapistLeft}>
                                                                                    <div className={styles.therapistRow}>
                                                                                        <span className={styles.therapistLabel}>With:</span>
                                                                                        {product.therapist_pager_number && product.therapist_pager_number !== 0 && product.therapist_pager_number !== '0' && (
                                                                                            <span
                                                                                                className={styles.therapistIcon}
                                                                                                style={{ background: getTherapistPagerColor(product.therapist_gender) }}
                                                                                            >
                                                                                                {product.therapist_pager_number}
                                                                                            </span>
                                                                                        )}
                                                                                        <span className={styles.therapistName}>{product.therapist}</span>
                                                                                    </div>
                                                                                    {(product.room_name || product.room_items?.[0]?.room_name) && (
                                                                                        <div className={styles.roomInfo}>
                                                                                            <span className={styles.roomLabel}>Room:</span>
                                                                                            <span className={styles.roomName}>
                                                                                                {product.room_name || product.room_items?.[0]?.room_name}
                                                                                            </span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                            {product.service_at && (
                                                                                <div className={styles.bookingTimeInfo}>
                                                                                    <span className={styles.bookingTimeLabel}>At:</span>
                                                                                    <span className={styles.bookingTimeValue}>{getBookingTimeDisplay(product, item)}</span>
                                                                                    <span className={styles.bookingDurationLabel}>Duration:</span>
                                                                                    <span className={styles.bookingDurationValue}>{product.duration} Mins</span>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </div>
                                                            );
                                                        });
                                                    } else if (item.services) {
                                                        // Render services (existing logic)
                                                        const servicesByCustomer = {};
                                                        item.services.forEach(service => {
                                                            const customerName = service.customerName || customerDetails?.name || 'Unknown';
                                                            if (!servicesByCustomer[customerName]) {
                                                                servicesByCustomer[customerName] = [];
                                                            }
                                                            servicesByCustomer[customerName].push(service);
                                                        });

                                                        // Check if this is a group booking (multiple customers)
                                                        const customerNames = [...new Set(item.services.map(service => service.customerName))];
                                                        const isGroupBooking = customerNames.length > 1;

                                                        return Object.entries(servicesByCustomer).map(([customerName, services], customerIndex) => {
                                                            const isPrimaryCustomer = customerIndex === 0 || customerName === customerDetails?.name;
                                                            const isGuest = !isPrimaryCustomer;
                                                            const guestCheckinId = services[0]?.guest_checkin_id; // Get guest check-in ID from first service
                                                            const guestData = guestCheckinId ? guestDetails[guestCheckinId] : null;

                                                            return (
                                                                <div key={customerName} className={styles.customerSection}>
                                                                    {isGuest && (
                                                                        <div className={styles.guestHeader}>
                                                                            <div className={styles.guestHeaderTop}>
                                                                                <div className={styles.guestIcon}>
                                                                                    <img src={GuestIcon} alt="Guest" />
                                                                                </div>
                                                                                <span className={styles.guestName}>{customerName} (Pax-1)</span>
                                                                            </div>
                                                                            
                                                                            {/* Guest Check-in Info Dropdown - Only show if guest_checkin_id exists */}
                                                                            {guestCheckinId && (
                                                                                <div className={styles.guestCheckinInfoWrapper}>
                                                                                    <button 
                                                                                        className={styles.guestCheckinInfoToggle}
                                                                                        onClick={() => toggleGuestCheckinInfo(`${item.id}-${customerName}`, guestCheckinId)}
                                                                                    >
                                                                                        <span className={styles.guestCheckinInfoLabel}>View Check-in info</span>
                                                                                        <img 
                                                                                            src={DropArrow} 
                                                                                            alt="toggle"
                                                                                            className={`${styles.guestCheckinInfoArrow} ${guestCheckinInfoOpen[`${item.id}-${customerName}`] ? styles.guestCheckinInfoArrowOpen : ''}`}
                                                                                        />
                                                                                    </button>
                                                                                    
                                                                                    {guestCheckinInfoOpen[`${item.id}-${customerName}`] && (
                                                                                        <div className={styles.guestCheckinInfoContent}>
                                                                                            <span className={styles.guestCheckinInfoItemLabel}>Full Name</span>
                                                                                            <span className={styles.guestCheckinInfoItemValue}>
                                                                                                {guestData ? `${guestData.name || ''} ${guestData.lastname || ''}`.trim() : customerName}
                                                                                            </span>
                                                                                            
                                                                                            <span className={styles.guestCheckinInfoItemLabel}>NRIC or Passport Number</span>
                                                                                            <span className={styles.guestCheckinInfoItemValue}>
                                                                                                <span className={styles.maskedText}>
                                                                                                    {maskNricOrPassport(guestData ? (guestData.passport_number || guestData.nric || 'N/A') : 'N/A')}
                                                                                                </span>
                                                                                            </span>
                                                                                            
                                                                                            <span className={styles.guestCheckinInfoItemLabel}>Mobile Number</span>
                                                                                            <span className={styles.guestCheckinInfoItemValue}>
                                                                                                {guestData ? (guestData.contact_number || 'N/A') : 'N/A'}
                                                                                            </span>
                                                                                            
                                                                                            <span className={styles.guestCheckinInfoItemLabel}>Nationality</span>
                                                                                            <span className={styles.guestCheckinInfoItemValue}>
                                                                                                {guestData ? (guestData.nationality || 'N/A') : 'N/A'}
                                                                                            </span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}

                                                                    {services.map((service, serviceIndex) => {
                                                                        const isSingleService = services.length === 1;
                                                                        return (
                                                                            <div key={service.id || serviceIndex} className={`${styles.serviceRow} ${isGroupBooking ? styles.groupBookingServiceRow : ''} ${isSingleService ? styles.singleService : ''}`}>
                                                                                {/* First row: Service name and price */}
                                                                                <div className={styles.serviceInfo}>
                                                                                    <span className={styles.serviceName}>
                                                                                        {(() => {
                                                                                            const status = item.status?.toLowerCase() || '';
                                                                                            const isCanceled = status === 'canceled' || status === 'cancelled';
                                                                                            const isNoShow = status === 'no-show';

                                                                                            if (isCanceled) {
                                                                                                return (
                                                                                                    <>
                                                                                                        <span className={styles.cancellationText}>Cancelled (Normal Cancellation):- </span>
                                                                                                        <span className={styles.strikethroughText}>{service.duration} Mins {service.service}</span>
                                                                                                    </>
                                                                                                );
                                                                                            } else if (isNoShow) {
                                                                                                return (
                                                                                                    <>
                                                                                                        <span className={styles.cancellationText}>Canceled (No-show): </span>
                                                                                                        <span className={styles.strikethroughText}>{service.duration} Mins {service.service}</span>
                                                                                                    </>
                                                                                                );
                                                                                            } else {
                                                                                                return `${service.duration} Mins ${service.service}`;
                                                                                            }
                                                                                        })()}
                                                                                    </span>
                                                                                    <span className={styles.servicePrice}>${getServiceTotalPrice(service).toFixed(2)}</span>
                                                                                </div>
                                                                                {renderServiceAddons(service)}

                                                                                {/* Second row: Therapist information */}
                                                                                <div className={styles.therapistInfo}>
                                                                                    <div className={styles.therapistLeft}>
                                                                                        <div className={styles.therapistRow}>
                                                                                            <span className={styles.therapistLabel}>With:</span>
                                                                                            {service.therapist_pager_number && service.therapist_pager_number !== 0 && service.therapist_pager_number !== '0' && (
                                                                                                <span
                                                                                                    className={styles.therapistIcon}
                                                                                                    style={{ background: getTherapistPagerColor(service.therapist_gender) }}
                                                                                                >
                                                                                                    {service.therapist_pager_number}
                                                                                                </span>
                                                                                            )}
                                                                                            <span className={styles.therapistName}>{service.therapist}</span>
                                                                                        </div>
                                                                                        {(service.room_name || service.room || service.room_items?.[0]?.room_name) && (
                                                                                            <div className={styles.roomInfo}>
                                                                                                <span className={styles.roomLabel}>Room:</span>
                                                                                                <span className={styles.roomName}>
                                                                                                    {service.room_name || service.room || service.room_items?.[0]?.room_name}
                                                                                                </span>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                    {/* {service.requested_person && (
                                                                                        <div className={styles.requestedTherapistInfo}>
                                                                                            <img src={requestIcon} alt="Requested" className={styles.requestIcon} />
                                                                                            <span className={styles.requestedText}>Requested therapist</span>
                                                                                        </div>
                                                                                    )} */}
                                                                                    <div className={styles.requestedWrapper}>
                                                                                        {service.requested_person === 1 && (
                                                                                            <div className={styles.requestedTherapistInfo}>
                                                                                                <img src={requestIcon} alt="Requested" className={styles.requestIcon} />
                                                                                                <span className={styles.requestedText}>Requested therapist</span>
                                                                                            </div>
                                                                                        )}
                                                                                        {service.requested_room === 1 && (
                                                                                            <div className={styles.requestedTherapistInfo}>
                                                                                                <img src={IconRoom} alt="Requested" className={styles.requestIcon} />
                                                                                                <span className={styles.requestedText}>Requested room</span>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                </div>

                                                                                {/* Third row: Booking time and duration */}
                                                                                {service.service_at && (
                                                                                    <div className={styles.bookingTimeInfo}>
                                                                                        <span className={styles.bookingTimeLabel}>At:</span>
                                                                                        <span className={styles.bookingTimeValue}>{getBookingTimeDisplay(service, item)}</span>
                                                                                        <span className={styles.bookingDurationLabel}>Duration:</span>
                                                                                        <span className={styles.bookingDurationValue}>{service.duration} Mins</span>
                                                                                    </div>
                                                                                )}
        
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            );
                                                        });
                                                    }
                                                    return null;
                                                })()}
                                            </div>

                                            {item.type === 'booking' && !isSaleTimelineItem(item) && item.source && (
                                                <div className={styles.appointmentSourceMeta}>
                                                    <span className={styles.appointmentSourceLabel}>Source:</span>
                                                    <span className={styles.appointmentSourceValue}>{item.source}</span>
                                                </div>
                                            )}
                                                                    {item.type === 'product_sale' && (
                                                                            <div className={styles.paymentBreakdown}>
                                                                                <div className={styles.breakdownItem}>
                                                                                    <span>Subtotal</span>
                                                                                    <span>${item.subtotal.toFixed(2)}</span>
                                                                                </div>
                                                                                <div className={styles.breakdownItem}>
                                                                                    <span>GST</span>
                                                                                    <span>${item.gst.toFixed(2)}</span>
                                                                                </div>
                                                                                {item.deposit > 0 && String(item.source || '').trim().toLowerCase() === 'website' && (
                                                                                    <div className={styles.breakdownItem}>
                                                                                        <span>Deposit</span>
                                                                                        <span>-${item.deposit.toFixed(2)}</span>
                                                                                    </div>
                                                                                )}
                                                                                <div className={styles.breakdownItem}>
                                                                                    <span>Total</span>
                                                                                    <span className={styles.totalAmount}>${item.total.toFixed(2)}</span>
                                                                                </div>
                                                                                <div className={styles.paymentDivider}></div>
                                                                                <button
                                                                                    type="button"
                                                                                    className={styles.paymentsToggleButton}
                                                                                    onClick={() => togglePaymentsDetails(`${item.id || index}-upcoming`)}
                                                                                >
                                                                                    <span>Payments</span>
                                                                                    <img
                                                                                        src={DropArrow}
                                                                                        alt=""
                                                                                        className={`${styles.paymentsToggleArrow} ${paymentsDetailsOpen[`${item.id || index}-upcoming`] ? styles.paymentsToggleArrowOpen : ''}`}
                                                                                    />
                                                                                </button>
                                                                                {paymentsDetailsOpen[`${item.id || index}-upcoming`] && (
                                                                                    <div className={styles.paymentsDetailsContent}>
                                                                                        <BookingPaymentDetails
                                                                                            bookingID={item.id}
                                                                                            saleId={item.sale_id}
                                                                                            from="sale-booking-info"
                                                                                            paymentQueryParams={item.type === 'product_sale' ? { sale_id: item.id } : undefined}
                                                                                            childSaleId={item.child_sale_id}
                                                                                            parentSaleId={item.parent_sale_id}
                                                                                            refundMode={Boolean(item.parent_sale_id)}
                                                                                            hideMembershipPaymentMeta
                                                                                            onGoToSale={handlePaymentGoToSale}
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}


                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    )}

                                    {/* Past Section */}
                                    {getFilteredTimeline().past.length > 0 && (
                                        <>
                                            <div
                                                className={styles.timelineSectionHeader}
                                                style={activeFilter === 'sales' ? { visibility: 'hidden' } : undefined}
                                            >
                                                <h3>Past</h3>
                                            </div>
                                            {getFilteredTimeline().past.map((item, index) => (
                                                <div key={`${item.type}-${item.id || item.date}-past-${index}`} className={`${styles.timelineItem} ${(() => {
                                                    // Product sales are ALWAYS sales, regardless of status
                                                    if (item.type === 'product_sale') {
                                                        return styles.saleItem;
                                                    }

                                                    return styles.appointmentItem;
                                                })()}`}>
                                                    <div className={styles.timelineDot}></div>
                                                    <div className={styles.timelineContent}>
                                                        <div className={styles.timelineCard}>
                                                            {/* Header with date and status */}
                                                            <div className={styles.timelineHeader}>
                                                                <h4>
                                                                    {formatShortDate(getDisplayDateForTimelineItem(item))} <span className={styles.separator}></span>
                                                                    {(() => {
                                                                        // Product sales are ALWAYS sales, regardless of status
                                                                        if (item.type === 'product_sale') {
                                                                            const saleHref = getSaleLinkHref(item);
                                                                            return saleHref ? (
                                                                                <a
                                                                                    href={saleHref}
                                                                                    className={styles.clickableSaleId}
                                                                                    onClick={(event) => handleTimelineLinkClick(event, () => handleSaleClick(item))}
                                                                                    title="Click to view sale details"
                                                                                >
                                                                                    Sale #{item.sale_id || item.id}
                                                                                </a>
                                                                            ) : (
                                                                                <span>Sale #{item.sale_id || item.id}</span>
                                                                            );
                                                                        }
                                                                        
                                                                        if (isAppointmentClickDisabled(item)) {
                                                                            return <span>{getAppointmentLabel(item)}</span>;
                                                                        }

                                                                        const appointmentHref = getAppointmentLinkHref(item);
                                                                        return appointmentHref ? (
                                                                            <a
                                                                                href={appointmentHref}
                                                                                className={styles.clickableSaleId}
                                                                                onClick={(event) => handleTimelineLinkClick(event, () => handleAppointmentClick(item))}
                                                                                title="Click to view appointment details"
                                                                            >
                                                                                {getAppointmentLabel(item)}
                                                                            </a>
                                                                        ) : (
                                                                            <span>{getAppointmentLabel(item)}</span>
                                                                        );
                                                                    })()} <span className={styles.separator}></span> {item.status || 'Closed'}
                                                                    <span className={styles.timelineHeaderSpacer}></span>
                                                                    {canShowSaleInvoiceMenu(item) && (
                                                                        <span
                                                                            className={styles.timelineActions}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            <button
                                                                                type="button"
                                                                                className={styles.timelineMoreButton}
                                                                                onClick={() => {
                                                                                    const menuId = `${item.type}-${item.id}-past`;
                                                                                    setOpenTimelineMenuId(
                                                                                        openTimelineMenuId === menuId ? null : menuId
                                                                                    );
                                                                                }}
                                                                                aria-label="Sale actions"
                                                                            >
                                                                                <img src={menuIcon} alt="Sale actions" />
                                                                            </button>
                                                                            {openTimelineMenuId === `${item.type}-${item.id}-past` && (
                                                                                <div className={styles.timelineDropdownMenu}>
                                                                                    <button
                                                                                        className={styles.dropdownItem}
                                                                                        onClick={() => {
                                                                                            setOpenTimelineMenuId(null);
                                                                                            openInvoiceDocument(item.sale_id ?? item.id, 'pdf');
                                                                                        }}
                                                                                    >
                                                                                        Download PDF Invoice
                                                                                    </button>
                                                                                    {/* <button
                                                                                        className={styles.dropdownItem}
                                                                                        onClick={() => {
                                                                                            setOpenTimelineMenuId(null);
                                                                                            openInvoiceDocument(item.sale_id ?? item.id, 'doc');
                                                                                        }}
                                                                                    >
                                                                                        Download Doc Invoice
                                                                                    </button> */}
                                                                                    {/* <button
                                                                                        className={`${styles.dropdownItem} ${isEmailInvoiceDisabled(item.id) ? styles.dropdownItemDisabled : ''}`}
                                                                                        disabled={isEmailInvoiceDisabled(item.id)}
                                                                                        title={getEmailInvoiceTooltip(item.id)}
                                                                                        onClick={async () => {
                                                                                            setOpenTimelineMenuId(null);
                                                                                            await handleEmailInvoice(item.id);
                                                                                        }}
                                                                                    >
                                                                                        {getEmailInvoiceLabel(item.id)}
                                                                                    </button> */}
                                                                                </div>
                                                                            )}
                                                                        </span>
                                                                    )}
                                                                    <span className={styles.outletName}>{item.outlet || 'Outlet'}</span>
                                                                </h4>
                                                            </div>

                                                            {(item?.cancel_reason || item?.note || item?.web_notes?.trim()) && (
                                                            <div className={styles.notesContainer}>

                                                            {item?.cancel_reason && (
                                                                <SaleCancelReason 
                                                                reason={item?.cancel_reason} 
                                                                remark={item?.cancel_remark}
                                                                customClass={styles.cancelReasonContainer}
                                                                />
                                                            )}

                                                            {/* NOTES START */}
                                                            {(item?.note || item?.web_notes?.trim()) && (
                                                                <BookingNotes
                                                                    outletNotes={item?.note}
                                                                    webCustomerNotes={item?.web_notes}
                                                                    customClass={`${styles.bookingNotesContainer} ${item?.cancel_reason ? styles.hasCancelReason : ''}`}
                                                                />
                                                            )}
                                                            {/* NOTES END */}
                                                            </div>
                                                            )}
                                                                    
                                                                    {item.type === 'booking' && (() => {
                                                                        // Check if this is a group booking (multiple customers)
                                                                        const customerNames = [...new Set(item.services.map(service => service.customerName))];
                                                                        const isGroupBooking = customerNames.length > 1;
                                                                        
                                                                        // Get primary customer's guest_checkin_id
                                                                        const primaryCustomerName = customerDetails?.name || customer?.name;
                                                                        const primaryService = item.services.find(service => service.customerName === primaryCustomerName);
                                                                        const primaryGuestCheckinId = primaryService?.guest_checkin_id;
                                                                        const primaryGuestData = primaryGuestCheckinId ? guestDetails[primaryGuestCheckinId] : null;
                                                                        
                                                                        return (
                                                                            <>
                                                                                {/* Primary Client Profile - Show for all bookings */}
                                                                                <div className={styles.primaryClientProfile}>
                                                                                    <div 
                                                                                        className={styles.clientAvatar}
                                                                                        style={{ backgroundColor: getConsistentColor(customer?.id || customerDetails?.id, `${customerDetails?.name || customer?.name || ''} ${getLastName()}`.trim()) }}
                                                                                    >
                                                                                        {getCustomInitials(customerDetails?.name || customer?.name || '', getLastName())}
                                                                                        {(customerDetails?.membership || customer?.membership) === 1 && (
                                                                                            <div className={styles.memberIconSmall}>
                                                                                                <img src={IconMember} alt="Member" />
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                    <div className={styles.clientInfo}>
                                                                                        <div className={styles.clientName}>
                                                                                            {customerDetails?.contact_number || customer?.contact_number}
                                                                                        </div>
                                                                                        <div className={styles.clientName}>
                                                                                            {customerDetails?.name || customer?.name} {getLastName()}
                                                                                        </div>

                                                                                    </div>
                                                                                </div>
                                                                                
                                                                                {/* Primary Client Check-in Info Dropdown - Separate Section - Only show if guest_checkin_id exists */}
                                                                                {primaryGuestCheckinId && (
                                                                                <div className={styles.primaryClientCheckinInfoSection}>
                                                                                    <button 
                                                                                        className={styles.primaryClientCheckinInfoToggle}
                                                                                        onClick={() => toggleGuestCheckinInfo(`${item.id}-primary-past`, primaryGuestCheckinId)}
                                                                                    >
                                                                                        <span className={styles.primaryClientCheckinInfoLabel}>View Check-in info</span>
                                                                                        <img 
                                                                                            src={DropArrow} 
                                                                                            alt="toggle"
                                                                                            className={`${styles.primaryClientCheckinInfoArrow} ${guestCheckinInfoOpen[`${item.id}-primary-past`] ? styles.primaryClientCheckinInfoArrowOpen : ''}`}
                                                                                        />
                                                                                    </button>
                                                                                    
                                                                                    {guestCheckinInfoOpen[`${item.id}-primary-past`] && (
                                                                                        <div className={styles.primaryClientCheckinInfoContent}>
                                                                                            <span className={styles.primaryClientCheckinInfoItemLabel}>Full Name</span>
                                                                                            <span className={styles.primaryClientCheckinInfoItemValue}>
                                                                                                {primaryGuestData ? `${primaryGuestData.name || ''} ${primaryGuestData.lastname || ''}`.trim() : `${customerDetails?.name || customer?.name || ''} ${getLastName()}`.trim()}
                                                                                            </span>
                                                                                            
                                                                                            <span className={styles.primaryClientCheckinInfoItemLabel}>NRIC or Passport Number</span>
                                                                                            <span className={styles.primaryClientCheckinInfoItemValue}>
                                                                                                <span className={styles.maskedText}>
                                                                                                    {maskNricOrPassport(primaryGuestData ? (primaryGuestData.passport_number || primaryGuestData.nric || 'N/A') : (customerDetails?.passport_number || customer?.passport_number || customerDetails?.nric || customer?.nric || 'N/A'))}
                                                                                                </span>
                                                                                            </span>
                                                                                            
                                                                                            <span className={styles.primaryClientCheckinInfoItemLabel}>Mobile Number</span>
                                                                                            <span className={styles.primaryClientCheckinInfoItemValue}>
                                                                                                {primaryGuestData ? (primaryGuestData.contact_number || 'N/A') : (customerDetails?.contact_number || customer?.contact_number || 'N/A')}
                                                                                            </span>
                                                                                            
                                                                                            <span className={styles.primaryClientCheckinInfoItemLabel}>Nationality</span>
                                                                                            <span className={styles.primaryClientCheckinInfoItemValue}>
                                                                                                {primaryGuestData ? (primaryGuestData.nationality || 'N/A') : (customerDetails?.nationality || customer?.nationality || 'N/A')}
                                                                                            </span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                                )}

                                                                                {/* Horizontal line */}
                                                                        <div className={styles.sectionDivider}></div>
                                                                    </>
                                                                        );
                                                                    })()}

                                                                    {/* Primary Client Section for Product Sales */}
                                                                    {item.type === 'product_sale' && (() => {
                                                                        return (
                                                                        <>
                                                                            {/* Primary Client Profile */}
                                                                            <div className={styles.primaryClientProfile}>
                                                                                <div 
                                                                                    className={styles.clientAvatar}
                                                                                    style={{ backgroundColor: getConsistentColor(customer?.id || customerDetails?.id, `${customerDetails?.name || customer?.name || ''} ${getLastName()}`.trim()) }}
                                                                                >
                                                                                    {getCustomInitials(customerDetails?.name || customer?.name || '', getLastName())}
                                                                                    {(customerDetails?.membership || customer?.membership) === 1 && (
                                                                                        <div className={styles.memberIconSmall}>
                                                                                            <img src={IconMember} alt="Member" />
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                                <div className={styles.clientInfo}>
                                                                                    <div className={styles.clientName}>
                                                                                        {customerDetails?.contact_number || customer?.contact_number}
                                                                                    </div>
                                                                                    <div className={styles.clientName}>
                                                                                        {customerDetails?.name || customer?.name} {getLastName()}
                                                                                    </div>
                                                                                </div>
                                                                            </div>

                                                                            
                                                                            
                                                                            {/* Check-in info is never shown on Sale # timeline items */}
                                                                            
                                                                            {/* Horizontal line */}
                                                                            <div className={styles.sectionDivider}></div>
                                                                        </>
                                                                        );
                                                                    })()}

                                                                    <div className={styles.serviceDetails}>
                                                                        {(() => {
                                                                            // Check if this is a product sale or service booking
                                                                            if (item.type === 'product_sale' && item.products) {
                                                                                // Render products
                                                                                return item.products.map((product, productIndex) => {
                                                                                    const unitPrice = product.price; // API provides unit price
                                                                                    const totalPrice = unitPrice * product.quantity;
                                                                                    const isBookingSaleService = item.is_booking_sale && product.duration;
                                                                                    const itemTypeLabel = product.item_type === 'voucher' ? 'Voucher' : 'Product';
                                                                                    
                                                                                    return (
                                                                                        <div key={product.product_id || productIndex} className={styles.serviceRow}>
                                                                                            <div className={styles.serviceInfo}>
                                                                                                <div className={styles.saleItemNameBlock}>
                                                                                                    <div className={styles.saleItemTitleRow}>
                                                                                                        <span className={styles.serviceName}>
                                                                                                            {isBookingSaleService ? `${product.duration} Mins ` : ''}
                                                                                                            {product.product_name}
                                                                                                        </span>
                                                                                                        {!isBookingSaleService && (
                                                                                                            <span className={styles.saleItemType}>{itemTypeLabel}</span>
                                                                                                        )}
                                                                                                    </div>
                                                                                                    {!isBookingSaleService && (
                                                                                                        <div className={styles.saleItemMeta}>
                                                                                                            <span className={styles.saleItemMetaPair}>
                                                                                                                <span className={styles.saleItemMetaLabel}>Qty:</span>
                                                                                                                <span className={styles.saleItemMetaValue}>{product.quantity}</span>
                                                                                                            </span>
                                                                                                            {product.item_type === 'voucher' && product.serial_number && (
                                                                                                                <span className={styles.saleItemMetaPair}>
                                                                                                                    <span className={styles.saleItemMetaLabel}>Serial No:</span>
                                                                                                                    <span className={`${styles.saleItemMetaValue} ${styles.saleItemSerial}`}>{product.serial_number}</span>
                                                                                                                </span>
                                                                                                            )}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                                <span className={styles.servicePrice}>
                                                                                                    {!isBookingSaleService && product.quantity > 1 ? (
                                                                                                        <>${unitPrice.toFixed(2)} × {product.quantity} = ${totalPrice.toFixed(2)}</>
                                                                                                    ) : (
                                                                                                        `$${unitPrice.toFixed(2)}`
                                                                                                    )}
                                                                                                </span>
                                                                                            </div>
                                                                                            {isBookingSaleService && (
                                                                                                <>
                                                                                                    <div className={styles.therapistInfo}>
                                                                                                        <div className={styles.therapistLeft}>
                                                                                                            <div className={styles.therapistRow}>
                                                                                                                <span className={styles.therapistLabel}>With:</span>
                                                                                                                {product.therapist_pager_number && product.therapist_pager_number !== 0 && product.therapist_pager_number !== '0' && (
                                                                                                                    <span
                                                                                                                        className={styles.therapistIcon}
                                                                                                                        style={{ background: getTherapistPagerColor(product.therapist_gender) }}
                                                                                                                    >
                                                                                                                        {product.therapist_pager_number}
                                                                                                                    </span>
                                                                                                                )}
                                                                                                                <span className={styles.therapistName}>{product.therapist}</span>
                                                                                                            </div>
                                                                                                            {(product.room_name || product.room_items?.[0]?.room_name) && (
                                                                                                                <div className={styles.roomInfo}>
                                                                                                                    <span className={styles.roomLabel}>Room:</span>
                                                                                                                    <span className={styles.roomName}>
                                                                                                                        {product.room_name || product.room_items?.[0]?.room_name}
                                                                                                                    </span>
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </div>
                                                                                                    </div>
                                                                                                    {product.service_at && (
                                                                                                        <div className={styles.bookingTimeInfo}>
                                                                                                            <span className={styles.bookingTimeLabel}>At:</span>
                                                                                                            <span className={styles.bookingTimeValue}>{getBookingTimeDisplay(product, item)}</span>
                                                                                                            <span className={styles.bookingDurationLabel}>Duration:</span>
                                                                                                            <span className={styles.bookingDurationValue}>{product.duration} Mins</span>
                                                                                                        </div>
                                                                                                    )}
                                                                                                </>
                                                                                            )}
                                                                                        </div>
                                                                                    );
                                                                                });
                                                                            } else if (item.services) {
                                                                                // Render services (existing logic)
                                                                                const servicesByCustomer = {};
                                                                                item.services.forEach(service => {
                                                                                    const customerName = service.customerName || customerDetails?.name || 'Unknown';
                                                                                    if (!servicesByCustomer[customerName]) {
                                                                                        servicesByCustomer[customerName] = [];
                                                                                    }
                                                                                    servicesByCustomer[customerName].push(service);
                                                                                });

                                                                                // Check if this is a group booking (multiple customers)
                                                                                const customerNames = [...new Set(item.services.map(service => service.customerName))];
                                                                                const isGroupBooking = customerNames.length > 1;

                                                                                return Object.entries(servicesByCustomer).map(([customerName, services], customerIndex) => {
                                                                                    const isPrimaryCustomer = customerName === customerDetails?.name;
                                                                                    const isGuest = !isPrimaryCustomer;
                                                                                    const guestCheckinId = services[0]?.guest_checkin_id; // Get guest check-in ID from first service
                                                                                    const guestData = guestCheckinId ? guestDetails[guestCheckinId] : null;

                                                                                    return (
                                                                                        <div key={customerName} className={styles.customerSection}>
                                                                                            {isGuest && (
                                                                                                <div className={styles.guestHeader}>
                                                                                                    <div className={styles.guestHeaderTop}>
                                                                                                        <div className={styles.guestIcon}>
                                                                                                            <img src={GuestIcon} alt="Guest" />
                                                                                                        </div>
                                                                                                        <span className={styles.guestName}>{customerName} (Pax-1)</span>
                                                                                                    </div>
                                                                                                    
                                                                                                    {/* Guest Check-in Info Dropdown - Only show if guest_checkin_id exists */}
                                                                                                    {guestCheckinId && (
                                                                                                        <div className={styles.guestCheckinInfoWrapper}>
                                                                                                            <button 
                                                                                                                className={styles.guestCheckinInfoToggle}
                                                                                                                onClick={() => toggleGuestCheckinInfo(`${item.id}-${customerName}-past`, guestCheckinId)}
                                                                                                            >
                                                                                                                <span className={styles.guestCheckinInfoLabel}>View Check-in info</span>
                                                                                                                <img 
                                                                                                                    src={DropArrow} 
                                                                                                                    alt="toggle"
                                                                                                                    className={`${styles.guestCheckinInfoArrow} ${guestCheckinInfoOpen[`${item.id}-${customerName}-past`] ? styles.guestCheckinInfoArrowOpen : ''}`}
                                                                                                                />
                                                                                                            </button>
                                                                                                            
                                                                                                            {guestCheckinInfoOpen[`${item.id}-${customerName}-past`] && (
                                                                                                                <div className={styles.guestCheckinInfoContent}>
                                                                                                                    <span className={styles.guestCheckinInfoItemLabel}>Full Name</span>
                                                                                                                    <span className={styles.guestCheckinInfoItemValue}>
                                                                                                                        {guestData ? `${guestData.name || ''} ${guestData.lastname || ''}`.trim() : customerName}
                                                                                                                    </span>
                                                                                                                    
                                                                                                                    <span className={styles.guestCheckinInfoItemLabel}>NRIC or Passport Number</span>
                                                                                                                    <span className={styles.guestCheckinInfoItemValue}>
                                                                                                                        <span className={styles.maskedText}>
                                                                                                                            {maskNricOrPassport(guestData ? (guestData.passport_number || guestData.nric || 'N/A') : 'N/A')}
                                                                                                                        </span>
                                                                                                                    </span>
                                                                                                                    
                                                                                                                    <span className={styles.guestCheckinInfoItemLabel}>Mobile Number</span>
                                                                                                                    <span className={styles.guestCheckinInfoItemValue}>
                                                                                                                        {guestData ? (guestData.contact_number || 'N/A') : 'N/A'}
                                                                                                                    </span>
                                                                                                                    
                                                                                                                    <span className={styles.guestCheckinInfoItemLabel}>Nationality</span>
                                                                                                                    <span className={styles.guestCheckinInfoItemValue}>
                                                                                                                        {guestData ? (guestData.nationality || 'N/A') : 'N/A'}
                                                                                                                    </span>
                                                                                                                </div>
                                                                                                            )}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            )}

                                                                                            {services.map((service, serviceIndex) => {
                                                                                                const isSingleService = services.length === 1;
                                                                                                return (
                                                                                                    <div key={service.id || serviceIndex} className={`${styles.serviceRow} ${isGroupBooking ? styles.groupBookingServiceRow : ''} ${isSingleService ? styles.singleService : ''}`}>
                                                                            {/* First row: Service name and price */}
                                                                            <div className={styles.serviceInfo}>
                                                                                <span className={styles.serviceName}>
                                                                                    {(() => {
                                                                                        const status = item.status?.toLowerCase() || '';
                                                                                        const isCanceled = status === 'canceled' || status === 'cancelled';
                                                                                        const isNoShow = status === 'no-show';
                                                                                        
                                                                                        if (isCanceled) {
                                                                                            return (
                                                                                                <>
                                                                                                    <span className={styles.cancellationText}>Cancelled (Normal Cancellation):- </span>
                                                                                                    <span className={styles.strikethroughText}>{service.duration} Mins {service.service}</span>
                                                                                                </>
                                                                                            );
                                                                                        } else if (isNoShow) {
                                                                                            return (
                                                                                                <>
                                                                                                    <span className={styles.cancellationText}>Canceled (No-show): </span>
                                                                                                    <span className={styles.strikethroughText}>{service.duration} Mins {service.service}</span>
                                                                                                </>
                                                                                            );
                                                                                        } else {
                                                                                            return `${service.duration} Mins ${service.service}`;
                                                                                        }
                                                                                    })()}
                                                                                </span>
                                                                                <span className={styles.servicePrice}>${getServiceTotalPrice(service).toFixed(2)}</span>
                                                                            </div>
                                                                            {renderServiceAddons(service)}
                                                                            
                                                                            {/* Second row: Therapist information */}
                                                                            <div className={styles.therapistInfo}>
                                                                                <div className={styles.therapistLeft}>
                                                                                    <div className={styles.therapistRow}>
                                                                                        <span className={styles.therapistLabel}>With:</span>
                                                                                        {service.therapist_pager_number && service.therapist_pager_number !== 0 && service.therapist_pager_number !== '0' && (
                                                                                            <span
                                                                                                className={styles.therapistIcon}
                                                                                                style={{ background: getTherapistPagerColor(service.therapist_gender) }}
                                                                                            >
                                                                                                {service.therapist_pager_number}
                                                                                            </span>
                                                                                        )}
                                                                                        <span className={styles.therapistName}>{service.therapist}</span>
                                                                                    </div>
                                                                                    {(service.room_name || service.room || service.room_items?.[0]?.room_name) && (
                                                                                        <div className={styles.roomInfo}>
                                                                                            <span className={styles.roomLabel}>Room:</span>
                                                                                            <span className={styles.roomName}>
                                                                                                {service.room_name || service.room || service.room_items?.[0]?.room_name}
                                                                                            </span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                                <div className={styles.requestedWrapper}>
                                                                                    {service.requested_person ===1 && (
                                                                                        <div className={styles.requestedTherapistInfo}>
                                                                                            <img src={requestIcon} alt="Requested" className={styles.requestIcon} />
                                                                                            <span className={styles.requestedText}>Requested therapist</span>
                                                                                        </div>
                                                                                    )}
                                                                                    {service.requested_room === 1 && (
                                                                                        <div className={styles.requestedTherapistInfo}>
                                                                                            <img src={IconRoom} alt="Requested" className={styles.requestIcon} />
                                                                                            <span className={styles.requestedText}>Requested room</span>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                                
                                                                            </div>

                                                                            {/* Third row: Booking time and duration */}
                                                                            {service.service_at && (
                                                                                <div className={styles.bookingTimeInfo}>
                                                                                    <span className={styles.bookingTimeLabel}>At:</span>
                                                                                    <span className={styles.bookingTimeValue}>{getBookingTimeDisplay(service, item)}</span>
                                                                                    <span className={styles.bookingDurationLabel}>Duration:</span>
                                                                                    <span className={styles.bookingDurationValue}>{service.duration} Mins</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                                                );
                                                                                            })}
                                                                </div>
                                                            );
                                                        });
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                            {item.type === 'booking' && !isSaleTimelineItem(item) && item.source && (
                                                <div className={styles.appointmentSourceMeta}>
                                                    <span className={styles.appointmentSourceLabel}>Source:</span>
                                                    <span className={styles.appointmentSourceValue}>{item.source}</span>
                                                </div>
                                            )}
                                            {item.type === 'product_sale' && (
                                            <div className={styles.paymentBreakdown}>
                                                <div className={styles.breakdownItem}>
                                                    <span>Subtotal</span>
                                                        <span>${item.subtotal.toFixed(2)}</span>
                                                </div>
                                                <div className={styles.breakdownItem}>
                                                    <span>GST</span>
                                                        <span>${item.gst.toFixed(2)}</span>
                                                </div>
                                                {item.deposit > 0 && String(item.source || '').trim().toLowerCase() === 'website' && (
                                                    <div className={styles.breakdownItem}>
                                                        <span>Deposit</span>
                                                        <span>-${item.deposit.toFixed(2)}</span>
                                                    </div>
                                                )}
                                                <div className={styles.breakdownItem}>
                                                    <span>Total</span>
                                                        <span className={styles.totalAmount}>${item.total.toFixed(2)}</span>
                                                </div>
                                                
                                                <div className={styles.paymentDivider}></div>
                                                <button
                                                    type="button"
                                                    className={styles.paymentsToggleButton}
                                                    onClick={() => togglePaymentsDetails(`${item.id || index}-past`)}
                                                >
                                                    <span>Payments</span>
                                                    <img
                                                        src={DropArrow}
                                                        alt=""
                                                        className={`${styles.paymentsToggleArrow} ${paymentsDetailsOpen[`${item.id || index}-past`] ? styles.paymentsToggleArrowOpen : ''}`}
                                                    />
                                                </button>
                                                {paymentsDetailsOpen[`${item.id || index}-past`] && (
                                                    <div className={styles.paymentsDetailsContent}>
                                                        <BookingPaymentDetails
                                                            bookingID={item.id}
                                                            saleId={item.sale_id}
                                                            from="sale-booking-info"
                                                            paymentQueryParams={item.type === 'product_sale' ? { sale_id: item.id } : undefined}
                                                            childSaleId={item.child_sale_id}
                                                            parentSaleId={item.parent_sale_id}
                                                            refundMode={Boolean(item.parent_sale_id)}
                                                            hideMembershipPaymentMeta
                                                            onGoToSale={handlePaymentGoToSale}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}


                                            </div>
                                        </div>
                                    </div>
                                ))}
                                        </>
                                    )}

                                    {!isTimelineLoading && getFilteredTimeline().upcoming.length === 0 && getFilteredTimeline().past.length === 0 && (
                                    <div className={styles.emptyStateContainer}>
                                        <img src={AppointmentSVG} alt="No appointments" className={styles.emptyStateIcon} />
                                        <div className={styles.emptyStateText}>{`No ${activeTab} history`}</div>
                                    </div>
                                )}
                                
                                {/* Client Created Information at Bottom */}
                                {customerDetails?.created_at && (getFilteredTimeline().upcoming.length > 0 || getFilteredTimeline().past.length > 0) && (
                                    <div className={styles.clientCreatedBottom}>
                                        {/* <div className={styles.clientCreatedTitle}>Client created</div>
                                        <div>
                                            <span className={styles.clientCreatedDateTime}>
                                                {formatDate(customerDetails.created_at)}
                                            </span>
                                            <span className={styles.clientCreatedByBottom}>
                                                Created by {JSON.parse(localStorage.getItem('user_name') || 'N/A')}
                                            </span>
                                        </div> */}
                                    </div>
                                )}
                                {isLoadingMoreTimeline && (
                                    <div style={{ padding: '12px', textAlign: 'center', color: '#6B7280' }}>
                                        Loading more...
                                    </div>
                                )}
                            </div>
                        </div>
                        )}
                        
 
                        {/* Membership Credit Content */}
                        {activeTab === 'membership' && (
                            <div className={styles.membershipContainer}>
                                {loading && (
                                    <div className={styles.loaderContainer} style={{ justifyContent: 'center', minHeight: '500px' }}>
                                        <Loader />
                                    </div>
                                )}

                                {(hasMembershipDetail || hasOpenSales) ? (
                                    <div className={styles.membershipDetailsContent}>
                                        {isMembershipHistoryOpen ? (
                                            <div className={styles.membershipHistoryInline}>
                                                <div className={styles.membershipHistoryInlineHeader}>
                                                    <button
                                                        type="button"
                                                        className={styles.membershipHistoryInlineBack}
                                                        onClick={handleCloseMembershipHistory}
                                                    >
                                                        &lt; Back
                                                    </button>
                                                </div>
                                                <div
                                                    ref={membershipHistoryTableRef}
                                                    className={styles.membershipHistoryTable}
                                                    aria-busy={isMembershipHistoryLoading}
                                                    onScroll={handleMembershipHistoryScroll}
                                                >
                                                    <div className={styles.membershipHistoryTableHead}>
                                                        <div>Date</div>
                                                        <div>Details</div>
                                                        <div>Outlet</div>
                                                        <div>Amt/Balance</div>
                                                    </div>
                                                    {membershipTransactions.length === 0 && !isMembershipHistoryLoading ? (
                                                        <div className={styles.membershipHistoryEmpty}>No history found.</div>
                                                    ) : (
                                                        membershipTransactions.map((transaction) => {
                                                            const rowClickable =
                                                                isMembershipHistoryRowClickable(transaction);
                                                            const balanceAfter =
                                                                getMembershipHistoryBalanceAfter(transaction);
                                                            const creditDelta =
                                                                getMembershipHistoryCreditDelta(transaction);
                                                            const formattedDelta =
                                                                formatMembershipHistoryCreditDelta(creditDelta);
                                                            const referenceId =
                                                                getMembershipHistoryReferenceId(transaction);
                                                            return (
                                                                <div
                                                                    key={`transaction-${getMembershipHistoryTransactionKey(transaction)}`}
                                                                    className={styles.membershipHistoryRow}
                                                                >
                                                                    <div className={styles.membershipHistoryDate}>
                                                                        {formatMembershipHistoryDate(
                                                                            transaction?.created_at || transaction?.date
                                                                        )}
                                                                    </div>
                                                                    <div className={styles.membershipHistoryDetails}>
                                                                        {rowClickable ? (
                                                                            <button
                                                                                type="button"
                                                                                className={`${styles.membershipHistoryType} ${styles.membershipHistoryTypeButton}`}
                                                                                onClick={() =>
                                                                                    handleMembershipHistoryRowClick(transaction)
                                                                                }
                                                                            >
                                                                                <span className={styles.membershipHistoryTypeText}>
                                                                                    {formatMembershipHistoryActionName(transaction)}
                                                                                </span>
                                                                                <span className={styles.membershipHistoryChevron}>&gt;</span>
                                                                            </button>
                                                                        ) : (
                                                                            <div className={styles.membershipHistoryType}>
                                                                                <span className={styles.membershipHistoryTypeText}>
                                                                                    {formatMembershipHistoryActionName(transaction)}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                        {referenceId ? (
                                                                            rowClickable ? (
                                                                                <button
                                                                                    type="button"
                                                                                    className={styles.membershipHistoryReferenceButton}
                                                                                    onClick={() =>
                                                                                        handleMembershipHistoryRowClick(transaction)
                                                                                    }
                                                                                >
                                                                                    {referenceId}
                                                                                </button>
                                                                            ) : (
                                                                                <div className={styles.membershipHistoryReference}>
                                                                                    {referenceId}
                                                                                </div>
                                                                            )
                                                                        ) : null}
                                                                    </div>
                                                                    <div className={styles.membershipHistoryOutlet}>
                                                                        {getMembershipHistoryOutlet(transaction)}
                                                                    </div>
                                                                    <div className={styles.membershipHistoryAmtBalance}>
                                                                        <div className={styles.membershipHistoryAmtPrimary}>
                                                                            {formattedDelta ??
                                                                                formatMembershipHistoryAmount(
                                                                                    getMembershipHistoryAmount(transaction)
                                                                                )}
                                                                        </div>
                                                                        <div className={styles.membershipHistoryAmtSecondary}>
                                                                            {balanceAfter != null
                                                                                ? formatMembershipHistoryAmount(balanceAfter)
                                                                                : '—'}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                    {isMembershipHistoryLoading && (
                                                        <div
                                                            className={styles.membershipHistoryLoading}
                                                            role="status"
                                                            aria-live="polite"
                                                        >
                                                            {membershipTransactions.length > 0
                                                                ? 'Loading more...'
                                                                : 'Loading history...'}
                                                        </div>
                                                    )}
                                                    {membershipHistoryLoadMoreError && (
                                                        <button
                                                            type="button"
                                                            className={styles.membershipHistoryRetry}
                                                            onClick={() => {
                                                                membershipHistoryAutoLoadPageRef.current = null;
                                                                setMembershipHistoryLoadMoreError(false);
                                                                handleLoadMoreMembershipHistory();
                                                            }}
                                                        >
                                                            Could not load more history. Retry
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                {renderMembershipOpenSalesBanner()}

                                                <div className={styles.membershipCard}>
                                                    <div ref={membershipMenuRef} className={styles.membershipMenuAnchor}>
                                                    <button
                                                        type="button"
                                                        className={styles.membershipCardMoreButton}
                                                        aria-label="Membership options"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            setIsMembershipMenuOpen((previousValue) => !previousValue);
                                                        }}
                                                    >
                                                        ...
                                                    </button>
                                                    {isMembershipMenuOpen && (
                                                        <div
                                                            className={styles.membershipDropdownMenu}
                                                            onClick={(event) => event.stopPropagation()}
                                                        >
                                                            <button
                                                                type="button"
                                                                className={styles.dropdownItem}
                                                                onClick={async () => {
                                                                    setIsMembershipMenuOpen(false);
                                                                    await handleViewMembershipHistory();
                                                                }}
                                                            >
                                                                View History
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={styles.dropdownItem}
                                                                onClick={() => {
                                                                    setIsMembershipMenuOpen(false);
                                                                    const customerRecordForMenu =
                                                                        customerDetails && Object.keys(customerDetails).length > 0
                                                                            ? customerDetails
                                                                            : customer;
                                                                    if (resolveMembershipPasswordIsSet(customerRecordForMenu, membershipDetail)) {
                                                                        handleOpenMembershipPasswordResetFromMenu();
                                                                    } else {
                                                                        openSetMembershipPasswordPopup();
                                                                    }
                                                                }}
                                                            >
                                                                {resolveMembershipPasswordIsSet(
                                                                    customerDetails && Object.keys(customerDetails).length > 0
                                                                        ? customerDetails
                                                                        : customer,
                                                                    membershipDetail
                                                                )
                                                                    ? 'Reset Password'
                                                                    : 'Set Password'}
                                                            </button>
                                                        </div>
                                                    )}
                                                    </div>
                                                    <div className={styles.membershipCardHeader}>
                                                        <div>
                                                            <div className={styles.membershipCreditValue}>${Number(membershipDetail?.credit_balance || 0).toFixed(2)}</div>
                                                            <div className={styles.membershipCreditLabel}>Credit Balance</div>
                                                        </div>
                                                        <div className={styles.membershipRegistrationOutlet}>
                                                            <span className={styles.membershipRegistrationLabel}>Registration Outlet: </span>
                                                            <span className={styles.membershipRegistrationValue}>{getRegistrationOutletName()}</span>
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className={styles.membershipTopupLink}
                                                        onClick={handleOpenTopupCreditsModal}
                                                    >
                                                        <span className={styles.membershipTopupIcon} aria-hidden="true">
                                                            <span className={styles.membershipTopupIconPlus}>+</span>
                                                        </span>
                                                        <span>Top-up Credit</span>
                                                    </button>
                                                </div>
                                                <div className={styles.membershipMetaSection}>
                                                    <div className={styles.membershipMetaGrid}>
                                                        <div className={styles.membershipMetaItem}>
                                                            <div className={styles.membershipMetaLabel}>Membership ID</div>
                                                           <div className={styles.membershipMetaValue}>
                                                            {membershipDetail?.membership_id ? `#${membershipDetail.membership_id}` : '-'}
                                                            </div>
                                                        </div>
                                                        <div className={styles.membershipMetaItem}>
                                                            <div className={styles.membershipMetaLabel}>Status</div>
                                                            <div
                                                                className={`${styles.membershipMetaValue}${
                                                                    normalizedMembershipStatus === 'suspended'
                                                                        ? ` ${styles.membershipStatusSuspended}`
                                                                        : ''
                                                                }`}
                                                            >
                                                                {membershipDetail?.status_label || '-'}
                                                            </div>
                                                        </div>
                                                        <div className={styles.membershipMetaItem}>
                                                            <div className={styles.membershipMetaLabel}>Expiry</div>
                                                            <div className={styles.membershipMetaValue}>{formatMembershipExpiryDate(membershipDetail?.expiry_date)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ) : !loading ? (
                                    <div className={styles.membershipEmptyState}>
                                        {renderMembershipOpenSalesBanner('empty')}
                                        <div className={styles.membershipEmptyIconContainer} aria-hidden="true">
                                            <svg
                                                className={styles.membershipEmptyIcon}
                                                width="24"
                                                height="24"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M4.75 8.25H19.25V17.25C19.25 17.9404 18.6904 18.5 18 18.5H6C5.30964 18.5 4.75 17.9404 4.75 17.25V8.25Z" stroke="white" strokeWidth="1.5" />
                                                <path d="M7.25 8.25V7C7.25 6.30964 7.80964 5.75 8.5 5.75H15.5C16.1904 5.75 16.75 6.30964 16.75 7V8.25" stroke="white" strokeWidth="1.5" />
                                                <circle cx="15.75" cy="13.375" r="1.125" fill="white" />
                                            </svg>
                                        </div>
                                        <p className={styles.membershipEmptyTitle}>No membership found</p>
                                        <PrimaryButton
                                            text="Purchase Membership"
                                            className={styles.membershipPurchaseButton}
                                            onClick={handleOpenPurchaseMembershipModal}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                </div>

                {isPurchaseMembershipModalOpen && typeof document !== 'undefined' ? createPortal((
                    <div
                        className={`${styles.sidebarOverlay} ${styles.open} ${styles.purchaseMembershipOverlay}`}
                        role="presentation"
                    >
                    <div
                        ref={purchaseMembershipPanelRef}
                        className={`${styles.sidebarContent} ${styles.purchaseMembershipContent} ${membershipFlowOnly ? styles.purchaseMembershipContentCompact : ''} ${isSalesOrClientsRoute ? styles.purchaseMembershipSalesClients : ''} ${isSalesRoute && !membershipFromNewCheckout ? styles.purchaseMembershipSalesPage : ''} ${membershipFromNewCheckout ? styles.purchaseMembershipNewCheckout : ''}`}
                        role="dialog"
                        aria-modal="true"
                        aria-label={purchaseMembershipFlowType === 'topup' ? 'Top-up Credits' : 'Purchase Membership'}
                    >
                        {purchaseMembershipStep === 'details' && (
                            <div
                                className={`${styles.purchaseMembershipHeader} ${membershipFlowOnly && isSalesOrClientsRoute ? styles.purchaseMembershipHeaderSalesFlow : ''} ${isHomeRoute ? styles.purchaseMembershipHeaderHome : ''}`}
                            >
                                <span className={styles.purchaseMembershipClose} onClick={handleClosePurchaseMembershipModal}>×</span>
                                <h3 className={`${styles.purchaseMembershipTitle} ${isHomeRoute ? styles.purchaseMembershipTitleHome : ''}`}>
                                    {purchaseMembershipFlowType === 'topup' ? 'Top-up Credits' : 'Purchase Membership'}
                                </h3>
                            </div>
                        )}
                        <div className={`${styles.purchaseMembershipBody} ${purchaseMembershipStep !== 'details' ? styles.purchaseMembershipBodyPayment : ''}`}>
                            {purchaseMembershipStep === 'payment' ? (
                                <PaymentPage
                                    embeddedMembershipCheckout={membershipFlowOnly}
                                    embeddedMembershipHomePage={isHomeRoute}
                                    onBack={() => {
                                        if (membershipFlowOnly) {
                                            restorePreservedExternalServiceTotal();
                                        }
                                        setPurchaseMembershipStep('details');
                                    }}
                                    onNext={() => setPurchaseMembershipStep('confirmation')}
                                    completeBooking={() => Promise.resolve()}
                                    setCurrentStep={() => {}}
                                    bookingDataFromAPI={purchaseBookingDataFromAPI}
                                    isProductSale={true}
                                    isMembershipPayment={true}
                                    gstRate={0}
                                    creditFetchUserId={customer?.id || customerDetails?.id || null}
                                />
                            ) : purchaseMembershipStep === 'confirmation' ? (
                                <ConfirmationPage
                                    embeddedMembershipCheckout={membershipFlowOnly}
                                    embeddedMembershipHomePage={isHomeRoute}
                                    summary={purchaseSummary}
                                    onBack={() => setPurchaseMembershipStep('payment')}
                                    onConfirm={handlePurchaseMembershipConfirm}
                                    onCancel={() => setPurchaseMembershipStep('payment')}
                                    isProductSale={true}
                                    bookingID={purchaseBookingDataFromAPI?.booking_id || null}
                                    isMembershipPayment={true}
                                    membershipListUserId={customer?.id || customerDetails?.id || null}
                                    onMembershipClientTopUpClick={(flowType = 'topup') => {
                                        setPurchaseMembershipFlowType(flowType);
                                        setPurchaseMembershipStep('details');
                                    }}
                                />
                            ) : (
                                <>
                            <div className={styles.purchaseCustomerInfo}>
                                <div
                                    className={styles.purchaseCustomerAvatar}
                                    style={{ backgroundColor: getConsistentColor(customer?.id || customerDetails?.id, `${customerDetails?.name || customer?.name || ''} ${getLastName()}`.trim()) }}
                                >
                                    {getCustomInitials(customerDetails?.name || customer?.name || '', getLastName())}
                                </div>
                                <div className={styles.purchaseCustomerMeta}>
                                    <div className={styles.purchaseCustomerPhoneRow}>
                                        <span>{customerDetails?.contact_number || customer?.contact_number || 'N/A'}</span>
                                        {hasCustomerMembershipId && (
                                            <span className={styles.purchaseCustomerId}> (#{customerMembershipId})</span>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.purchaseCustomerNameRow}
                                        onClick={() => setIsPurchaseClientDetailsOpen((prevValue) => !prevValue)}
                                        aria-expanded={isPurchaseClientDetailsOpen}
                                        aria-label="Toggle client details"
                                    >
                                        <span className={styles.purchaseCustomerName}>{`${customerDetails?.name || customer?.name || ''} ${getLastName()}`.trim()}</span>
                                        <span className={styles.purchaseCustomerArrow} aria-hidden="true">
                                            <img
                                                src={DropArrow}
                                                alt=""
                                                className={`${styles.purchaseCustomerArrowIcon} ${isPurchaseClientDetailsOpen ? styles.purchaseCustomerArrowIconOpen : ''}`}
                                            />
                                        </span>
                                    </button>
                                </div>
                                {isPurchaseClientDetailsOpen ? (
                                    <div className={styles.purchaseCustomerActions}>
                                        <button
                                            type="button"
                                            className={styles.purchaseCustomerResetButton}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handlePurchaseClientFormReset();
                                            }}
                                            disabled={!isPurchaseClientFormDirty || isSavingPurchaseClient}
                                            title="reset"
                                        >
                                            <img
                                                src={refreshIcon}
                                                alt=""
                                                className={styles.purchaseCustomerResetIcon}
                                            />
                                        </button>
                                        <button
                                            type="button"
                                            className={styles.purchaseCustomerUpdateButton}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handlePurchaseClientSaveUpdate();
                                            }}
                                            disabled={!isPurchaseClientFormDirty || isSavingPurchaseClient}
                                        >
                                            {isSavingPurchaseClient ? 'Updating...' : 'Update'}
                                        </button>
                                    </div>
                                ) : null}
                            </div>

                            <div
                                className={`${styles.purchaseClientDetailsSection} ${isPurchaseClientDetailsOpen ? styles.purchaseClientDetailsSectionOpen : styles.purchaseClientDetailsSectionCollapsed}`}
                            >
                                <div className={styles.purchaseClientDetailGrid}>
                                    <div className={styles.purchaseClientDetailBlock}>
                                        <div className={styles.purchaseClientDetailLabel}>First Name</div>
                                        <input
                                            type="text"
                                            className={styles.purchaseClientDetailInput}
                                            value={purchaseClientForm.firstName}
                                            onChange={(event) => setPurchaseClientForm((prevValue) => ({ ...prevValue, firstName: event.target.value }))}
                                        />
                                    </div>
                                    <div className={styles.purchaseClientDetailBlock}>
                                        <div className={styles.purchaseClientDetailLabel}>Last Name</div>
                                        <input
                                            type="text"
                                            className={styles.purchaseClientDetailInput}
                                            value={purchaseClientForm.lastName}
                                            onChange={(event) => setPurchaseClientForm((prevValue) => ({ ...prevValue, lastName: event.target.value }))}
                                        />
                                    </div>
                                </div>
                                <div className={styles.purchaseClientDetailBlock}>
                                    <div className={styles.purchaseClientDetailLabel}>NRIC</div>
                                    <input
                                        type="text"
                                        className={styles.purchaseClientDetailInput}
                                        value={maskNricOrPassport(purchaseClientForm.nric)}
                                        onChange={(event) => {
                                            const nextRaw = mergeNricMaskedInput(
                                                purchaseClientForm.nric,
                                                event.target.value
                                            );
                                            setPurchaseClientNricError('');
                                            setPurchaseClientForm((prevValue) => ({ ...prevValue, nric: nextRaw }));
                                        }}
                                        autoComplete="off"
                                        spellCheck={false}
                                        maxLength={9}
                                        aria-invalid={purchaseClientNricError ? 'true' : 'false'}
                                    />
                                    {purchaseClientNricError ? (
                                        <p className={styles.purchaseClientFieldError} role="alert">
                                            {purchaseClientNricError}
                                        </p>
                                    ) : null}
                                </div>
                                <div className={styles.purchaseClientDetailBlock}>
                                    <div className={styles.purchaseClientDetailLabel}>Email</div>
                                    <input
                                        type="email"
                                        className={styles.purchaseClientDetailInput}
                                        value={purchaseClientForm.email}
                                        onChange={(event) => setPurchaseClientForm((prevValue) => ({ ...prevValue, email: event.target.value }))}
                                    />
                                </div>
                            </div>

                            {purchaseMembershipFlowType === 'topup' ? (
                                <div className={styles.purchaseTopupSummaryGrid}>
                                    <div className={styles.purchaseTopupSummaryItem}>
                                        <div className={styles.purchaseTopupSummaryLabel}>Credit Balance</div>
                                        <div className={styles.purchaseTopupSummaryValue}>
                                            ${Number(membershipDetail?.credit_balance || 0).toFixed(2)}
                                        </div>
                                    </div>
                                    <div className={styles.purchaseTopupSummaryItem}>
                                        <div className={styles.purchaseTopupSummaryLabel}>New Credit Balance</div>
                                        <div className={styles.purchaseTopupSummaryValue}>
                                            ${(() => {
                                                const selectedPackage = membershipPackages.find((membershipPackage, packageIndex) => {
                                                    const membershipPackageId = getMembershipPackageId(membershipPackage, packageIndex);
                                                    return String(membershipPackageId) === String(selectedMembershipPackageId);
                                                });
                                                const currentBalance = Number(membershipDetail?.credit_balance || 0);
                                                const topupAmount = Number(getMembershipPackageAmount(selectedPackage) || 0);
                                                return (currentBalance + topupAmount).toFixed(2);
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {isLoadingMembershipPackages && (
                                <Loader />
                            )}

                            {purchaseMembershipError && (
                                <div className={styles.purchaseMembershipError}>{purchaseMembershipError}</div>
                            )}

                            {!isLoadingMembershipPackages && membershipPackages.length > 0 && (
                                <>
                                    <div className={`${styles.purchaseSection} ${styles.purchaseMemberPackageSection}`}>
                                        <h4 className={`${styles.purchaseSectionTitle} ${styles.purchaseMemberPackageTitle}`}>Member Package</h4>
                                        <div className={styles.purchaseOptionGrid}>
                                            {membershipPackages.map((membershipPackage, packageIndex) => {
                                                const membershipPackageId = getMembershipPackageId(membershipPackage, packageIndex);
                                                return (
                                                    <label key={membershipPackageId} className={styles.purchaseRadioOption}>
                                                        <input
                                                            type="radio"
                                                            name="membershipPackage"
                                                            value={membershipPackageId}
                                                            checked={String(selectedMembershipPackageId) === String(membershipPackageId)}
                                                            disabled={isLoadingGiftCategories || isLoadingMembershipPackages}
                                                            onChange={() => handleMembershipPackageChange(membershipPackageId)}
                                                        />
                                                        <span>{getMembershipPackageLabel(membershipPackage, packageIndex)}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className={`${styles.purchaseInputGroup} ${styles.purchaseInsuranceInputGroup}`}>
                                        {/* <input
                                            type="text"
                                            value={insuranceCertificateNumber}
                                            onChange={(event) => setInsuranceCertificateNumber(event.target.value)}
                                            placeholder="Insurance Certificate Number"
                                            className={`${styles.purchaseTextInput} ${styles.purchaseInsuranceInput}`}
                                        /> */}
                                    </div>

                                    <div className={styles.purchaseGiftsWrapper}>
                                        {giftSections.map((giftSection, giftSectionIndex) => (
                                            <div
                                                key={giftSection.id}
                                                className={`${styles.purchaseSection} ${giftSectionIndex === 0 ? styles.purchaseVoucherSection : ''} ${giftSectionIndex === giftSections.length - 1 ? styles.purchaseGiftSectionLast : ''}`}
                                            >
                                                <h4 className={`${styles.purchaseSectionTitle} ${styles.purchaseGiftSectionTitle}`}>{giftSection.label}</h4>
                                                <div className={styles.purchaseOptionList}>
                                                    {giftSection.options.map((giftOption) => (
                                                        <label key={giftOption.id} className={styles.purchaseRadioOption}>
                                                            <input
                                                                type="radio"
                                                                name={`gift-${giftSection.id}`}
                                                                value={giftOption.id}
                                                                checked={String(selectedGiftOptionBySection[giftSection.id]) === String(giftOption.id)}
                                                                onChange={() => {
                                                                    setSelectedGiftOptionBySection((previousValue) => ({
                                                                        ...previousValue,
                                                                        [giftSection.id]: giftOption.id
                                                                    }));
                                                                }}
                                                            />
                                                            <span className={styles.purchaseGiftOptionText}>{giftOption.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                                {(() => {
                                                    const selectedId = selectedGiftOptionBySection[giftSection.id];
                                                    const selectedOption = giftSection.options.find((o) => String(o.id) === String(selectedId));
                                                    const raw = selectedOption?.raw || {};
                                                    const isDidNotIssue = raw.is_did_not_issue === true;
                                                    const type = raw.type == null ? null : String(raw.type).trim().toLowerCase();
                                                    const showRemarks =
                                                        isDidNotIssue || type === null || type === 'others';
                                                    const showVoucherSerial =
                                                        !isDidNotIssue &&
                                                        type !== null &&
                                                        (type === 'inhouse' || type === 'third_party');

                                                    if (!showVoucherSerial && !showRemarks) return null;

                                                    if (showRemarks) {
                                                        return (
                                                            <div className={`${styles.purchaseInputGroup} ${styles.purchaseVoucherInputGroup}`}>
                                                                <input
                                                                    type="text"
                                                                    value={String(giftRemarksBySection[giftSection.id] ?? '')}
                                                                    onChange={(event) => {
                                                                        const value = event.target.value;
                                                                        setGiftRemarksBySection((prev) => ({
                                                                            ...prev,
                                                                            [giftSection.id]: value
                                                                        }));
                                                                    }}
                                                                    placeholder="Remarks"
                                                                    className={`${styles.purchaseTextInput} ${styles.purchaseVoucherInput}`}
                                                                />
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <div className={`${styles.purchaseInputGroup} ${styles.purchaseVoucherInputGroup}`}>
                                                            <input
                                                                type="text"
                                                                value={String(voucherSerialBySection[giftSection.id] ?? '')}
                                                                onChange={(event) => {
                                                                    const value = event.target.value;
                                                                    setVoucherSerialBySection((prev) => ({
                                                                        ...prev,
                                                                        [giftSection.id]: value
                                                                    }));
                                                                }}
                                                                placeholder="Voucher Serial Number"
                                                                className={`${styles.purchaseTextInput} ${styles.purchaseVoucherInput}`}
                                                            />
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        ))}

                                        {isLoadingGiftCategories && (
                                            <div className={styles.purchaseGiftsLoadingOverlay}>
                                                <Loader />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                                </>
                            )}
                        </div>
                        {purchaseMembershipStep === 'details' && (
                            <div className={styles.purchaseMembershipFooter}>
                                <PrimaryButton
                                    text="Go to Payment"
                                    className={styles.purchaseMembershipFooterButton}
                                    onClick={handleGoToPurchasePayment}
                                    disabled={
                                        isLoadingMembershipPackages ||
                                        isLoadingGiftCategories ||
                                        membershipPackages.length === 0 ||
                                        (isPurchaseClientDetailsOpen && isPurchaseClientFormDirty)
                                    }
                                />
                            </div>
                        )}
                    </div>
                    </div>
                ), document.body) : null}

                {isMembershipSalePanelOpen && membershipSalePanelDetail ? (
                    <MembershipSaleSidePanel
                        onClose={resetMembershipSalePanelState}
                        title={membershipSalePanelTitle}
                        detail={membershipSalePanelDetail}
                        saleNote={membershipSalePanelNote}
                        panelType="membershipCredit"
                        onAddNote={() => setIsMembershipSaleNotePopupOpen(true)}
                        menuOptions={
                            isClosedSaleStatus(
                                membershipSalePanelDetail?.payment_status || membershipSalePanelDetail?.status
                            )
                                ? [
                            {
                                label: 'Download PDF Invoice',
                                action: () => openInvoiceDocument(getMembershipSalePanelInvoiceId(), 'pdf'),
                            },
                            // {
                            //     label: getEmailInvoiceLabel(getMembershipSalePanelInvoiceId()),
                            //     action: () => handleEmailInvoice(getMembershipSalePanelInvoiceId()),
                            //     disabled: !getMembershipSalePanelInvoiceId() || isEmailInvoiceDisabled(getMembershipSalePanelInvoiceId()),
                            //     tooltip: !getMembershipSalePanelInvoiceId()
                            //         ? 'Sale ID not found.'
                            //         : getEmailInvoiceTooltip(getMembershipSalePanelInvoiceId()),
                            // },
                                ]
                                : []
                        }
                    />
                ) : null}

                {isMembershipTransactionPanelOpen && (
                    <MembershipTransactionSidePanel
                        onClose={() => {
                            setIsMembershipTransactionPanelOpen(false);
                            setMembershipTransactionPanelId(null);
                            setMembershipTransactionPanelSaleDisplay(null);
                            setMembershipTransactionPanelTitlePrefix('Sale');
                            setMembershipTransactionDetail(null);
                            setMembershipTransactionNote('');
                        }}
                        onGoToSale={(saleId) => {
                            setIsMembershipTransactionPanelOpen(false);
                            setMembershipTransactionPanelId(null);
                            setMembershipTransactionPanelSaleDisplay(null);
                            setMembershipTransactionPanelTitlePrefix('Sale');
                            setMembershipTransactionDetail(null);
                            setMembershipTransactionNote('');
                            const resolvedSaleId = String(saleId || membershipTransactionPanelSaleDisplay || '').replace(/^#/, '').trim();
                            if (resolvedSaleId) {
                                redirectWithOutlet(`/sales?search=${encodeURIComponent(resolvedSaleId)}`);
                            } else {
                                onClose?.();
                            }
                        }}
                        title={
                            membershipTransactionPanelSaleDisplay != null && String(membershipTransactionPanelSaleDisplay).trim() !== ''
                                ? `${membershipTransactionPanelTitlePrefix} #${membershipTransactionPanelSaleDisplay}`
                                : membershipTransactionPanelId != null
                                    ? `${membershipTransactionPanelTitlePrefix} #${membershipTransactionPanelId}`
                                    : membershipTransactionPanelTitlePrefix
                        }
                        detail={membershipTransactionDetail}
                        saleNote={membershipTransactionNote}
                        onAddNote={() => setIsMembershipNotePopupOpen(true)}
                        saleId={membershipTransactionPanelSaleDisplay}
                    />
                )}

                {isMembershipTopupPreviewOpen && (
                    <MembershipTransactionSidePanel
                        onClose={() => {
                            setIsMembershipTopupPreviewOpen(false);
                            setMembershipTopupPreviewPanelId(null);
                            setMembershipTopupPreviewSaleDisplay(null);
                            setMembershipTopupPreviewDetail(null);
                            setMembershipTopupPreviewNote('');
                        }}
                        onGoToSale={(saleId) => {
                            setIsMembershipTopupPreviewOpen(false);
                            setMembershipTopupPreviewPanelId(null);
                            setMembershipTopupPreviewSaleDisplay(null);
                            setMembershipTopupPreviewDetail(null);
                            setMembershipTopupPreviewNote('');
                            const resolvedSaleId = String(saleId || membershipTopupPreviewSaleDisplay || '').replace(/^#/, '').trim();
                            if (resolvedSaleId) {
                                redirectWithOutlet(`/sales?search=${encodeURIComponent(resolvedSaleId)}`);
                            } else {
                                onClose?.();
                            }
                        }}
                        title={
                            membershipTopupPreviewSaleDisplay != null && String(membershipTopupPreviewSaleDisplay).trim() !== ''
                                ? `Sale #${membershipTopupPreviewSaleDisplay}`
                                : membershipTopupPreviewPanelId != null
                                    ? `Sale #${membershipTopupPreviewPanelId}`
                                    : 'Sale'
                        }
                        detail={membershipTopupPreviewDetail}
                        saleNote={membershipTopupPreviewNote}
                        saleId={membershipTopupPreviewSaleDisplay}
                        footer={(
                            <PrimaryButton
                                text="Continue to top-up"
                                className={styles.purchaseMembershipFooterButton}
                                onClick={handleContinueMembershipTopupFromPreview}
                            />
                        )}
                    />
                )}
                {isSalesSidebarOpen && salesSidebarId && (
                    <>
                        <div
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 1300,
                                background: 'rgba(0,0,0,0.25)',
                            }}
                            onClick={() => {
                                resetSalesSidebarState();
                            }}
                        />

                        <div style={{ position: 'relative', zIndex: 1301 }}>
                            <SideBarRight
                                active={isSalesSidebarOpen}
                                fullScreen
                                onClose={() => {
                                    resetSalesSidebarState();
                                }}
                                id={salesSidebarId}
                                status={salesSidebarSale?.status}
                                sale={salesSidebarSale}
                                initialStep={salesSidebarSale?.initialStep || null}
                                showBookingDetails={true}
                                onOpenSale={openSalesSidebarFromSale}
                            />
                        </div>
                    </>
                )}

                {isMembershipSaleNotePopupOpen && (
                    <SaleNotePopup
                        onClose={() => setIsMembershipSaleNotePopupOpen(false)}
                        addSaleNote={handleMembershipSalePanelAddNote}
                        editableNote={membershipSalePanelNote}
                    />
                )}

                {isMembershipNotePopupOpen && (
                    <MembershipTransactionNotePopup
                        show={isMembershipNotePopupOpen}
                        onClose={() => setIsMembershipNotePopupOpen(false)}
                        initialNote={membershipTransactionNote}
                        onConfirm={handleMembershipTransactionAddNote}
                        isProcessing={isMembershipNoteProcessing}
                    />
                )}

                {/* Delete Confirmation Popup */}
                {showDeleteConfirm && (
                    <DeleteConfirmPopup
                        title="Delete Client"
                        message={deleteErrorMessage || "Are you sure you want to delete this client? This action cannot be undone."}
                        onCancel={() => {
                            setShowDeleteConfirm(false);
                            setDeleteErrorMessage(null);
                        }}
                        onConfirm={handleDeleteCustomer}
                        isDeleting={isDeleting}
                        showCloseOnly={!!deleteErrorMessage}
                    />
                )}

                {membershipMenuResetPopupId != null && (
                    <MembershipPasswordPopup
                        startWithReset
                        shouldContinueAfterReset={false}
                        membershipId={membershipMenuResetPopupId}
                        onClose={() => setMembershipMenuResetPopupId(null)}
                        onResetSuccess={async () => {
                            const uid = customer?.id || customerDetails?.id;
                            if (uid) {
                                try {
                                    await fetchCustomerDetails(uid);
                                } catch (refreshError) {
                                    console.error('Failed to refresh customer after password reset:', refreshError);
                                }
                            }
                        }}
                    />
                )}

                {membershipMenuSetPopupId != null && (
                    <SetMembershipPasswordPopup
                        membershipId={membershipMenuSetPopupId}
                        showSkipForNow={setPasswordPopupFromFirstPurchase}
                        showSuccessToast={!setPasswordPopupFromFirstPurchase}
                        onClose={handleCloseSetMembershipPasswordPopup}
                        onSkip={handleSkipSetMembershipPasswordPopup}
                        onSuccess={async () => {
                            setMembershipMenuSetPopupId(null);
                            setSetPasswordPopupFromFirstPurchase(false);
                            const uid = customer?.id || customerDetails?.id;
                            if (uid) {
                                setCheckoutCustomerIsPasswordSet(uid, true, true);
                                setCustomerDetails((prev) =>
                                    prev ? withMembershipPasswordSetFlag(prev) : prev
                                );
                                const updatedForListeners = withMembershipPasswordSetFlag({
                                    ...(customerDetails && Object.keys(customerDetails).length > 0
                                        ? customerDetails
                                        : {}),
                                    ...(customer && typeof customer === 'object' ? customer : {}),
                                    id: uid,
                                });
                                window.dispatchEvent(
                                    new CustomEvent('customerUpdated', {
                                        detail: { customer: updatedForListeners },
                                    })
                                );
                            }
                            if (pendingPurchaseFinalizeRef.current) {
                                showPendingPurchaseSuccessToast();
                                await finalizeMembershipPurchaseAfterPayment(null, {
                                    showToast: false,
                                });
                            } else if (uid) {
                                try {
                                    await fetchCustomerDetails(uid);
                                } catch (refreshError) {
                                    console.error('Failed to refresh customer after password set:', refreshError);
                                }
                            }
                        }}
                    />
                )}

            </div>
            
            {/* Toast Container — rendered via portal to escape backdrop-filter stacking context */}
            {createPortal(
                <ToastContainer
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop={true}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    containerId="customerDetailsModal"
                    style={{ zIndex: 10000 }}
                />,
                document.body
            )}
        </div>
    );
};

export default CustomerDetailsSidebar; 
